const SIGNAL_DATA_TYPES = new Set(["BOOL", "REAL", "LREAL", "DINT", "UDINT", "INT", "UINT", "WORD", "DWORD"]);
const SIGNAL_ADDRESS_PREFIXES = new Set(["%IX", "%QX", "%MX", "%IW", "%QW", "%MW", "%ID", "%QD", "%MD"]);

export function normalizeSignalCategoryEntry(entry, index = 0) {
    const id = String(entry?.id || "").trim() || `category_${index + 1}`;
    const direction = ["input", "output", "internal"].includes(entry?.direction) ? entry.direction : "internal";
    const addressPrefix = SIGNAL_ADDRESS_PREFIXES.has(entry?.addressPrefix) ? entry.addressPrefix : "%MX";
    const startByte = Math.max(0, parseInt(entry?.startByte, 10) || 0);
    return {
        id,
        name: String(entry?.name || entry?.label || id).trim() || id,
        direction,
        addressPrefix,
        startByte
    };
}

const AXIS_ACTUATOR_TYPES = new Set(["hydraulicCylinder", "pneumaticCylinder", "electricCylinder", "motor", "chuck", "vacuum", "heater", "generic"]);
const MOTOR_CONTROL_TYPES = new Set(["", "speedControl", "pulseCommand", "pulseTrain", "servo", "inverter"]);
const ACTUATOR_CONTROL_METHODS = new Set(["discreteIo", "plcopenMotion", "plcopenFluidPower", "vendorFunction"]);
const PNEUMATIC_VALVE_MODES = new Set(["doubleSolenoid", "singleSolenoid"]);
const ACTION_COMPLETION_MODES = new Set(["sensor", "timer", "ignore"]);
const SOLENOID_ACTIVE_DIRECTIONS = new Set(["positive", "negative"]);
const POSITIVE_MOTION_ACTION_IDS = new Set(["extend", "up"]);
const NEGATIVE_MOTION_ACTION_IDS = new Set(["retract", "down"]);
const PNEUMATIC_VALVE_STATE_KEYS = Object.freeze(["extend", "retract", "stop", "emergency"]);
const VALVE_OUTPUT_STATES = new Set(["on", "off", "hold"]);

export function normalizeControlSpecText(value, fallback = "") {
    return String(value || "").trim() || fallback;
}

export function buildControlSpecSlug(value, fallback = "item") {
    const normalized = normalizeControlSpecText(value, fallback)
        .replace(/[^A-Za-z0-9_]+/g, "_")
        .replace(/^_+|_+$/g, "");
    if (!normalized) return fallback;
    return /^[A-Za-z_]/.test(normalized) ? normalized : `A_${normalized}`;
}

export function normalizePlcopenActionMeta(plcopen = {}) {
    const family = String(plcopen?.family || "").trim();
    const fbName = String(plcopen?.fbName || "").trim();
    if (!family && !fbName) return null;
    return {
        family: family === "fluidPower" ? "fluidPower" : family === "motion" ? "motion" : "",
        fbName
    };
}

export function normalizeActionCommand(command = {}) {
    return {
        setOutputs: Array.isArray(command?.setOutputs) ? command.setOutputs.slice() : [],
        resetOutputs: Array.isArray(command?.resetOutputs) ? command.resetOutputs.slice() : [],
        executeSignal: String(command?.executeSignal || "").trim()
    };
}

export function normalizeActionParameters(parameters = {}) {
    return {
        position: String(parameters?.position || "").trim(),
        velocity: String(parameters?.velocity || "").trim()
    };
}

export function normalizeActionStatus(status = {}) {
    return {
        busySignal: String(status?.busySignal || "").trim(),
        doneSignal: String(status?.doneSignal || "").trim(),
        errorSignal: String(status?.errorSignal || "").trim(),
        errorIdSignal: String(status?.errorIdSignal || "").trim()
    };
}

const DEFAULT_ACTION_ID_BY_NAME = Object.freeze({
    "前進": "extend", "後退": "retract", "上昇": "up", "下降": "down",
    "正転": "forward", "逆転": "reverse", "停止": "stop", "リセット": "reset",
    "原点復帰": "home", "位置決め": "position", "JOG": "jog", "起動": "start",
    "チャック": "chuck", "アンチャック": "unchuck", "吸着": "pick", "吸着破壊": "release", "破棄": "release",
    "ON": "on", "OFF": "off"
});

const DEFAULT_ACTUATOR_ACTION_TEMPLATES = {
    hydraulicCylinder: [
        { id: "home", name: "原点復帰", plcopen: { family: "fluidPower", fbName: "MC_Home" } },
        { id: "position", name: "位置決め", plcopen: { family: "fluidPower", fbName: "MC_MoveAbsolute" } },
        { id: "stop", name: "停止", plcopen: { family: "fluidPower", fbName: "MC_Stop" } },
        { id: "reset", name: "リセット", plcopen: { family: "fluidPower", fbName: "MC_Reset" } }
    ],
    electricCylinder: [
        { id: "extend", name: "前進" }, { id: "retract", name: "後退" },
        { id: "stop", name: "停止" }, { id: "reset", name: "リセット" }
    ],
    pneumaticCylinder: [
        { id: "extend", name: "前進" }, { id: "retract", name: "後退" }, { id: "stop", name: "停止" }
    ],
    motor: [
        { id: "forward", name: "正転", plcopen: { family: "motion", fbName: "MC_MoveVelocity" } },
        { id: "reverse", name: "逆転", plcopen: { family: "motion", fbName: "MC_MoveVelocity" } },
        { id: "stop", name: "停止", plcopen: { family: "motion", fbName: "MC_Stop" } },
        { id: "reset", name: "リセット", plcopen: { family: "motion", fbName: "MC_Reset" } },
        { id: "home", name: "原点復帰", plcopen: { family: "motion", fbName: "MC_Home" } },
        { id: "position", name: "位置決め", plcopen: { family: "motion", fbName: "MC_MoveAbsolute" } }
    ],
    chuck: [{ id: "chuck", name: "チャック" }, { id: "unchuck", name: "アンチャック" }],
    vacuum: [{ id: "pick", name: "吸着" }, { id: "release", name: "吸着破壊" }],
    heater: [{ id: "on", name: "ON" }, { id: "off", name: "OFF" }],
    generic: [{ id: "on", name: "ON" }, { id: "off", name: "OFF" }]
};

const DEFAULT_MOTOR_ACTION_TEMPLATES = {
    speedControl: [
        { id: "forward", name: "正転", plcopen: { family: "motion", fbName: "MC_MoveVelocity" } },
        { id: "reverse", name: "逆転", plcopen: { family: "motion", fbName: "MC_MoveVelocity" } },
        { id: "stop", name: "停止", plcopen: { family: "motion", fbName: "MC_Stop" } },
        { id: "reset", name: "リセット", plcopen: { family: "motion", fbName: "MC_Reset" } }
    ],
    pulseCommand: [
        { id: "start", name: "起動", plcopen: { family: "motion", fbName: "MC_MoveAbsolute" } },
        { id: "stop", name: "停止", plcopen: { family: "motion", fbName: "MC_Stop" } },
        { id: "reset", name: "リセット", plcopen: { family: "motion", fbName: "MC_Reset" } },
        { id: "position", name: "位置決め", plcopen: { family: "motion", fbName: "MC_MoveAbsolute" } }
    ],
    pulseTrain: [
        { id: "forward", name: "正転", plcopen: { family: "motion", fbName: "MC_MoveVelocity" } },
        { id: "reverse", name: "逆転", plcopen: { family: "motion", fbName: "MC_MoveVelocity" } },
        { id: "stop", name: "停止", plcopen: { family: "motion", fbName: "MC_Stop" } },
        { id: "reset", name: "リセット", plcopen: { family: "motion", fbName: "MC_Reset" } }
    ],
    servo: [
        { id: "home", name: "原点復帰", plcopen: { family: "motion", fbName: "MC_Home" } },
        { id: "position", name: "位置決め", plcopen: { family: "motion", fbName: "MC_MoveAbsolute" } },
        { id: "jog", name: "JOG", plcopen: { family: "motion", fbName: "MC_MoveVelocity" } },
        { id: "reset", name: "リセット", plcopen: { family: "motion", fbName: "MC_Reset" } }
    ],
    inverter: [
        { id: "forward", name: "正転", plcopen: { family: "motion", fbName: "MC_MoveVelocity" } },
        { id: "reverse", name: "逆転", plcopen: { family: "motion", fbName: "MC_MoveVelocity" } },
        { id: "stop", name: "停止", plcopen: { family: "motion", fbName: "MC_Stop" } },
        { id: "reset", name: "リセット", plcopen: { family: "motion", fbName: "MC_Reset" } }
    ]
};

export function getDefaultActuatorActionTemplates(
    actuatorType = "pneumaticCylinder",
    motorControlType = "",
    controlMethod = "",
    catalogEntry = null
) {
    const normalizedActuatorType = normalizeAxisActuatorType(actuatorType);
    const normalizedMethod = normalizeActuatorControlMethod(controlMethod, normalizedActuatorType);
    if (normalizedActuatorType === "motor" || normalizedMethod === "plcopenMotion") {
        return DEFAULT_MOTOR_ACTION_TEMPLATES[normalizeMotorControlType(motorControlType, actuatorType)] || DEFAULT_MOTOR_ACTION_TEMPLATES.speedControl;
    }
    if (DEFAULT_ACTUATOR_ACTION_TEMPLATES[normalizedActuatorType]) {
        return DEFAULT_ACTUATOR_ACTION_TEMPLATES[normalizedActuatorType];
    }
    if (catalogEntry?.defaultActionNames?.length) {
        return catalogEntry.defaultActionNames.map((name, index) => ({
            id: DEFAULT_ACTION_ID_BY_NAME[name] || `custom_${index + 1}`,
            name
        }));
    }
    return DEFAULT_ACTUATOR_ACTION_TEMPLATES[normalizedActuatorType] || DEFAULT_ACTUATOR_ACTION_TEMPLATES.pneumaticCylinder;
}

export function createDefaultActuatorActions(
    actuatorType = "pneumaticCylinder",
    motorControlType = "",
    controlMethod = "",
    catalogEntry = null
) {
    const templates = getDefaultActuatorActionTemplates(actuatorType, motorControlType, controlMethod, catalogEntry);
    return templates.map((template) => {
        const plcopen = normalizePlcopenActionMeta(template.plcopen);
        const pins = plcopen ? createDefaultPlcopenPins(template.id) : {};
        return {
            id: template.id,
            name: template.name,
            timeoutSec: 0,
            plcopen,
            command: { setOutputs: [], resetOutputs: [], ...(pins.command || {}) },
            parameters: normalizeActionParameters(pins.parameters),
            status: normalizeActionStatus(pins.status),
            doneConditions: [],
            faultConditions: []
        };
    });
}

export function mergeActuatorActionsWithDefaults(
    actions = [],
    actuatorType = "pneumaticCylinder",
    motionName = "",
    motionId = "",
    motorControlType = "",
    controlMethod = "",
    catalogEntry = null
) {
    const merged = Array.isArray(actions) ? actions.map((action) => ({
        ...action,
        plcopen: normalizePlcopenActionMeta(action?.plcopen),
        command: normalizeActionCommand(action?.command),
        parameters: normalizeActionParameters(action?.parameters),
        status: normalizeActionStatus(action?.status),
        doneConditions: Array.isArray(action?.doneConditions) ? action.doneConditions.slice() : [],
        faultConditions: Array.isArray(action?.faultConditions) ? action.faultConditions.slice() : []
    })) : [];
    const existing = new Set(merged.map((action) => String(action?.id || "").toLowerCase()));
    createDefaultActuatorActions(actuatorType, motorControlType, controlMethod, catalogEntry).forEach((template) => {
        if (existing.has(template.id.toLowerCase())) return;
        merged.push(template);
        existing.add(template.id.toLowerCase());
    });
    return merged;
}

export function createDefaultPlcopenPins(actionId = "") {
    const base = buildControlSpecSlug(actionId || "Action", "Action");
    return {
        command: { executeSignal: `${base}Execute` },
        parameters: actionId === "position" ? { position: `${base}Position`, velocity: `${base}Velocity` } : {},
        status: {
            busySignal: `${base}Busy`,
            doneSignal: `${base}Done`,
            errorSignal: `${base}Error`,
            errorIdSignal: `${base}ErrorID`
        }
    };
}

export function normalizeAxisActuatorType(value) {
    const normalized = String(value || "").trim();
    if (normalized === "cylinder" || normalized === "cylinderIo") return "pneumaticCylinder";
    if (normalized === "motorIo" || normalized === "servoAxis") return "motor";
    if (normalized === "genericIo" || normalized === "signalOnly") return "generic";
    if (AXIS_ACTUATOR_TYPES.has(normalized)) return normalized;
    return normalized || "pneumaticCylinder";
}

export function normalizeMotorControlType(value, actuatorType = "") {
    const normalizedType = normalizeAxisActuatorType(actuatorType);
    if (normalizedType !== "motor") return "";
    const normalized = String(value || "").trim();
    if (!normalized) {
        if (actuatorType === "servoAxis") return "servo";
        if (actuatorType === "motorIo") return "speedControl";
    }
    if (normalized === "motorIo") return "speedControl";
    if (normalized === "servoAxis") return "servo";
    return MOTOR_CONTROL_TYPES.has(normalized) ? normalized : "speedControl";
}

export function normalizeActuatorControlMethod(value, actuatorType = "") {
    const normalized = String(value || "").trim();
    if (normalized === "motionFb") return "plcopenMotion";
    if (ACTUATOR_CONTROL_METHODS.has(normalized)) return normalized;
    const type = normalizeAxisActuatorType(actuatorType);
    if (type === "motor") return "plcopenMotion";
    if (type === "hydraulicCylinder") return "plcopenFluidPower";
    return "discreteIo";
}

export function normalizePneumaticValveMode(value, actuatorType = "") {
    if (normalizeAxisActuatorType(actuatorType) !== "pneumaticCylinder") return "";
    const normalized = String(value || "").trim();
    return PNEUMATIC_VALVE_MODES.has(normalized) ? normalized : "doubleSolenoid";
}

function normalizeSolenoidActiveDirection(value = "") {
    const normalized = String(value || "").trim();
    return SOLENOID_ACTIVE_DIRECTIONS.has(normalized) ? normalized : "positive";
}

function normalizeValveOutputState(value = "", fallback = "hold") {
    const normalized = String(value || "").trim();
    if (VALVE_OUTPUT_STATES.has(normalized)) return normalized;
    return VALVE_OUTPUT_STATES.has(String(fallback || "").trim()) ? String(fallback).trim() : "hold";
}

function firstOutputSignal(signals = []) {
    return Array.isArray(signals)
        ? String(signals.find((signal) => String(signal || "").trim()) || "").trim()
        : "";
}

function findActuatorActionByIds(actions = [], actionIds = new Set()) {
    return Array.isArray(actions)
        ? actions.find((action) => actionIds.has(String(action?.id || "").trim()))
        : null;
}

export function resolveMotionSolenoidDirectionPair(actionId = "", actionName = "") {
    const normalizedId = String(actionId || "").trim();
    const normalizedName = String(actionName || "").trim();
    if (
        normalizedId === "up"
        || normalizedId === "down"
        || normalizedName.includes("上昇")
        || normalizedName.includes("下降")
    ) {
        return {
            positiveLabel: "上昇",
            negativeLabel: "下降"
        };
    }
    return {
        positiveLabel: "前進",
        negativeLabel: "後退"
    };
}

function resolvePneumaticValveStateKey(actionId = "") {
    const normalizedId = String(actionId || "").trim();
    if (POSITIVE_MOTION_ACTION_IDS.has(normalizedId)) return "extend";
    if (NEGATIVE_MOTION_ACTION_IDS.has(normalizedId)) return "retract";
    if (normalizedId === "stop") return "stop";
    if (normalizedId === "emergency") return "emergency";
    return "";
}

function createDefaultPneumaticValveStateTable(
    pneumaticValveMode = "doubleSolenoid",
    singleSolenoidActiveDirection = "positive"
) {
    const valveMode = normalizePneumaticValveMode(pneumaticValveMode, "pneumaticCylinder");
    const activeDirection = normalizeSolenoidActiveDirection(singleSolenoidActiveDirection);
    if (valveMode === "singleSolenoid") {
        const extendSingle = activeDirection === "positive" ? "on" : "off";
        const retractSingle = activeDirection === "positive" ? "off" : "on";
        return {
            extend: { positive: "hold", negative: "hold", single: extendSingle },
            retract: { positive: "hold", negative: "hold", single: retractSingle },
            stop: { positive: "hold", negative: "hold", single: "hold" },
            emergency: { positive: "hold", negative: "hold", single: "off" }
        };
    }
    return {
        extend: { positive: "on", negative: "off", single: "hold" },
        retract: { positive: "off", negative: "on", single: "hold" },
        stop: { positive: "hold", negative: "hold", single: "hold" },
        emergency: { positive: "off", negative: "off", single: "hold" }
    };
}

const PNEUMATIC_VALVE_MODEL_PRESETS = Object.freeze({
    doubleHoldStop: Object.freeze({
        pneumaticValveMode: "doubleSolenoid",
        singleSolenoidActiveDirection: "positive",
        stopMode: "hold",
        label: "5/2 両ソレ / 停止=保持"
    }),
    doubleOffStop: Object.freeze({
        pneumaticValveMode: "doubleSolenoid",
        singleSolenoidActiveDirection: "positive",
        stopMode: "off",
        label: "5/2 両ソレ / 停止=両OFF"
    }),
    singlePositiveHoldStop: Object.freeze({
        pneumaticValveMode: "singleSolenoid",
        singleSolenoidActiveDirection: "positive",
        stopMode: "hold",
        label: "5/2 片ソレ(通電で前進/上昇) / 停止=保持"
    }),
    singlePositiveOffStop: Object.freeze({
        pneumaticValveMode: "singleSolenoid",
        singleSolenoidActiveDirection: "positive",
        stopMode: "off",
        label: "5/2 片ソレ(通電で前進/上昇) / 停止=OFF"
    }),
    singleNegativeHoldStop: Object.freeze({
        pneumaticValveMode: "singleSolenoid",
        singleSolenoidActiveDirection: "negative",
        stopMode: "hold",
        label: "5/2 片ソレ(通電で後退/下降) / 停止=保持"
    }),
    singleNegativeOffStop: Object.freeze({
        pneumaticValveMode: "singleSolenoid",
        singleSolenoidActiveDirection: "negative",
        stopMode: "off",
        label: "5/2 片ソレ(通電で後退/下降) / 停止=OFF"
    })
});

function buildPresetPneumaticValveStateTable(
    pneumaticValveMode = "doubleSolenoid",
    singleSolenoidActiveDirection = "positive",
    stopMode = "hold"
) {
    const stateTable = createDefaultPneumaticValveStateTable(
        pneumaticValveMode,
        singleSolenoidActiveDirection
    );
    if (String(stopMode || "").trim() === "off") {
        stateTable.stop = normalizePneumaticValveStateRow(
            pneumaticValveMode === "singleSolenoid"
                ? { positive: "hold", negative: "hold", single: "off" }
                : { positive: "off", negative: "off", single: "hold" },
            stateTable.stop
        );
    }
    return stateTable;
}

function normalizePneumaticValveStateRow(row = {}, fallback = {}) {
    return {
        positive: normalizeValveOutputState(row?.positive, fallback?.positive || "hold"),
        negative: normalizeValveOutputState(row?.negative, fallback?.negative || "hold"),
        single: normalizeValveOutputState(row?.single, fallback?.single || "hold")
    };
}

function normalizePneumaticValveStateTable(
    stateTable = {},
    pneumaticValveMode = "doubleSolenoid",
    singleSolenoidActiveDirection = "positive"
) {
    const defaults = createDefaultPneumaticValveStateTable(
        pneumaticValveMode,
        singleSolenoidActiveDirection
    );
    const normalized = {};
    PNEUMATIC_VALVE_STATE_KEYS.forEach((stateKey) => {
        normalized[stateKey] = normalizePneumaticValveStateRow(
            stateTable?.[stateKey],
            defaults[stateKey]
        );
    });
    return normalized;
}

function buildCommandStateRowFromAction(
    action = null,
    pneumaticValveMode = "doubleSolenoid",
    solenoidConfig = {}
) {
    const defaults = createDefaultPneumaticValveStateTable(
        pneumaticValveMode,
        solenoidConfig?.singleSolenoidActiveDirection || "positive"
    );
    const fallback = defaults.stop;
    const command = normalizeActionCommand(action?.command);
    if (normalizePneumaticValveMode(pneumaticValveMode, "pneumaticCylinder") === "singleSolenoid") {
        const singleOutput = String(solenoidConfig?.singleOutput || "").trim();
        return {
            positive: "hold",
            negative: "hold",
            single: !singleOutput
                ? fallback.single
                : command.setOutputs.includes(singleOutput)
                    ? "on"
                    : command.resetOutputs.includes(singleOutput)
                        ? "off"
                        : "hold"
        };
    }
    const positiveOutput = String(solenoidConfig?.positiveOutput || "").trim();
    const negativeOutput = String(solenoidConfig?.negativeOutput || "").trim();
    return {
        positive: !positiveOutput
            ? fallback.positive
            : command.setOutputs.includes(positiveOutput)
                ? "on"
                : command.resetOutputs.includes(positiveOutput)
                    ? "off"
                    : "hold",
        negative: !negativeOutput
            ? fallback.negative
            : command.setOutputs.includes(negativeOutput)
                ? "on"
                : command.resetOutputs.includes(negativeOutput)
                    ? "off"
                    : "hold",
        single: "hold"
    };
}

function derivePneumaticValveStateTableFromActions(
    actions = [],
    pneumaticValveMode = "doubleSolenoid",
    solenoidConfig = {}
) {
    const defaults = createDefaultPneumaticValveStateTable(
        pneumaticValveMode,
        solenoidConfig?.singleSolenoidActiveDirection || "positive"
    );
    const table = normalizePneumaticValveStateTable(
        {},
        pneumaticValveMode,
        solenoidConfig?.singleSolenoidActiveDirection || "positive"
    );
    ["extend", "retract", "stop"].forEach((actionId) => {
        const action = Array.isArray(actions)
            ? actions.find((candidate) => String(candidate?.id || "").trim() === actionId)
            : null;
        if (!action) {
            table[actionId] = defaults[actionId];
            return;
        }
        table[actionId] = normalizePneumaticValveStateRow(
            buildCommandStateRowFromAction(action, pneumaticValveMode, solenoidConfig),
            defaults[actionId]
        );
    });
    table.emergency = defaults.emergency;
    return table;
}

export function deriveActuatorSolenoidConfig(actions = [], pneumaticValveMode = "doubleSolenoid") {
    const valveMode = normalizePneumaticValveMode(pneumaticValveMode, "pneumaticCylinder");
    const positiveAction = findActuatorActionByIds(actions, POSITIVE_MOTION_ACTION_IDS);
    const negativeAction = findActuatorActionByIds(actions, NEGATIVE_MOTION_ACTION_IDS);
    const positiveSet = firstOutputSignal(positiveAction?.command?.setOutputs);
    const positiveReset = firstOutputSignal(positiveAction?.command?.resetOutputs);
    const negativeSet = firstOutputSignal(negativeAction?.command?.setOutputs);
    const negativeReset = firstOutputSignal(negativeAction?.command?.resetOutputs);

    if (valveMode === "singleSolenoid") {
        if (positiveSet || negativeReset) {
            return {
                positiveOutput: "",
                negativeOutput: "",
                singleOutput: positiveSet || negativeReset,
                singleSolenoidActiveDirection: "positive"
            };
        }
        if (negativeSet || positiveReset) {
            return {
                positiveOutput: "",
                negativeOutput: "",
                singleOutput: negativeSet || positiveReset,
                singleSolenoidActiveDirection: "negative"
            };
        }
        return {
            positiveOutput: "",
            negativeOutput: "",
            singleOutput: "",
            singleSolenoidActiveDirection: "positive"
        };
    }

    return {
        positiveOutput: positiveSet || negativeReset || "",
        negativeOutput: negativeSet || positiveReset || "",
        singleOutput: "",
        singleSolenoidActiveDirection: "positive"
    };
}

export function projectActuatorSolenoidConfigFromValveModel(valveModel = {}, pneumaticValveMode = "doubleSolenoid") {
    return {
        positiveOutput: String(valveModel?.positiveOutput || "").trim(),
        negativeOutput: String(valveModel?.negativeOutput || "").trim(),
        singleOutput: String(valveModel?.singleOutput || "").trim(),
        singleSolenoidActiveDirection: normalizeSolenoidActiveDirection(
            valveModel?.singleSolenoidActiveDirection
        )
    };
}

export function normalizeActuatorSolenoidConfig(
    config = {},
    actuatorType = "",
    actions = [],
    pneumaticValveMode = "doubleSolenoid",
    valveModel = null
) {
    if (normalizeAxisActuatorType(actuatorType) !== "pneumaticCylinder") return null;
    const derived = valveModel
        ? projectActuatorSolenoidConfigFromValveModel(valveModel, pneumaticValveMode)
        : deriveActuatorSolenoidConfig(actions, pneumaticValveMode);
    const source = config && typeof config === "object" ? config : {};
    const singleSolenoidActiveDirection = normalizeSolenoidActiveDirection(
        source.singleSolenoidActiveDirection || derived.singleSolenoidActiveDirection
    );
    const positiveOutput = String(
        source.positiveOutput
        || derived.positiveOutput
        || (singleSolenoidActiveDirection === "positive" ? source.singleOutput : "")
        || ""
    ).trim();
    const negativeOutput = String(
        source.negativeOutput
        || derived.negativeOutput
        || (singleSolenoidActiveDirection === "negative" ? source.singleOutput : "")
        || ""
    ).trim();
    const singleOutput = String(
        source.singleOutput
        || derived.singleOutput
        || (singleSolenoidActiveDirection === "positive" ? positiveOutput : negativeOutput)
        || ""
    ).trim();
    return {
        positiveOutput,
        negativeOutput,
        singleOutput,
        singleSolenoidActiveDirection
    };
}

export function normalizePneumaticActuatorValveModel(
    valveModel = {},
    actuatorType = "",
    actions = [],
    pneumaticValveMode = "doubleSolenoid",
    solenoidConfig = null
) {
    if (normalizeAxisActuatorType(actuatorType) !== "pneumaticCylinder") return null;
    const source = valveModel && typeof valveModel === "object" ? valveModel : {};
    const baseSolenoidConfig = normalizeActuatorSolenoidConfig(
        solenoidConfig || source,
        actuatorType,
        actions,
        pneumaticValveMode
    );
    const singleSolenoidActiveDirection = normalizeSolenoidActiveDirection(
        source.singleSolenoidActiveDirection || baseSolenoidConfig?.singleSolenoidActiveDirection
    );
    const derivedStateTable = derivePneumaticValveStateTableFromActions(
        actions,
        pneumaticValveMode,
        {
            ...baseSolenoidConfig,
            singleSolenoidActiveDirection
        }
    );
    const normalizedStateTable = {};
    PNEUMATIC_VALVE_STATE_KEYS.forEach((stateKey) => {
        normalizedStateTable[stateKey] = normalizePneumaticValveStateRow(
            source?.stateTable?.[stateKey] || derivedStateTable[stateKey],
            derivedStateTable[stateKey]
        );
    });
    return {
        positiveOutput: String(source.positiveOutput || baseSolenoidConfig?.positiveOutput || "").trim(),
        negativeOutput: String(source.negativeOutput || baseSolenoidConfig?.negativeOutput || "").trim(),
        singleOutput: String(source.singleOutput || baseSolenoidConfig?.singleOutput || "").trim(),
        singleSolenoidActiveDirection,
        stateTable: normalizedStateTable
    };
}

function addReservedValveOutputName(reservedNames = new Set(), value = "") {
    const name = String(value || "").trim().toUpperCase();
    if (name) reservedNames.add(name);
}

function buildReservedValveOutputNames(actuators = [], editingActuatorId = "") {
    const reservedNames = new Set();
    const editingId = String(editingActuatorId || "");
    (Array.isArray(actuators) ? actuators : []).forEach((actuator) => {
        if (String(actuator?.id || "") === editingId) return;
        if (normalizeAxisActuatorType(actuator?.actuatorType) !== "pneumaticCylinder") return;
        const pneumaticValveMode = normalizePneumaticValveMode(
            actuator?.pneumaticValveMode,
            actuator?.actuatorType
        );
        const valveModel = normalizePneumaticActuatorValveModel(
            actuator?.valveModel,
            actuator?.actuatorType,
            actuator?.actions,
            pneumaticValveMode,
            actuator?.solenoidConfig
        );
        addReservedValveOutputName(reservedNames, valveModel?.positiveOutput);
        addReservedValveOutputName(reservedNames, valveModel?.negativeOutput);
        addReservedValveOutputName(reservedNames, valveModel?.singleOutput);
    });
    return reservedNames;
}

function buildNextAutoValveOutputName(reservedNames = new Set()) {
    let nextNumber = 101;
    reservedNames.forEach((name) => {
        const match = /^YV(\d+)$/.exec(String(name || "").trim().toUpperCase());
        if (!match) return;
        const value = Number(match[1]);
        if (Number.isFinite(value) && value >= nextNumber) {
            nextNumber = value + 1;
        }
    });
    let candidate = `YV${nextNumber}`;
    while (reservedNames.has(candidate.toUpperCase())) {
        nextNumber += 1;
        candidate = `YV${nextNumber}`;
    }
    reservedNames.add(candidate.toUpperCase());
    return candidate;
}

function autoAssignPneumaticValveOutputs(
    valveModel = {},
    pneumaticValveMode = "doubleSolenoid",
    actuators = [],
    editingActuatorId = ""
) {
    const nextValveModel = valveModel && typeof valveModel === "object"
        ? {
            ...valveModel,
            stateTable: valveModel?.stateTable && typeof valveModel.stateTable === "object"
                ? valveModel.stateTable
                : {}
        }
        : { stateTable: {} };
    const reservedNames = buildReservedValveOutputNames(actuators, editingActuatorId);
    addReservedValveOutputName(reservedNames, nextValveModel?.positiveOutput);
    addReservedValveOutputName(reservedNames, nextValveModel?.negativeOutput);
    addReservedValveOutputName(reservedNames, nextValveModel?.singleOutput);
    if (normalizePneumaticValveMode(pneumaticValveMode, "pneumaticCylinder") === "singleSolenoid") {
        if (!String(nextValveModel?.singleOutput || "").trim()) {
            nextValveModel.singleOutput = buildNextAutoValveOutputName(reservedNames);
        }
        return nextValveModel;
    }
    if (!String(nextValveModel?.positiveOutput || "").trim()) {
        nextValveModel.positiveOutput = buildNextAutoValveOutputName(reservedNames);
    }
    if (!String(nextValveModel?.negativeOutput || "").trim()) {
        nextValveModel.negativeOutput = buildNextAutoValveOutputName(reservedNames);
    }
    return nextValveModel;
}

export function assignDeferredPneumaticValveOutputs(project = {}) {
    const actuators = Array.isArray(project?.actuators) ? project.actuators : [];
    actuators.forEach((actuator) => {
        if (!actuator || normalizeAxisActuatorType(actuator?.actuatorType) !== "pneumaticCylinder") {
            return;
        }
        const pneumaticValveMode = normalizePneumaticValveMode(
            actuator?.pneumaticValveMode,
            actuator?.actuatorType
        );
        actuator.pneumaticValveMode = pneumaticValveMode;
        actuator.valveModel = autoAssignPneumaticValveOutputs(
            normalizePneumaticActuatorValveModel(
                actuator?.valveModel,
                actuator?.actuatorType,
                actuator?.actions,
                pneumaticValveMode,
                actuator?.solenoidConfig
            ),
            pneumaticValveMode,
            actuators,
            actuator?.id || ""
        );
        syncPneumaticActuatorActionCommands(actuator);
    });
    return project;
}

function buildCommandStateForActionId(
    actionId = "",
    pneumaticValveMode = "doubleSolenoid",
    valveModel = {}
) {
    const stateKey = resolvePneumaticValveStateKey(actionId);
    if (!stateKey) return null;
    const normalizedValveModel = normalizePneumaticActuatorValveModel(
        valveModel,
        "pneumaticCylinder",
        [],
        pneumaticValveMode,
        valveModel
    );
    return normalizedValveModel?.stateTable?.[stateKey] || null;
}

function buildActionCommandFromValveState(
    stateRow = null,
    pneumaticValveMode = "doubleSolenoid",
    valveModel = {}
) {
    if (!stateRow) return { setOutputs: [], resetOutputs: [] };
    const valveMode = normalizePneumaticValveMode(pneumaticValveMode, "pneumaticCylinder");
    if (valveMode === "singleSolenoid") {
        const singleOutput = String(valveModel?.singleOutput || "").trim();
        const singleState = normalizeValveOutputState(stateRow?.single, "hold");
        return {
            setOutputs: singleOutput && singleState === "on" ? [singleOutput] : [],
            resetOutputs: singleOutput && singleState === "off" ? [singleOutput] : []
        };
    }
    const positiveOutput = String(valveModel?.positiveOutput || "").trim();
    const negativeOutput = String(valveModel?.negativeOutput || "").trim();
    const setOutputs = [];
    const resetOutputs = [];
    if (positiveOutput) {
        const positiveState = normalizeValveOutputState(stateRow?.positive, "hold");
        if (positiveState === "on") setOutputs.push(positiveOutput);
        if (positiveState === "off") resetOutputs.push(positiveOutput);
    }
    if (negativeOutput) {
        const negativeState = normalizeValveOutputState(stateRow?.negative, "hold");
        if (negativeState === "on") setOutputs.push(negativeOutput);
        if (negativeState === "off") resetOutputs.push(negativeOutput);
    }
    return { setOutputs, resetOutputs };
}

export function syncPneumaticActuatorActionCommands(actuator = {}) {
    if (!actuator || normalizeAxisActuatorType(actuator?.actuatorType) !== "pneumaticCylinder") {
        return actuator;
    }
    if (!Array.isArray(actuator.actions)) {
        actuator.actions = [];
    }
    const pneumaticValveMode = normalizePneumaticValveMode(actuator?.pneumaticValveMode, actuator?.actuatorType);
    const valveModel = normalizePneumaticActuatorValveModel(
        actuator?.valveModel,
        actuator?.actuatorType,
        actuator.actions,
        pneumaticValveMode,
        actuator?.solenoidConfig
    );
    actuator.valveModel = valveModel;
    const solenoidConfig = normalizeActuatorSolenoidConfig(
        actuator?.solenoidConfig,
        actuator?.actuatorType,
        actuator.actions,
        pneumaticValveMode,
        valveModel
    );
    actuator.solenoidConfig = solenoidConfig;
    actuator.actions = actuator.actions.map((action) => {
        const actionId = String(action?.id || "").trim();
        const stateRow = buildCommandStateForActionId(actionId, pneumaticValveMode, valveModel);
        if (!stateRow) {
            return action;
        }

        const command = normalizeActionCommand(action?.command);
        const nextCommand = buildActionCommandFromValveState(
            stateRow,
            pneumaticValveMode,
            valveModel
        );

        return {
            ...action,
            command: {
                ...command,
                setOutputs: nextCommand.setOutputs,
                resetOutputs: nextCommand.resetOutputs
            }
        };
    });
    return actuator;
}

export function normalizeControlSpecActionCompletionMode(value = "", fallback = {}) {
    const normalized = String(value || "").trim();
    if (ACTION_COMPLETION_MODES.has(normalized)) return normalized;
    const fallbackMode = String(fallback?.completionMode || "").trim();
    if (ACTION_COMPLETION_MODES.has(fallbackMode)) return fallbackMode;

    const doneConditions = Array.isArray(fallback?.doneConditions) ? fallback.doneConditions : [];
    const doneSig = String(fallback?.doneSig || "").trim();
    const timeoutSec = Number(fallback?.timeoutSec);
    if (doneConditions.length > 0 || doneSig) return "sensor";
    if (Number.isFinite(timeoutSec) && timeoutSec > 0) return "timer";
    return "ignore";
}

export function getControlSpecActionCompletionModeLabel(mode = "sensor") {
    const normalized = normalizeControlSpecActionCompletionMode(mode);
    if (normalized === "timer") return "タイマ完了";
    if (normalized === "ignore") return "完了待ちなし";
    return "センサ監視";
}

export function buildControlSpecActionCompletionModeFieldState(completionMode = "sensor", timeoutSec = 0) {
    const normalized = normalizeControlSpecActionCompletionMode(completionMode, { timeoutSec });
    return {
        completionMode: normalized,
        doneSignalDisabled: normalized !== "sensor",
        doneValueDisabled: normalized !== "sensor",
        timeoutDisabled: normalized === "ignore",
        timeoutValue: normalized === "ignore" ? 0 : timeoutSec
    };
}

export function normalizeActuatorTypeCatalogEntry(entry, index = 0) {
    const rawId = String(entry?.id || "").trim();
    const legacyCylinder = rawId === "cylinder" || rawId === "cylinderIo";
    const id = legacyCylinder ? "pneumaticCylinder" : rawId || `type_${index + 1}`;
    const defaultLabel = id === "pneumaticCylinder" && legacyCylinder ? "空圧シリンダ" : id;
    const label = String(legacyCylinder ? defaultLabel : entry?.label || defaultLabel).trim() || defaultLabel;
    const defaultActionNames = legacyCylinder
        ? ["前進", "後退", "停止"]
        : Array.isArray(entry?.defaultActionNames)
        ? entry.defaultActionNames.map((name) => String(name || "").trim()).filter(Boolean)
        : [];
    return { id, label, defaultActionNames };
}

export function buildControlSpecFindingDisplayModel(
    finding = {},
    localizeMessage = localizeControlSpecFindingMessage
) {
    const severity = String(finding?.severity || "");
    return {
        severity,
        severityColor: severity === "error" ? "#b71c1c" : (severity === "warning" ? "#8a5a00" : "#555"),
        code: String(finding?.code || ""),
        message: typeof localizeMessage === "function" ? localizeMessage(finding) : String(finding?.message || ""),
        sourceText: formatControlSpecFindingSource(finding?.source),
        targetText: formatControlSpecFindingTarget(finding)
    };
}

export function buildControlSpecFindingRowModel(
    finding = {},
    localizeMessage = localizeControlSpecFindingMessage
) {
    return {
        ...buildControlSpecFindingDisplayModel(finding, localizeMessage),
        actions: buildControlSpecFindingTargetActions(finding)
    };
}

export function buildControlSpecOutputSections(vm = {}) {
    const ioTable = Array.isArray(vm?.ioTable) ? vm.ioTable : [];
    const valveModels = Array.isArray(vm?.valveModels) ? vm.valveModels : [];
    const actionSteps = Array.isArray(vm?.actionSteps) ? vm.actionSteps : [];
    const conditionSteps = Array.isArray(vm?.conditionSteps) ? vm.conditionSteps : [];
    const errorSteps = Array.isArray(vm?.errorSteps) ? vm.errorSteps : [];
    const timeoutFindings = Array.isArray(vm?.timeoutFindings) ? vm.timeoutFindings : [];
    const errorCount = errorSteps.length + timeoutFindings.length;
    return {
        io: {
            rows: ioTable,
            count: ioTable.length,
            metaText: `I/O ${ioTable.length}件`,
            emptyMessage: "I/O表はまだ生成されていません。フローとアクチュエータ定義を設定すると表示されます。"
        },
        valveModel: {
            rows: valveModels,
            count: valveModels.length,
            metaText: `弁モデル ${valveModels.length}件`,
            emptyMessage: "弁モデル表はまだ生成されていません。空圧シリンダのアクチュエータを設定すると表示されます。"
        },
        action: {
            rows: actionSteps,
            count: actionSteps.length,
            metaText: `動作 ${actionSteps.length}件`,
            emptyMessage: "動作一覧はまだ生成されていません。フローに動作ノードを配置すると表示されます。"
        },
        condition: {
            rows: conditionSteps,
            count: conditionSteps.length,
            metaText: `条件 ${conditionSteps.length}件`,
            emptyMessage: "条件一覧はまだ生成されていません。フローに条件ノードを配置すると表示されます。"
        },
        error: {
            errorSteps,
            timeoutFindings,
            count: errorCount,
            metaText: `エラー/timeout ${errorCount}件`,
            emptyMessage: "エラー一覧はまだ生成されていません。エラーノードまたは timeout finding があると表示されます。"
        }
    };
}

function formatControlSpecValveStateText(value = "") {
    const normalized = normalizeValveOutputState(value, "hold");
    if (normalized === "on") return "ON";
    if (normalized === "off") return "OFF";
    return "保持";
}

function buildControlSpecValveStateSummary(
    stateRow = {},
    valveModel = {},
    pneumaticValveMode = "doubleSolenoid",
    directionPair = resolveMotionSolenoidDirectionPair("extend")
) {
    if (normalizePneumaticValveMode(pneumaticValveMode, "pneumaticCylinder") === "singleSolenoid") {
        const singleOutput = String(valveModel?.singleOutput || "").trim() || "未設定";
        return `${singleOutput}=${formatControlSpecValveStateText(stateRow?.single)}`;
    }
    const positiveOutput = String(valveModel?.positiveOutput || "").trim() || "未設定";
    const negativeOutput = String(valveModel?.negativeOutput || "").trim() || "未設定";
    return `${directionPair.positiveLabel}:${positiveOutput}=${formatControlSpecValveStateText(stateRow?.positive)} / ${directionPair.negativeLabel}:${negativeOutput}=${formatControlSpecValveStateText(stateRow?.negative)}`;
}

export function buildControlSpecValveModelRowModel(
    actuator = {},
    getValveModeLabel = getPneumaticValveModeLabel,
    normalizeActuatorType = normalizeAxisActuatorType
) {
    const actuatorType = typeof normalizeActuatorType === "function"
        ? normalizeActuatorType(actuator?.actuatorType)
        : String(actuator?.actuatorType || "");
    const pneumaticValveMode = normalizePneumaticValveMode(actuator?.pneumaticValveMode, actuatorType);
    const valveModel = normalizePneumaticActuatorValveModel(
        actuator?.valveModel,
        actuatorType,
        actuator?.actions,
        pneumaticValveMode,
        actuator?.solenoidConfig
    );
    const usesVerticalLabels = Array.isArray(actuator?.actions)
        && actuator.actions.some((action) => ["up", "down"].includes(String(action?.id || "").trim()));
    const directionPair = resolveMotionSolenoidDirectionPair(usesVerticalLabels ? "up" : "extend");
    const valveModeText = typeof getValveModeLabel === "function"
        ? getValveModeLabel(pneumaticValveMode)
        : String(pneumaticValveMode || "");
    const outputsText = pneumaticValveMode === "singleSolenoid"
        ? `${String(valveModel?.singleOutput || "").trim() || "未設定"} / 通電=${String(valveModel?.singleSolenoidActiveDirection || "").trim() === "negative" ? directionPair.negativeLabel : directionPair.positiveLabel}`
        : `${directionPair.positiveLabel}:${String(valveModel?.positiveOutput || "").trim() || "未設定"} / ${directionPair.negativeLabel}:${String(valveModel?.negativeOutput || "").trim() || "未設定"}`;
    return {
        actuatorIdText: String(actuator?.id || ""),
        actuatorNameText: String(actuator?.name || ""),
        valveModeText,
        outputsText,
        extendText: buildControlSpecValveStateSummary(valveModel?.stateTable?.extend, valveModel, pneumaticValveMode, directionPair),
        retractText: buildControlSpecValveStateSummary(valveModel?.stateTable?.retract, valveModel, pneumaticValveMode, directionPair),
        stopText: buildControlSpecValveStateSummary(valveModel?.stateTable?.stop, valveModel, pneumaticValveMode, directionPair),
        emergencyText: buildControlSpecValveStateSummary(valveModel?.stateTable?.emergency, valveModel, pneumaticValveMode, directionPair)
    };
}

export function buildControlSpecIoRowModel(entry = {}, formatReferencedBy = () => "") {
    const direction = String(entry?.direction || "");
    return {
        direction,
        badgeClass: direction === "input" ? "input" : (direction === "internal" ? "internal" : "output"),
        address: String(entry?.address || ""),
        signalId: String(entry?.signalId || ""),
        signalName: String(entry?.signalName || ""),
        usagesText: Array.isArray(entry?.usages) ? entry.usages.join(", ") : "",
        refsText: typeof formatReferencedBy === "function" ? formatReferencedBy(entry?.referencedBy) : ""
    };
}

export function formatIoReferencedBy(refs = []) {
    const list = Array.isArray(refs) ? refs : [];
    if (!list.length) return "";
    return list.map((ref) => {
        const step = ref.step != null ? `Step ${ref.step}` : "Step -";
        const label = ref.label ? ` ${ref.label}` : "";
        const usage = ref.usage ? ` (${ref.usage})` : "";
        return `${step}${label}${usage}`;
    }).join("; ");
}

export function formatControlSpecCommands(commands = []) {
    const list = Array.isArray(commands) ? commands : [];
    if (!list.length) return "";
    return list.map((command) => {
        const type = command.type || "command";
        const signalId = command.signalId || "";
        return signalId ? `${type}: ${signalId}` : type;
    }).join("; ");
}

export function formatControlSpecConditions(conditions = []) {
    const list = Array.isArray(conditions) ? conditions : [];
    if (!list.length) return "";
    return list.map((condition) => {
        const signalId = condition.signalId || "";
        const operator = condition.operator || "==";
        const value = condition.value === false ? "FALSE" : "TRUE";
        return signalId ? `${signalId} ${operator} ${value}` : "";
    }).filter(Boolean).join("; ");
}

export function formatControlSpecConditionStepExpression(step = {}) {
    if (step.conditionSignalId) return step.conditionSignalId;
    const terms = Array.isArray(step.conditionTerms) ? step.conditionTerms : [];
    if (terms.length) {
        return terms.map((term) => term.raw || term.ident || "").filter(Boolean).join("; ");
    }
    const entries = Array.isArray(step.conditions) ? step.conditions : [];
    return entries.map((entry) => entry.expression || entry.label || "").filter(Boolean).join("; ");
}

export function formatControlSpecConditionBranches(step = {}) {
    const labels = [
        step.trueLabel ? `TRUE: ${step.trueLabel}` : "",
        step.falseLabel ? `FALSE: ${step.falseLabel}` : "",
        step.timeoutLabel ? `TIMEOUT: ${step.timeoutLabel}` : ""
    ].filter(Boolean);
    return labels.join("; ");
}

export function getMotorControlTypeLabel(motorControlType = "") {
    switch (motorControlType) {
        case "pulseCommand":
            return "パルスモータ（コマンド入力）";
        case "pulseTrain":
            return "パルスモータ（パルス入力）";
        case "servo":
            return "サーボモータ";
        case "inverter":
            return "インバータ";
        case "speedControl":
        default:
            return "速度指定モータ";
    }
}

export function getPneumaticValveModeLabel(pneumaticValveMode = "") {
    const normalized = normalizePneumaticValveMode(pneumaticValveMode, "pneumaticCylinder");
    return normalized === "singleSolenoid" ? "片ソレノイド" : "両ソレノイド";
}

export function getActuatorTypeLabel(catalogLabel = "", normalizedActuatorType = "") {
    return String(catalogLabel || normalizedActuatorType || "空圧シリンダ");
}

export function buildActuatorTypeCatalogFormInput(values = {}) {
    return {
        rawId: String(values?.rawId || ""),
        label: String(values?.label || ""),
        actionsText: String(values?.actionsText || "")
    };
}

export function buildActuatorTypeCatalogDraft(values = {}, id = "") {
    return {
        id: String(id || "").trim(),
        label: String(values?.label || "").trim(),
        defaultActionNames: String(values?.actionsText || "")
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean)
    };
}

export function validateActuatorTypeCatalogDraft(draft = {}, editingTypeId = null, catalog = []) {
    if (!/^[a-zA-Z0-9_]+$/.test(String(draft?.id || ""))) {
        return { ok: false, errorMessage: "種類IDは半角英数字およびアンダースコア（_）のみ使用可能です。" };
    }
    if (!draft?.label) {
        return { ok: false, errorMessage: "表示名は必須項目です。" };
    }
    if (!Array.isArray(draft?.defaultActionNames) || draft.defaultActionNames.length === 0) {
        return { ok: false, errorMessage: "既定アクション候補を1件以上入力してください。" };
    }
    const list = Array.isArray(catalog) ? catalog : [];
    const duplicate = list.some((entry) => (
        String(entry?.id || "").toLowerCase() === String(draft?.id || "").toLowerCase()
        && entry?.id !== editingTypeId
    ));
    if (duplicate) {
        return { ok: false, errorMessage: `種類ID '${draft.id}' は既に登録されています。` };
    }
    return { ok: true };
}

export function buildSavedActuatorTypeCatalogState(catalog = [], editingTypeId = null, nextEntry = {}) {
    const list = Array.isArray(catalog) ? [...catalog] : [];
    if (editingTypeId) {
        const index = list.findIndex((entry) => entry.id === editingTypeId);
        if (index >= 0) {
            list[index] = nextEntry;
        }
    } else {
        list.push(nextEntry);
    }
    return {
        catalog: list,
        editingActuatorTypeCatalogId: nextEntry?.id || null
    };
}

export function buildDeleteActuatorTypeCatalogState(
    editingTypeId = "",
    defaultTypeIds = [],
    referenceSummary = {},
    catalog = []
) {
    const typeId = String(editingTypeId || "");
    if (!typeId) {
        return { ok: false, reason: "missing" };
    }
    if ((Array.isArray(defaultTypeIds) ? defaultTypeIds : []).includes(typeId)) {
        return {
            ok: false,
            alertMessage: "標準種類は削除できません。表示名や既定アクションを編集してください。"
        };
    }
    const axisCount = Number(referenceSummary?.axisCount || 0);
    const actuatorCount = Number(referenceSummary?.actuatorCount || 0);
    if (axisCount || actuatorCount) {
        return {
            ok: false,
            alertMessage: `この種類は軸 ${axisCount} 件、アクチュエータ ${actuatorCount} 件で使用中のため削除できません。`
        };
    }
    return {
        ok: true,
        confirmMessage: `種類 '${typeId}' を削除しますか？`,
        nextCatalog: (Array.isArray(catalog) ? catalog : []).filter((entry) => entry?.id !== typeId),
        editingActuatorTypeCatalogId: null
    };
}

export function buildActuatorTypeCatalogFormState(target = null, isCore = false) {
    return {
        editingActuatorTypeCatalogId: target?.id || null,
        inputId: target?.id || "",
        inputIdDisabled: Boolean(target),
        inputIdBackground: target ? "#eee" : "#fff",
        inputLabel: target?.label || "",
        inputActions: Array.isArray(target?.defaultActionNames) ? target.defaultActionNames.join(", ") : "",
        metaText: target
            ? `現在の種類: ${target.label} (${target.id})`
            : "新しい種類を追加できます。既定アクションはカンマ区切りで入力します。",
        deleteDisabled: !target || isCore,
        deleteOpacity: (!target || isCore) ? "0.5" : "1",
        deleteTitle: isCore ? "標準種類は削除できません" : "",
        selectValue: target?.id || ""
    };
}

export function buildActuatorTypeCatalogEditorState(entries = [], editingTypeId = null, escapeHtml = (value) => String(value ?? "")) {
    const list = Array.isArray(entries) ? entries : [];
    return {
        options: [
            `<option value="">新しい種類を追加...</option>`,
            ...list.map((entry) => `<option value="${escapeHtml(entry.id)}">${escapeHtml(entry.label)} (${escapeHtml(entry.id)})</option>`)
        ].join(""),
        selectedTypeId: editingTypeId && list.some((entry) => entry.id === editingTypeId)
            ? editingTypeId
            : (list[0]?.id || null)
    };
}

export function buildActuatorTypeReferenceSummary(typeId = "", axes = [], actuators = [], normalizeActuatorType = (value) => String(value || "")) {
    const normalizedTypeId = String(typeId || "");
    return {
        axisCount: (Array.isArray(axes) ? axes : []).filter((axis) => normalizeActuatorType(axis?.actuatorType) === normalizedTypeId).length,
        actuatorCount: (Array.isArray(actuators) ? actuators : []).filter((actuator) => normalizeActuatorType(actuator?.actuatorType) === normalizedTypeId).length
    };
}

export function buildActuatorTypeOptionsState(
    entries = [],
    selectedType = "pneumaticCylinder",
    includeBlank = false,
    escapeHtml = (value) => String(value ?? "")
) {
    const list = Array.isArray(entries) ? entries : [];
    const normalizedSelected = String(selectedType || "pneumaticCylinder");
    const options = list.map((entry) => {
        const selected = entry?.id === normalizedSelected ? " selected" : "";
        return `<option value="${escapeHtml(entry?.id || "")}"${selected}>${escapeHtml(entry?.label || "")}</option>`;
    });
    if (normalizedSelected && !list.some((entry) => entry?.id === normalizedSelected)) {
        options.push(`<option value="${escapeHtml(normalizedSelected)}" selected>${escapeHtml(normalizedSelected)}</option>`);
    }
    if (includeBlank) options.unshift(`<option value="">選択してください</option>`);
    return {
        options: options.join("")
    };
}

export function buildMotorControlTypeFieldState(actuatorType = "") {
    const enabled = String(actuatorType || "").trim() === "motor";
    return {
        enabled,
        disabled: !enabled,
        background: enabled ? "#fff" : "#f3f4f6"
    };
}

export function buildPneumaticValveModeFieldState(actuatorType = "") {
    const enabled = String(actuatorType || "").trim() === "pneumaticCylinder";
    return {
        enabled,
        disabled: !enabled,
        background: enabled ? "#fff" : "#f3f4f6"
    };
}

export function buildActuatorValveModelFieldState(
    actuatorType = "",
    pneumaticValveMode = "doubleSolenoid",
    valveModel = null
) {
    const enabled = String(actuatorType || "").trim() === "pneumaticCylinder";
    const normalizedValveMode = normalizePneumaticValveMode(pneumaticValveMode, actuatorType);
    const normalizedValveModel = enabled
        ? normalizePneumaticActuatorValveModel(valveModel, actuatorType, [], normalizedValveMode, valveModel)
        : null;
    return {
        enabled,
        showPanel: enabled,
        showSingleOutput: enabled && normalizedValveMode === "singleSolenoid",
        showDoubleOutputs: enabled && normalizedValveMode === "doubleSolenoid",
        valveModel: normalizedValveModel
    };
}

export function buildPneumaticValvePresetOptionsState(
    actuatorType = "",
    pneumaticValveMode = "doubleSolenoid",
    selectedPreset = "",
    escapeHtml = (value) => String(value ?? "")
) {
    if (normalizeAxisActuatorType(actuatorType) !== "pneumaticCylinder") {
        return {
            options: `<option value="">選択してください</option>`,
            selectedPreset: ""
        };
    }
    const normalizedValveMode = normalizePneumaticValveMode(pneumaticValveMode, actuatorType);
    const entries = Object.entries(PNEUMATIC_VALVE_MODEL_PRESETS)
        .filter(([, preset]) => preset.pneumaticValveMode === normalizedValveMode)
        .map(([id, preset]) => ({ id, label: preset.label }));
    const normalizedSelected = entries.some((entry) => entry.id === selectedPreset)
        ? selectedPreset
        : "";
    return {
        options: [
            `<option value="">選択してください</option>`,
            ...entries.map((entry) => {
                const selected = entry.id === normalizedSelected ? " selected" : "";
                return `<option value="${escapeHtml(entry.id)}"${selected}>${escapeHtml(entry.label)}</option>`;
            })
        ].join(""),
        selectedPreset: normalizedSelected
    };
}

export function applyPneumaticActuatorValvePreset(
    presetId = "",
    actuatorType = "",
    valveModel = null
) {
    if (normalizeAxisActuatorType(actuatorType) !== "pneumaticCylinder") {
        return {
            applied: false,
            pneumaticValveMode: "",
            valveModel: null
        };
    }
    const preset = PNEUMATIC_VALVE_MODEL_PRESETS[String(presetId || "").trim()];
    const currentValveModel = valveModel && typeof valveModel === "object" ? valveModel : {};
    const fallbackValveMode = normalizePneumaticValveMode(
        currentValveModel?.pneumaticValveMode || "doubleSolenoid",
        "pneumaticCylinder"
    );
    if (!preset) {
        return {
            applied: false,
            pneumaticValveMode: fallbackValveMode,
            valveModel: normalizePneumaticActuatorValveModel(
                currentValveModel,
                actuatorType,
                [],
                fallbackValveMode,
                currentValveModel
            )
        };
    }
    const nextValveMode = normalizePneumaticValveMode(
        preset.pneumaticValveMode,
        "pneumaticCylinder"
    );
    return {
        applied: true,
        pneumaticValveMode: nextValveMode,
        valveModel: normalizePneumaticActuatorValveModel(
            {
                ...currentValveModel,
                singleSolenoidActiveDirection: preset.singleSolenoidActiveDirection,
                stateTable: buildPresetPneumaticValveStateTable(
                    nextValveMode,
                    preset.singleSolenoidActiveDirection,
                    preset.stopMode
                )
            },
            actuatorType,
            [],
            nextValveMode,
            currentValveModel
        )
    };
}

export function buildActuatorControlMethodFieldState(
    {
        actuatorType = "",
        controlMethodValue = "",
        force = false,
        axisRefValue = "",
        defaultAxisRef = ""
    } = {},
    normalizeControlMethod = (value) => String(value || "")
) {
    const suggested = normalizeControlMethod("", actuatorType);
    const shouldReplace = force || !controlMethodValue || controlMethodValue === "motionFb";
    const selectedControlMethod = normalizeControlMethod(
        shouldReplace ? suggested : controlMethodValue,
        actuatorType
    );
    const needsAxisRef = selectedControlMethod === "plcopenMotion" || selectedControlMethod === "plcopenFluidPower";
    return {
        selectedControlMethod,
        axisRefDisabled: !needsAxisRef,
        axisRefBackground: needsAxisRef ? "#fff" : "#f3f4f6",
        axisRefValue: needsAxisRef && !String(axisRefValue || "").trim() ? defaultAxisRef : axisRefValue
    };
}

export function buildActuatorTypeSelectRefreshState(
    {
        currentValue = "",
        fallback = "pneumaticCylinder",
        entries = [],
        includeBlank = false
    } = {},
    normalizeActuatorType = (value) => String(value || ""),
    escapeHtml = (value) => String(value ?? "")
) {
    const current = normalizeActuatorType(currentValue || fallback);
    return {
        current,
        options: buildActuatorTypeOptionsState(entries, current, includeBlank, escapeHtml).options
    };
}

export function buildFlowActuatorOptionsState(
    actuators = [],
    selectedId = "",
    escapeHtml = (value) => String(value ?? "")
) {
    const list = Array.isArray(actuators) ? actuators : [];
    return {
        options: [
            `<option value="">選択してください</option>`,
            ...list.map((actuator) =>
                `<option value="${escapeHtml(actuator?.id || "")}"${actuator?.id === selectedId ? " selected" : ""}>${escapeHtml(actuator?.name || actuator?.id || "")} (${escapeHtml(actuator?.id || "")})</option>`
            )
        ].join("")
    };
}

export function buildFlowActionOptionsState(
    actions = [],
    selectedId = "",
    escapeHtml = (value) => String(value ?? "")
) {
    const list = Array.isArray(actions) ? actions : [];
    return {
        options: [
            `<option value="">選択してください</option>`,
            ...list.map((action) =>
                `<option value="${escapeHtml(action?.id || "")}"${action?.id === selectedId ? " selected" : ""}>${escapeHtml(action?.name || action?.id || "")} (${escapeHtml(action?.id || "")})</option>`
            )
        ].join("")
    };
}

export function buildFlowActionSelectionState(
    actions = [],
    selectedId = "",
    escapeHtml = (value) => String(value ?? "")
) {
    const list = Array.isArray(actions) ? actions : [];
    const nextSelectedId = selectedId && list.some((action) => action?.id === selectedId)
        ? selectedId
        : (list[0]?.id || "");
    return {
        selectedActionId: nextSelectedId,
        options: buildFlowActionOptionsState(list, nextSelectedId, escapeHtml).options
    };
}

export function buildFlowMotionEditorState(
    {
        found = null,
        selectedActuatorType = "pneumaticCylinder",
        actionState = { selectedActionId: "", options: "" },
        selectedAction = null,
        selectedActuator = null
    } = {}
) {
    const hasMotion = Boolean(found?.motion);
    const completionModeState = buildFlowMotionCompletionModeState(selectedAction);
    const pneumaticValveModeState = buildFlowMotionPneumaticValveModeState(selectedActuatorType, selectedActuator);
    return {
        hasMotion,
        motionName: hasMotion ? String(found.motion.name || "") : "",
        selectedActuatorType: String(selectedActuatorType || "pneumaticCylinder"),
        selectedActionId: String(actionState?.selectedActionId || ""),
        actionOptions: String(actionState?.options || ""),
        showPneumaticValveMode: pneumaticValveModeState.showPneumaticValveMode,
        pneumaticValveMode: pneumaticValveModeState.pneumaticValveMode,
        pneumaticValveModeOptions: pneumaticValveModeState.pneumaticValveModeOptions,
        pneumaticValveModeHelpText: pneumaticValveModeState.pneumaticValveModeHelpText,
        completionMode: completionModeState.completionMode,
        completionModeOptions: completionModeState.completionModeOptions,
        completionModeHelpText: completionModeState.completionModeHelpText,
        completionModeDisabled: completionModeState.completionModeDisabled,
        missingMotionHelpText: "対応する既存動作が見つかりません。制御仕様の参照だけ編集できます。"
    };
}

export function buildFlowMotionCompletionModeState(action = null) {
    const selectedAction = action && typeof action === "object" ? action : null;
    const completionMode = normalizeControlSpecActionCompletionMode("", selectedAction || {});
    const options = [
        { value: "sensor", label: "センサ監視" },
        { value: "timer", label: "タイマ完了" },
        { value: "ignore", label: "完了待ちなし" }
    ].map((option) =>
        `<option value="${option.value}"${option.value === completionMode ? " selected" : ""}>${option.label}</option>`
    ).join("");
    if (!selectedAction) {
        return {
            completionMode,
            completionModeOptions: options,
            completionModeHelpText: "アクションを選択すると完了方式を表示できます。",
            completionModeDisabled: true
        };
    }
    const timeoutSec = Number(selectedAction?.timeoutSec);
    return {
        completionMode,
        completionModeOptions: options,
        completionModeHelpText: completionMode === "sensor"
            ? "完了条件信号はアクション定義の doneConditions を参照します。"
            : completionMode === "timer"
                ? `タイムアウト ${Number.isFinite(timeoutSec) && timeoutSec > 0 ? timeoutSec : 0} 秒で完了します。`
                : "完了待ちを行わず、次のノードへ進みます。",
        completionModeDisabled: false
    };
}

export function buildFlowMotionPneumaticValveModeState(actuatorType = "", actuator = null) {
    const showPneumaticValveMode = normalizeAxisActuatorType(actuatorType) === "pneumaticCylinder";
    const pneumaticValveMode = showPneumaticValveMode
        ? normalizePneumaticValveMode(actuator?.pneumaticValveMode, actuatorType)
        : "";
    return {
        showPneumaticValveMode,
        pneumaticValveMode,
        pneumaticValveModeOptions: `
            <option value="doubleSolenoid"${pneumaticValveMode === "doubleSolenoid" ? " selected" : ""}>両ソレノイド</option>
            <option value="singleSolenoid"${pneumaticValveMode === "singleSolenoid" ? " selected" : ""}>片ソレノイド</option>
        `,
        pneumaticValveModeHelpText: showPneumaticValveMode
            ? "片ソレノイドは前進で setOutputs、後退で resetOutputs を使います。"
            : ""
    };
}

export function applyFlowMotionPneumaticValveMode(actuator = {}, actuatorType = "", pneumaticValveMode = "") {
    if (!actuator || typeof actuator !== "object") {
        return buildFlowMotionPneumaticValveModeState(actuatorType, null);
    }
    actuator.pneumaticValveMode = normalizePneumaticValveMode(pneumaticValveMode, actuatorType);
    return buildFlowMotionPneumaticValveModeState(actuatorType, actuator);
}

export function buildFlowMotionSolenoidConfigState(
    actuatorType = "",
    actuator = null,
    action = null,
    selectedActionId = ""
) {
    const showSolenoidConfig = normalizeAxisActuatorType(actuatorType) === "pneumaticCylinder";
    const actionId = String(action?.id || selectedActionId || "").trim();
    const directionPair = resolveMotionSolenoidDirectionPair(actionId, action?.name || "");
    const pneumaticValveMode = showSolenoidConfig
        ? normalizePneumaticValveMode(actuator?.pneumaticValveMode, actuatorType)
        : "";
    const solenoidConfig = showSolenoidConfig
        ? normalizeActuatorSolenoidConfig(
            actuator?.solenoidConfig,
            actuatorType,
            actuator?.actions,
            pneumaticValveMode
        )
        : null;
    const positiveDirectionLabel = directionPair.positiveLabel;
    const negativeDirectionLabel = directionPair.negativeLabel;
    return {
        showSolenoidConfig,
        showSingleSolenoidConfig: showSolenoidConfig && pneumaticValveMode === "singleSolenoid",
        showDoubleSolenoidConfig: showSolenoidConfig && pneumaticValveMode === "doubleSolenoid",
        positiveDirectionLabel,
        negativeDirectionLabel,
        positiveOutput: String(solenoidConfig?.positiveOutput || ""),
        negativeOutput: String(solenoidConfig?.negativeOutput || ""),
        singleOutput: String(solenoidConfig?.singleOutput || ""),
        singleSolenoidActiveDirection: normalizeSolenoidActiveDirection(solenoidConfig?.singleSolenoidActiveDirection),
        singleSolenoidActiveDirectionOptions: `
            <option value="positive"${normalizeSolenoidActiveDirection(solenoidConfig?.singleSolenoidActiveDirection) === "positive" ? " selected" : ""}>通電で${positiveDirectionLabel}</option>
            <option value="negative"${normalizeSolenoidActiveDirection(solenoidConfig?.singleSolenoidActiveDirection) === "negative" ? " selected" : ""}>通電で${negativeDirectionLabel}</option>
        `,
        solenoidConfigHelpText: !showSolenoidConfig
            ? ""
            : pneumaticValveMode === "singleSolenoid"
                ? `通電で${positiveDirectionLabel}か${negativeDirectionLabel}かを選びます。OFF時は反対方向として表示します。`
                : `${positiveDirectionLabel}側と${negativeDirectionLabel}側の出力を、動作ノード表示とアクション出力へ反映します。`
    };
}

export function applyFlowMotionCompletionMode(action = {}, completionMode = "sensor") {
    if (!action || typeof action !== "object") return buildFlowMotionCompletionModeState(null);
    const normalized = normalizeControlSpecActionCompletionMode(completionMode, action);
    action.completionMode = normalized;
    if (normalized === "ignore") {
        action.doneConditions = [];
        action.timeoutSec = 0;
        return buildFlowMotionCompletionModeState(action);
    }
    if (normalized === "timer") {
        action.doneConditions = [];
        const timeoutSec = Number(action?.timeoutSec);
        action.timeoutSec = Number.isFinite(timeoutSec) && timeoutSec > 0 ? timeoutSec : 5;
        return buildFlowMotionCompletionModeState(action);
    }
    if (!Array.isArray(action.doneConditions)) action.doneConditions = [];
    const timeoutSec = Number(action?.timeoutSec);
    action.timeoutSec = Number.isFinite(timeoutSec) && timeoutSec >= 0 ? timeoutSec : 0;
    return buildFlowMotionCompletionModeState(action);
}

function syncFlowMotionCompletionModeUi(panel, action = null) {
    if (!panel) return buildFlowMotionCompletionModeState(action);
    const completionState = buildFlowMotionCompletionModeState(action);
    const select = panel.querySelector("#flowMotionCompletionMode");
    if (select) {
        select.innerHTML = completionState.completionModeOptions;
        select.value = completionState.completionMode;
        select.disabled = completionState.completionModeDisabled;
        select.style.background = completionState.completionModeDisabled ? "#f3f4f6" : "#fff";
    }
    const help = panel.querySelector("#flowMotionCompletionModeHelp");
    if (help) help.textContent = completionState.completionModeHelpText;
    return completionState;
}

function syncFlowMotionPneumaticValveModeUi(panel, actuatorType = "", actuator = null) {
    if (!panel) return buildFlowMotionPneumaticValveModeState(actuatorType, actuator);
    const valveState = buildFlowMotionPneumaticValveModeState(actuatorType, actuator);
    const field = panel.querySelector("#flowMotionPneumaticValveModeField");
    if (field?.style) field.style.display = valveState.showPneumaticValveMode ? "" : "none";
    const select = panel.querySelector("#flowMotionPneumaticValveMode");
    if (select) {
        select.innerHTML = valveState.pneumaticValveModeOptions;
        select.value = valveState.pneumaticValveMode;
    }
    const help = panel.querySelector("#flowMotionPneumaticValveModeHelp");
    if (help) help.textContent = valveState.pneumaticValveModeHelpText;
    return valveState;
}

function syncFlowMotionActionUi(panel, actuator = null, data = null, escapeHtml = (value) => String(value ?? "")) {
    const actionSelect = panel?.querySelector?.("#flowMotionActionId");
    const actions = Array.isArray(actuator?.actions) ? actuator.actions : [];
    const actionState = buildFlowActionSelectionState(
        actions,
        actionSelect?.value || data?.actionId || "",
        escapeHtml
    );
    if (actionSelect) {
        actionSelect.innerHTML = actionState.options;
        actionSelect.value = actionState.selectedActionId;
    }
    if (data) data.actionId = actionState.selectedActionId;
    return actions.find((action) => action?.id === actionState.selectedActionId) || null;
}

export function buildFlowMotionEditorMarkup(
    editorState = {},
    actuatorOptions = "",
    headerHtml = "",
    escapeHtml = (value) => String(value ?? "")
) {
    const motionNameMarkup = editorState.hasMotion ? `
                <label title="何をするかの名前です。例: 退避、搬入、受取">動作名
                    <input type="text" id="flowMotionName" title="例: 退避、搬入、受取、待機" value="${escapeHtml(editorState.motionName)}">
                </label>
        ` : `
                <div class="help editor-full">${escapeHtml(editorState.missingMotionHelpText)}</div>
        `;
    return `
            ${headerHtml}
            <div class="editor-grid">
                ${motionNameMarkup}
                <label title="機器の種類です。例: 空圧シリンダ、油圧シリンダ、モータ、チャック">アクチュエータ種類
                    <select id="flowMotionActuatorId" title="例: 空圧シリンダ、油圧シリンダ、モータ、チャック">
                        ${actuatorOptions}
                    </select>
                </label>
                <label title="実際の動きです。例: 前進、後退、上昇">アクション
                    <select id="flowMotionActionId" title="例: 前進、後退、上昇、下降、チャック">
                        ${editorState.actionOptions || ""}
                    </select>
                </label>
                <label id="flowMotionPneumaticValveModeField" title="空圧シリンダのソレノイド方式です。"${editorState.showPneumaticValveMode ? "" : ' style="display:none;"'}>
                    ソレノイド方式
                    <select id="flowMotionPneumaticValveMode" title="両ソレノイド / 片ソレノイド">
                        ${editorState.pneumaticValveModeOptions || ""}
                    </select>
                </label>
                <label title="動作完了の判定方法です。制御仕様のアクション定義に連動します。">完了方式
                    <select id="flowMotionCompletionMode" title="センサ監視 / タイマ完了 / 完了待ちなし"${editorState.completionModeDisabled ? " disabled" : ""}>
                        ${editorState.completionModeOptions || ""}
                    </select>
                </label>
            </div>
            <div id="flowMotionPneumaticValveModeHelp" class="help editor-full">${escapeHtml(editorState.pneumaticValveModeHelpText || "")}</div>
            <div id="flowMotionCompletionModeHelp" class="help editor-full">${escapeHtml(editorState.completionModeHelpText || "")}</div>
        `;
}

export function buildFlowMotionNameState(motionName = "") {
    return {
        motionName: String(motionName || "動作")
    };
}

export function buildFlowMotionNameAppliedState(motionName = "") {
    return buildFlowMotionNameState(motionName);
}

export function buildFlowMotionActionState(actionId = "") {
    return {
        actionId: String(actionId || "")
    };
}

export function buildFlowMotionActionAppliedState(actionId = "") {
    return buildFlowMotionActionState(actionId);
}

export function buildFlowMotionActuatorChangeState(
    actions = [],
    escapeHtml = (value) => String(value ?? "")
) {
    return buildFlowActionSelectionState(actions, "", escapeHtml);
}

export function buildFlowMotionActuatorAppliedState(
    actions = [],
    escapeHtml = (value) => String(value ?? "")
) {
    return buildFlowMotionActuatorChangeState(actions, escapeHtml);
}

export function buildFlowMotionSyncState(
    {
        motionName = "",
        actionId = "",
        actions = []
    } = {},
    escapeHtml = (value) => String(value ?? "")
) {
    return {
        motionName: buildFlowMotionNameState(motionName).motionName,
        actionId: buildFlowActionSelectionState(actions, actionId, escapeHtml).selectedActionId
    };
}

export function buildFlowMotionSavedState(
    input = {},
    escapeHtml = (value) => String(value ?? "")
) {
    return buildFlowMotionSyncState(input, escapeHtml);
}

export function buildFlowMotionAppliedDataState(
    input = {},
    escapeHtml = (value) => String(value ?? "")
) {
    return buildFlowMotionSavedState(input, escapeHtml);
}

export function bindFlowMotionEditorEvents(
    panel,
    {
        found,
        data,
        escapeHtml = (value) => String(value ?? ""),
        applyFlowMotionActuatorType,
        refreshFlowNodeHtml,
        renderControlSpecActuators,
        recomputeAll,
        renderErrors,
        renderTimingGrid,
        renderSpeedChart,
        scheduleAutoSave,
        updateAllFlowNodeLabels,
        isControlSpecViewActive = false
    } = {}
) {
    if (!panel || !data) return;
    const refreshNode = typeof refreshFlowNodeHtml === "function" ? refreshFlowNodeHtml : () => {};
    const renderActuators = typeof renderControlSpecActuators === "function" ? renderControlSpecActuators : () => {};
    const recompute = typeof recomputeAll === "function" ? recomputeAll : () => {};
    const renderErr = typeof renderErrors === "function" ? renderErrors : () => {};
    const renderGrid = typeof renderTimingGrid === "function" ? renderTimingGrid : () => {};
    const renderChart = typeof renderSpeedChart === "function" ? renderSpeedChart : () => {};
    const autoSave = typeof scheduleAutoSave === "function" ? scheduleAutoSave : () => {};
    const updateLabels = typeof updateAllFlowNodeLabels === "function" ? updateAllFlowNodeLabels : () => {};
    const resolveCurrentActuator = () => {
        if (!found) return null;
        return applyFlowMotionActuatorType(
            data,
            found,
            panel.querySelector("#flowMotionActuatorId")?.value || data?.actuatorId || ""
        );
    };
    const resolveCurrentAction = (actuator = resolveCurrentActuator()) =>
        Array.isArray(actuator?.actions)
            ? actuator.actions.find((action) => action?.id === (data?.actionId || "")) || null
            : null;
    const refreshMotionEditor = () => {
        refreshNode();
        if (isControlSpecViewActive) renderActuators();
        recompute();
        renderErr();
        renderGrid();
        renderChart();
        autoSave();
    };
    const initialActuator = resolveCurrentActuator();
    syncFlowMotionPneumaticValveModeUi(panel, initialActuator?.actuatorType || data?.actuatorType || "", initialActuator);
    const initialAction = syncFlowMotionActionUi(panel, initialActuator, data, escapeHtml);
    syncFlowMotionCompletionModeUi(panel, initialAction);

    panel.querySelector("#flowMotionName")?.addEventListener("input", (e) => {
        if (!found) return;
        found.motion.name = buildFlowMotionNameAppliedState(
            e.target.value
        ).motionName;
        refreshNode();
        updateLabels(found.axis.id);
        recompute();
        if (isControlSpecViewActive) renderActuators();
        renderErr();
        renderGrid();
        renderChart();
    });
    panel.querySelector("#flowMotionActuatorId")?.addEventListener("change", (e) => {
        const actuator = applyFlowMotionActuatorType(data, found, e.target.value || "");
        syncPneumaticActuatorActionCommands(actuator);
        syncFlowMotionPneumaticValveModeUi(panel, actuator?.actuatorType || e.target.value || "", actuator);
        const currentAction = syncFlowMotionActionUi(panel, actuator, data, escapeHtml);
        syncFlowMotionCompletionModeUi(panel, currentAction);
        refreshMotionEditor();
    });
    panel.querySelector("#flowMotionActionId")?.addEventListener("change", (e) => {
        data.actionId = buildFlowMotionActionAppliedState(
            e.target.value
        ).actionId;
        const currentActuator = resolveCurrentActuator();
        syncFlowMotionCompletionModeUi(panel, resolveCurrentAction(currentActuator));
        refreshMotionEditor();
    });
    panel.querySelector("#flowMotionPneumaticValveMode")?.addEventListener("change", (e) => {
        const actuator = resolveCurrentActuator();
        applyFlowMotionPneumaticValveMode(actuator, actuator?.actuatorType || "", e.target.value || "");
        syncPneumaticActuatorActionCommands(actuator);
        syncFlowMotionPneumaticValveModeUi(panel, actuator?.actuatorType || "", actuator);
        refreshMotionEditor();
    });
    panel.querySelector("#flowMotionCompletionMode")?.addEventListener("change", (e) => {
        const action = resolveCurrentAction();
        if (!action) {
            syncFlowMotionCompletionModeUi(panel, null);
            return;
        }
        applyFlowMotionCompletionMode(action, e.target.value || "");
        syncFlowMotionCompletionModeUi(panel, action);
        refreshMotionEditor();
    });
}

export function mountFlowMotionEditor(
    panel,
    {
        editorState,
        actuatorOptionsHtml = "",
        headerHtml = "",
        escapeHtml = (value) => String(value ?? ""),
        found,
        data,
        applyFlowMotionActuatorType,
        refreshFlowNodeHtml,
        renderControlSpecActuators,
        recomputeAll,
        renderErrors,
        renderTimingGrid,
        renderSpeedChart,
        scheduleAutoSave,
        updateAllFlowNodeLabels,
        isControlSpecViewActive = false
    } = {}
) {
    panel.innerHTML = buildFlowMotionEditorMarkup(
        editorState,
        actuatorOptionsHtml,
        headerHtml,
        escapeHtml
    );
    bindFlowMotionEditorEvents(panel, {
        found,
        data,
        escapeHtml,
        applyFlowMotionActuatorType,
        refreshFlowNodeHtml,
        renderControlSpecActuators,
        recomputeAll,
        renderErrors,
        renderTimingGrid,
        renderSpeedChart,
        scheduleAutoSave,
        updateAllFlowNodeLabels,
        isControlSpecViewActive
    });
}

export function buildFlowConditionEditorState(
    {
        data = {},
        conditionMode = "single",
        conditionKind = "expression",
        conditionRows = [],
        operator = "AND",
        internalFlags = []
    } = {}
) {
    const rows = Array.isArray(conditionRows) ? conditionRows : [];
    const firstRow = rows[0] || {};
    const mode = conditionMode === "composite" ? "composite" : "single";
    const kind = conditionKind === "signal" || conditionKind === "internalFlag" ? conditionKind : "expression";
    return {
        conditionText: String(data.conditionText || ""),
        conditionMode: mode,
        conditionKind: kind,
        operator: operator === "OR" ? "OR" : "AND",
        conditionRows: rows,
        internalFlags: Array.isArray(internalFlags) ? internalFlags : [],
        structuredConditionName: String(data.conditionName || data.conditionText || firstRow.label || ""),
        structuredConditionValue: data.conditionValue === false ? "FALSE" : "TRUE",
        structuredConditionActuatorId: String(data.conditionActuatorId || ""),
        structuredConditionSignalId: String(data.conditionSignalId || firstRow.expression || ""),
        conditionExpression: String(firstRow.expression || data.conditionText || ""),
        trueLabel: String(data.trueLabel || ""),
        falseLabel: String(data.falseLabel || ""),
        timeoutSec: data.timeoutSec ?? 5,
        timeoutLabel: String(data.timeoutLabel || ""),
        canRemoveConditionRows: rows.length > 1
    };
}

export function buildFlowConditionRenderState(editorState = {}) {
    const state = editorState || {};
    const kind = state.conditionKind === "signal" || state.conditionKind === "internalFlag"
        ? state.conditionKind
        : "expression";
    const mode = state.conditionMode === "composite" ? "composite" : "single";
    const rows = Array.isArray(state.conditionRows) ? state.conditionRows : [];
    return {
        conditionMode: mode,
        conditionKind: kind,
        showStructuredCondition: mode === "single",
        showCompositeEditor: mode === "composite",
        showExpressionEditor: mode === "single" && kind === "expression",
        kindOptions: [
            { value: "expression", label: "expression (自由入力)", selected: kind === "expression" },
            { value: "signal", label: "signal (センサー/外部入力)", selected: kind === "signal" },
            { value: "internalFlag", label: "internalFlag (内部条件)", selected: kind === "internalFlag" }
        ],
        valueOptions: [
            { value: "TRUE", selected: state.structuredConditionValue === "TRUE" },
            { value: "FALSE", selected: state.structuredConditionValue === "FALSE" }
        ],
        compositeRows: rows.map((row, index) => ({
            index,
            label: String(row?.label || ""),
            expression: String(row?.expression || ""),
            removeDisabled: !state.canRemoveConditionRows
        }))
    };
}

export function buildFlowConditionEditorRenderState(input = {}) {
    const editorState = buildFlowConditionEditorState(input);
    return {
        editorState,
        renderState: buildFlowConditionRenderState(editorState)
    };
}

export function buildFlowConditionEditorPanelState(input = {}) {
    const { editorState, renderState } = buildFlowConditionEditorRenderState(input);
    return {
        editorState,
        renderState,
        markupOptions: {
            internalFlags: editorState.internalFlags,
            selectedActuatorId: editorState.structuredConditionActuatorId
        }
    };
}

export function bindFlowConditionEditorEvents(
    panel,
    {
        data,
        getConditionMode,
        getConditionKind,
        ensureConditionDraftSignal,
        normalizeConditionEntries,
        normalizeConditionEntry,
        renderFlowNodeEditor,
        refreshFlowNodeHtml
    } = {}
) {
    if (!panel || !data) return;
    const refreshNode = typeof refreshFlowNodeHtml === "function" ? refreshFlowNodeHtml : () => {};
    const rerenderEditor = typeof renderFlowNodeEditor === "function" ? renderFlowNodeEditor : () => {};

    panel.querySelector("#flowConditionText").addEventListener("input", (e) => {
        const nextState = buildFlowConditionTextAppliedState(
            {
                conditionText: e.target.value,
                conditionMode: getConditionMode(data),
                conditionKind: getConditionKind(data),
                conditionName: data.conditionName,
                conditionSignalId: data.conditionSignalId,
                conditions: data.conditions
            },
            normalizeConditionEntries,
            normalizeConditionEntry
        );
        data.conditionText = nextState.conditionText;
        if (nextState.conditionName !== undefined) data.conditionName = nextState.conditionName;
        data.conditions = nextState.conditions;
        refreshNode();
    });
    panel.querySelector("#flowConditionMode").addEventListener("change", (e) => {
        const nextState = buildFlowConditionModeAppliedState(
            e.target.value,
            {
                conditions: data.conditions,
                conditionText: data.conditionText || "条件"
            },
            normalizeConditionEntries,
            normalizeConditionEntry
        );
        data.conditionMode = nextState.conditionMode;
        data.conditions = nextState.conditions;
        rerenderEditor();
        refreshNode();
    });
    panel.querySelector("#flowConditionKind")?.addEventListener("change", (e) => {
        const nextState = buildFlowConditionKindAppliedState(
            e.target.value,
            {
                conditionText: data.conditionText || "条件",
                firstExpression: data.conditions?.[0]?.expression || data.conditionText || "条件",
                conditionName: data.conditionName || data.conditionText || "条件",
                conditionValue: data.conditionValue,
                actuatorId: data.conditionActuatorId || ""
            },
            ensureConditionDraftSignal,
            normalizeConditionEntry
        );
        data.conditionKind = nextState.conditionKind;
        if (nextState.conditionName !== undefined) data.conditionName = nextState.conditionName;
        if (nextState.conditionValue !== undefined) data.conditionValue = nextState.conditionValue;
        if (nextState.conditionActuatorId !== undefined) data.conditionActuatorId = nextState.conditionActuatorId;
        if (nextState.conditionSignalId !== undefined) data.conditionSignalId = nextState.conditionSignalId;
        if (nextState.conditions !== undefined) data.conditions = nextState.conditions;
        rerenderEditor();
        refreshNode();
    });
    panel.querySelector("#flowConditionOperator")?.addEventListener("change", (e) => {
        data.operator = buildFlowConditionOperatorAppliedState(e.target.value).operator;
        refreshNode();
    });
    panel.querySelector("#flowConditionExpression")?.addEventListener("input", (e) => {
        const nextState = buildFlowConditionExpressionAppliedState(
            e.target.value,
            data.conditionText || "条件",
            normalizeConditionEntry
        );
        data.conditions = nextState.conditions;
        refreshNode();
    });
    const commitStructuredCondition = () => {
        const nameInput = panel.querySelector("#flowStructuredConditionName");
        const valueSelect = panel.querySelector("#flowStructuredConditionValue");
        const actuatorSelect = panel.querySelector("#flowStructuredConditionActuatorId");
        if (!nameInput) return;
        const nextState = buildFlowStructuredConditionCommittedDataState(
            {
                conditionName: nameInput.value,
                conditionValue: valueSelect?.value || "TRUE",
                actuatorId: actuatorSelect?.value || "",
                fallbackName: data.conditionText || "条件"
            },
            {
                conditionKind: getConditionKind(data),
                conditionText: data.conditionText || "条件",
                ensureConditionDraftSignal,
                normalizeConditionEntry
            }
        );
        Object.assign(data, nextState);
        const idInput = panel.querySelector("#flowStructuredConditionSignalId");
        if (idInput) idInput.value = data.conditionSignalId || "";
        refreshNode();
    };
    panel.querySelector("#flowStructuredConditionName")?.addEventListener("blur", commitStructuredCondition);
    panel.querySelector("#flowStructuredConditionName")?.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            commitStructuredCondition();
        }
    });
    panel.querySelector("#flowStructuredConditionValue")?.addEventListener("change", commitStructuredCondition);
    panel.querySelector("#flowStructuredConditionActuatorId")?.addEventListener("change", commitStructuredCondition);
    panel.querySelector("#flowConditionFlagSelect")?.addEventListener("change", (e) => {
        const nextState = buildFlowConditionFlagAppliedState(
            e.target.value,
            data.conditionText || e.target.value,
            normalizeConditionEntry
        );
        if (!nextState.ok) return;
        data.conditions = nextState.conditions;
        rerenderEditor();
        refreshNode();
    });
    panel.querySelectorAll("[data-condition-field]").forEach((input) => {
        input.addEventListener("input", (e) => {
            const nextState = buildFlowConditionRowInputAppliedState(
                {
                    index: Number(e.target.getAttribute("data-condition-index")),
                    field: e.target.getAttribute("data-condition-field"),
                    value: e.target.value,
                    conditions: data.conditions,
                    conditionText: data.conditionText || "条件"
                },
                normalizeConditionEntries,
                normalizeConditionEntry
            );
            if (!nextState.ok) return;
            data.conditions = nextState.conditions;
            refreshNode();
        });
    });
    panel.querySelectorAll("[data-condition-flag-select]").forEach((select) => {
        select.addEventListener("change", (e) => {
            const nextState = buildFlowConditionRowFlagSelectAppliedState(
                {
                    index: Number(e.target.getAttribute("data-condition-index")),
                    value: e.target.value,
                    conditions: data.conditions,
                    conditionText: data.conditionText || "条件"
                },
                normalizeConditionEntries,
                normalizeConditionEntry
            );
            if (!nextState.ok) return;
            data.conditions = nextState.conditions;
            rerenderEditor();
            refreshNode();
        });
    });
    panel.querySelectorAll("[data-condition-remove]").forEach((button) => {
        button.addEventListener("click", () => {
            data.conditions = buildFlowConditionRowRemoveAppliedState(
                {
                    index: Number(button.getAttribute("data-condition-remove")),
                    conditions: data.conditions,
                    conditionText: data.conditionText || "条件"
                },
                normalizeConditionEntries
            ).conditions;
            rerenderEditor();
            refreshNode();
        });
    });
    panel.querySelector("#flowConditionAddRow")?.addEventListener("click", () => {
        data.conditions = buildFlowConditionAddRowAppliedState(
            {
                conditions: data.conditions,
                conditionText: data.conditionText || "条件"
            },
            normalizeConditionEntries,
            normalizeConditionEntry
        ).conditions;
        rerenderEditor();
        refreshNode();
    });
    panel.querySelector("#flowConditionTrueLabel").addEventListener("input", (e) => {
        data.trueLabel = buildFlowConditionBranchLabelAppliedState(
            e.target.value,
            "ON"
        ).label;
        refreshNode();
    });
    panel.querySelector("#flowConditionFalseLabel").addEventListener("input", (e) => {
        data.falseLabel = buildFlowConditionBranchLabelAppliedState(
            e.target.value,
            "OFF"
        ).label;
        refreshNode();
    });
    panel.querySelector("#flowConditionTimeoutSec").addEventListener("input", (e) => {
        data.timeoutSec = buildFlowConditionTimeoutSecAppliedState(
            e.target.value
        ).timeoutSec;
        refreshNode();
    });
    panel.querySelector("#flowConditionTimeoutLabel").addEventListener("input", (e) => {
        data.timeoutLabel = buildFlowConditionTimeoutLabelAppliedState(
            e.target.value
        ).timeoutLabel;
        refreshNode();
    });
}

export function buildFlowConditionSectionMarkup(
    editorState = {},
    renderState = {},
    {
        internalFlags = [],
        actuators = [],
        selectedActuatorId = "",
        escapeHtml = (value) => String(value ?? "")
    } = {}
) {
    const internalFlagOptionsHtml = (Array.isArray(internalFlags) ? internalFlags : []).map((item) =>
        `<option value="${escapeHtml(item?.name || "")}">${escapeHtml(item?.name || "")}</option>`
    ).join("");
    const actuatorOptionsHtml = buildFlowActuatorOptionsState(
        actuators,
        selectedActuatorId,
        escapeHtml
    ).options;
    const structuredConditionHtml = renderState.showStructuredCondition ? `
                    <label>条件種別
                        <select id="flowConditionKind">
                            ${(Array.isArray(renderState.kindOptions) ? renderState.kindOptions : []).map((option) =>
                                `<option value="${option.value}"${option.selected ? " selected" : ""}>${escapeHtml(option.label)}</option>`
                            ).join("")}
                        </select>
                    </label>
                    ${renderState.showExpressionEditor ? `
                        <label class="editor-full">条件式
                            <input type="text" id="flowConditionExpression" value="${escapeHtml(editorState.conditionExpression)}">
                        </label>
                        <div class="condition-editor-helper editor-full">
                            <label>内部フラグを挿入
                                <select id="flowConditionFlagSelect">
                                    <option value="">選択してください</option>
                                    ${internalFlagOptionsHtml}
                                </select>
                            </label>
                        </div>
                    ` : `
                        <label>条件名
                            <input type="text" id="flowStructuredConditionName" value="${escapeHtml(editorState.structuredConditionName)}">
                        </label>
                        <label>成立状態
                            <select id="flowStructuredConditionValue">
                                ${(Array.isArray(renderState.valueOptions) ? renderState.valueOptions : []).map((option) =>
                                    `<option value="${option.value}"${option.selected ? " selected" : ""}>${option.value}</option>`
                                ).join("")}
                            </select>
                        </label>
                        <label>所属アクチュエータ
                            <select id="flowStructuredConditionActuatorId">
                                ${actuatorOptionsHtml}
                            </select>
                        </label>
                        <label>生成ID
                            <input type="text" id="flowStructuredConditionSignalId" value="${escapeHtml(editorState.structuredConditionSignalId)}" readonly>
                        </label>
                    `}
        ` : "";
    const compositeRowsHtml = (Array.isArray(renderState.compositeRows) ? renderState.compositeRows : []).map((row) => `
            <div class="condition-editor-row" data-condition-index="${row.index}">
                <label>表示名
                    <input type="text" data-condition-field="label" data-condition-index="${row.index}" value="${escapeHtml(row.label)}">
                </label>
                <label>条件式
                    <input type="text" data-condition-field="expression" data-condition-index="${row.index}" value="${escapeHtml(row.expression)}">
                </label>
                <button type="button" class="secondary" data-condition-remove="${row.index}"${row.removeDisabled ? " disabled" : ""}>削除</button>
            </div>
            <div class="condition-editor-helper">
                <label>内部フラグを挿入
                    <select data-condition-flag-select data-condition-index="${row.index}">
                        <option value="">選択してください</option>
                        ${internalFlagOptionsHtml}
                    </select>
                </label>
            </div>
        `).join("");
    return renderState.showCompositeEditor ? `
                    <label>結合
                        <select id="flowConditionOperator">
                            <option value="AND"${editorState.operator === "AND" ? " selected" : ""}>AND</option>
                            <option value="OR"${editorState.operator === "OR" ? " selected" : ""}>OR</option>
                        </select>
                    </label>
                    <div class="condition-editor-rows">
                        ${compositeRowsHtml}
                    </div>
                    <div class="condition-editor-actions">
                        <button type="button" class="secondary" id="flowConditionAddRow">＋ 条件行</button>
                    </div>
                ` : `
                    ${structuredConditionHtml}
                `;
}

export function buildFlowConditionEditorMarkup(
    editorState = {},
    renderState = {},
    {
        headerHtml = "",
        internalFlags = [],
        actuators = [],
        selectedActuatorId = "",
        escapeHtml = (value) => String(value ?? "")
    } = {}
) {
    const conditionSectionHtml = buildFlowConditionSectionMarkup(
        editorState,
        renderState,
        {
            internalFlags,
            actuators,
            selectedActuatorId,
            escapeHtml
        }
    );
    return `
            ${headerHtml}
            <div class="editor-grid">
                <label>条件名
                    <input type="text" id="flowConditionText" value="${escapeHtml(editorState.conditionText)}">
                </label>
                <label>モード
                    <select id="flowConditionMode">
                        <option value="single"${renderState.conditionMode === "single" ? " selected" : ""}>単一条件</option>
                        <option value="composite"${renderState.conditionMode === "composite" ? " selected" : ""}>複合条件</option>
                    </select>
                </label>
                ${conditionSectionHtml}
                <label>上側ラベル
                    <input type="text" id="flowConditionTrueLabel" value="${escapeHtml(editorState.trueLabel)}">
                </label>
                <label>下側ラベル
                    <input type="text" id="flowConditionFalseLabel" value="${escapeHtml(editorState.falseLabel)}">
                </label>
                <label>タイムアウト秒
                    <input type="number" id="flowConditionTimeoutSec" min="0" step="0.1" value="${escapeHtml(editorState.timeoutSec)}">
                </label>
                <label>タイムアウト表示
                    <input type="text" id="flowConditionTimeoutLabel" value="${escapeHtml(editorState.timeoutLabel)}">
                </label>
            </div>
        `;
}

export function buildFlowConditionModeChangeState(
    nextMode = "single",
    {
        conditions = [],
        conditionText = "条件"
    } = {},
    normalizeConditionEntries,
    normalizeConditionEntry
) {
    const mode = nextMode === "composite" ? "composite" : "single";
    const fallbackName = String(conditionText || "条件");
    if (mode === "composite") {
        const current = normalizeConditionEntries(conditions, fallbackName, "composite");
        if (current.length < 2) {
            current.push(normalizeConditionEntry({}, current.length, fallbackName));
        }
        return {
            conditionMode: mode,
            conditions: current
        };
    }
    const first = normalizeConditionEntries(conditions, fallbackName, "single")[0];
    return {
        conditionMode: mode,
        conditions: [
            normalizeConditionEntry(
                {
                    label: fallbackName || first.label,
                    expression: first.expression || fallbackName
                },
                0,
                fallbackName
            )
        ]
    };
}

export function buildFlowConditionModeAppliedState(
    nextMode = "single",
    input = {},
    normalizeConditionEntries,
    normalizeConditionEntry
) {
    return buildFlowConditionModeChangeState(
        nextMode,
        input,
        normalizeConditionEntries,
        normalizeConditionEntry
    );
}

export function buildFlowConditionTextState(
    {
        conditionText = "",
        conditionMode = "single",
        conditionKind = "expression",
        conditionName = "",
        conditionSignalId = "",
        conditions = []
    } = {},
    normalizeConditionEntries,
    normalizeConditionEntry
) {
    const nextText = String(conditionText || "").trim() || "条件";
    const mode = conditionMode === "composite" ? "composite" : "single";
    const kind = conditionKind === "signal" || conditionKind === "internalFlag" ? conditionKind : "expression";
    const nextState = {
        conditionText: nextText
    };
    if (mode === "single") {
        if (kind === "expression") {
            nextState.conditions = [
                normalizeConditionEntry(
                    {
                        label: nextText,
                        expression: conditions?.[0]?.expression || nextText
                    },
                    0,
                    nextText
                )
            ];
        } else {
            nextState.conditionName = String(conditionName || "").trim() || nextText;
            nextState.conditions = [
                normalizeConditionEntry(
                    {
                        label: nextState.conditionName,
                        expression: conditionSignalId || conditions?.[0]?.expression || nextState.conditionName
                    },
                    0,
                    nextState.conditionName
                )
            ];
        }
    } else {
        nextState.conditions = normalizeConditionEntries(conditions, nextText, "composite");
    }
    return nextState;
}

export function buildFlowConditionTextAppliedState(
    input = {},
    normalizeConditionEntries,
    normalizeConditionEntry
) {
    return buildFlowConditionTextState(input, normalizeConditionEntries, normalizeConditionEntry);
}

export function buildFlowConditionKindChangeState(
    nextKind = "expression",
    {
        conditionText = "条件",
        firstExpression = "",
        conditionName = "",
        conditionValue = true,
        actuatorId = ""
    } = {}
) {
    const kind = nextKind === "signal" || nextKind === "internalFlag" ? nextKind : "expression";
    return {
        conditionKind: kind,
        structuredOptions: kind === "expression"
            ? {
                expression: String(firstExpression || conditionText || "条件")
            }
            : {
                conditionName: String(conditionName || conditionText || "条件"),
                conditionValue: conditionValue === false ? false : true,
                actuatorId: String(actuatorId || "")
            }
    };
}

export function buildFlowConditionKindAppliedState(
    nextKind = "expression",
    input = {},
    ensureConditionDraftSignal,
    normalizeConditionEntry
) {
    const nextState = buildFlowConditionKindChangeState(nextKind, input);
    const applied = buildFlowStructuredConditionAppliedState(
        {
            kind: nextState.conditionKind,
            conditionText: input?.conditionText || "条件",
            firstExpression: input?.firstExpression || input?.conditionText || "条件",
            conditionName: nextState.structuredOptions?.conditionName,
            conditionValue: nextState.structuredOptions?.conditionValue,
            actuatorId: nextState.structuredOptions?.actuatorId
        },
        ensureConditionDraftSignal,
        normalizeConditionEntry
    );
    return {
        conditionKind: nextState.conditionKind,
        conditionName: applied.conditionName,
        conditionValue: applied.conditionValue,
        conditionActuatorId: applied.conditionActuatorId,
        conditionSignalId: applied.conditionSignalId,
        conditions: applied.conditions
    };
}

export function buildFlowConditionOperatorChangeState(operator = "AND") {
    return {
        operator: operator === "OR" ? "OR" : "AND"
    };
}

export function buildFlowConditionOperatorAppliedState(operator = "AND") {
    return buildFlowConditionOperatorChangeState(operator);
}

export function buildFlowConditionExpressionState(
    expression = "",
    conditionText = "条件"
) {
    const fallbackName = String(conditionText || "条件");
    return {
        label: fallbackName,
        expression: String(expression || "").trim() || fallbackName,
        fallbackName
    };
}

export function buildFlowConditionExpressionAppliedState(
    expression = "",
    conditionText = "条件",
    normalizeConditionEntry
) {
    const nextState = buildFlowConditionExpressionState(expression, conditionText);
    return {
        conditions: [
            normalizeConditionEntry(
                { label: nextState.label, expression: nextState.expression },
                0,
                nextState.fallbackName
            )
        ]
    };
}

export function buildFlowConditionFlagSelectState(
    flagValue = "",
    conditionText = "条件"
) {
    if (!flagValue) return { ok: false };
    const value = String(flagValue);
    return {
        ok: true,
        label: String(conditionText || value),
        expression: value,
        fallbackName: String(conditionText || value)
    };
}

export function buildFlowConditionFlagAppliedState(
    flagValue = "",
    conditionText = "条件",
    normalizeConditionEntry
) {
    const nextState = buildFlowConditionFlagSelectState(flagValue, conditionText);
    if (!nextState.ok) return { ok: false };
    return {
        ok: true,
        conditions: [
            normalizeConditionEntry(
                {
                    label: nextState.label,
                    expression: nextState.expression
                },
                0,
                nextState.fallbackName
            )
        ]
    };
}

export function buildFlowConditionBranchLabelState(label = "", fallback = "ON") {
    return {
        label: String(label || "").trim() || String(fallback || "ON")
    };
}

export function buildFlowConditionBranchLabelAppliedState(label = "", fallback = "ON") {
    return buildFlowConditionBranchLabelState(label, fallback);
}

export function buildFlowConditionTimeoutSecState(timeoutSec = 0) {
    const nextValue = Number.parseFloat(String(timeoutSec ?? ""));
    return {
        timeoutSec: Number.isFinite(nextValue) && nextValue >= 0 ? nextValue : 0
    };
}

export function buildFlowConditionTimeoutSecAppliedState(timeoutSec = 0) {
    return buildFlowConditionTimeoutSecState(timeoutSec);
}

export function buildFlowConditionTimeoutLabelState(label = "") {
    return {
        timeoutLabel: String(label || "").trim() || "TIMEOUT"
    };
}

export function buildFlowConditionTimeoutLabelAppliedState(label = "") {
    return buildFlowConditionTimeoutLabelState(label);
}

export function buildFlowConditionRowInputState(
    {
        index = 0,
        field = "label",
        value = "",
        conditions = [],
        conditionText = "条件"
    } = {},
    normalizeConditionEntries,
    normalizeConditionEntry
) {
    const current = normalizeConditionEntries(conditions, conditionText, "composite");
    if (!current[index]) return { ok: false };
    current[index][field] = String(value || "").trim()
        || (field === "label" ? `条件${index + 1}` : current[index].label || `条件${index + 1}`);
    current[index] = normalizeConditionEntry(current[index], index, conditionText);
    return {
        ok: true,
        conditions: current
    };
}

export function buildFlowConditionRowInputAppliedState(
    input = {},
    normalizeConditionEntries,
    normalizeConditionEntry
) {
    return buildFlowConditionRowInputState(input, normalizeConditionEntries, normalizeConditionEntry);
}

export function buildFlowConditionRowFlagSelectState(
    {
        index = 0,
        value = "",
        conditions = [],
        conditionText = "条件"
    } = {},
    normalizeConditionEntries,
    normalizeConditionEntry
) {
    if (!value) return { ok: false };
    const current = normalizeConditionEntries(conditions, conditionText, "composite");
    if (!current[index]) return { ok: false };
    current[index].expression = String(value);
    if (!current[index].label || current[index].label.startsWith("条件")) {
        current[index].label = String(value);
    }
    current[index] = normalizeConditionEntry(current[index], index, conditionText);
    return {
        ok: true,
        conditions: current
    };
}

export function buildFlowConditionRowFlagSelectAppliedState(
    input = {},
    normalizeConditionEntries,
    normalizeConditionEntry
) {
    return buildFlowConditionRowFlagSelectState(input, normalizeConditionEntries, normalizeConditionEntry);
}

export function buildFlowConditionRowRemoveState(
    {
        index = 0,
        conditions = [],
        conditionText = "条件"
    } = {},
    normalizeConditionEntries
) {
    const current = normalizeConditionEntries(conditions, conditionText, "composite");
    current.splice(index, 1);
    return {
        conditions: normalizeConditionEntries(current, conditionText, "composite")
    };
}

export function buildFlowConditionRowRemoveAppliedState(
    input = {},
    normalizeConditionEntries
) {
    return buildFlowConditionRowRemoveState(input, normalizeConditionEntries);
}

export function buildFlowConditionAddRowState(
    {
        conditions = [],
        conditionText = "条件"
    } = {},
    normalizeConditionEntries,
    normalizeConditionEntry
) {
    const current = normalizeConditionEntries(conditions, conditionText, "composite");
    current.push(normalizeConditionEntry({}, current.length, conditionText));
    return {
        conditions: current
    };
}

export function buildFlowConditionAddRowAppliedState(
    input = {},
    normalizeConditionEntries,
    normalizeConditionEntry
) {
    return buildFlowConditionAddRowState(input, normalizeConditionEntries, normalizeConditionEntry);
}

export function buildFlowConditionSyncState(
    {
        conditionText = "",
        conditionMode = "single",
        operator = "AND",
        singleExpression = null,
        structuredName = null,
        structuredKind = "signal",
        structuredValue = "TRUE",
        structuredActuatorId = "",
        compositeRows = [],
        trueLabel = "",
        falseLabel = "",
        timeoutLabel = "",
        timeoutSec = null
    } = {},
    normalizeConditionEntry,
    normalizeConditionEntries
) {
    const nextConditionText = String(conditionText || "").trim() || "条件";
    const nextConditionMode = conditionMode === "composite" ? "composite" : "single";
    const nextOperator = operator === "OR" ? "OR" : "AND";
    const nextState = {
        conditionText: nextConditionText,
        conditionMode: nextConditionMode,
        operator: nextOperator,
        trueLabel: String(trueLabel || "ON"),
        falseLabel: String(falseLabel || "OFF"),
        timeoutLabel: String(timeoutLabel || "TIMEOUT"),
        timeoutSec: 0
    };
    const parsedTimeout = Number.parseFloat(String(timeoutSec ?? ""));
    nextState.timeoutSec = Number.isFinite(parsedTimeout) && parsedTimeout >= 0 ? parsedTimeout : 0;

    if (nextConditionMode === "single" && singleExpression !== null) {
        nextState.conditionKind = "expression";
        nextState.conditions = [
            normalizeConditionEntry(
                {
                    label: nextConditionText,
                    expression: String(singleExpression || "").trim() || nextConditionText
                },
                0,
                nextConditionText
            )
        ];
    }

    if (nextConditionMode === "single" && structuredName !== null) {
        nextState.conditionKind = structuredKind === "internalFlag" ? "internalFlag" : "signal";
        nextState.structuredOptions = {
            conditionName: String(structuredName || "").trim() || nextConditionText,
            conditionValue: structuredValue !== "FALSE",
            actuatorId: String(structuredActuatorId || "")
        };
    }

    if (nextConditionMode === "composite") {
        const rows = (Array.isArray(compositeRows) ? compositeRows : []).map((row, index) =>
            normalizeConditionEntry(
                {
                    label: row?.label,
                    expression: row?.expression
                },
                index,
                nextConditionText
            )
        );
        nextState.conditions = normalizeConditionEntries(rows, nextConditionText, "composite");
    }

    return nextState;
}

export function buildFlowStructuredConditionApplyState(
    {
        kind = "expression",
        conditionText = "",
        firstExpression = "",
        conditionName = "",
        conditionValue = true,
        actuatorId = ""
    } = {},
    ensureConditionDraftSignal
) {
    const nextKind = kind === "signal" || kind === "internalFlag" ? kind : "expression";
    if (nextKind === "expression") {
        const expression = String(firstExpression || conditionText || "条件").trim() || "条件";
        return {
            conditionKind: "expression",
            conditionName: "",
            conditionValue: true,
            conditionActuatorId: "",
            conditionSignalId: "",
            conditionEntry: {
                label: String(conditionText || expression),
                expression
            },
            fallbackName: String(conditionText || expression)
        };
    }

    const nextConditionName = String(conditionName || conditionText || "").trim();
    if (!nextConditionName) {
        return {
            conditionKind: nextKind,
            skipped: true
        };
    }
    const nextConditionValue = conditionValue === false ? false : true;
    const nextActuatorId = String(actuatorId || "").trim();
    const signalId = ensureConditionDraftSignal(
        nextKind,
        nextConditionName,
        nextConditionValue,
        nextActuatorId
    );
    return {
        conditionKind: nextKind,
        conditionName: nextConditionName,
        conditionValue: nextConditionValue,
        conditionActuatorId: nextActuatorId,
        conditionSignalId: signalId,
        conditionEntry: {
            label: nextConditionName,
            expression: signalId
        },
        fallbackName: nextConditionName
    };
}

export function buildFlowStructuredConditionAppliedState(
    input = {},
    ensureConditionDraftSignal,
    normalizeConditionEntry
) {
    const nextState = buildFlowStructuredConditionApplyState(
        input,
        ensureConditionDraftSignal
    );
    if (nextState.skipped) {
        return {
            conditionKind: nextState.conditionKind,
            skipped: true
        };
    }
    return {
        conditionKind: nextState.conditionKind,
        conditionName: nextState.conditionName,
        conditionValue: nextState.conditionValue,
        conditionActuatorId: nextState.conditionActuatorId,
        conditionSignalId: nextState.conditionSignalId,
        conditions: [
            normalizeConditionEntry(
                nextState.conditionEntry,
                0,
                nextState.fallbackName
            )
        ]
    };
}

export function buildFlowStructuredConditionAppliedDataState(
    input = {},
    {
        ensureConditionDraftSignal,
        normalizeConditionEntry
    } = {}
) {
    return buildFlowStructuredConditionAppliedState(
        input,
        ensureConditionDraftSignal,
        normalizeConditionEntry
    );
}

export function buildFlowStructuredConditionCommitState(
    {
        conditionName = "",
        conditionValue = "TRUE",
        actuatorId = "",
        fallbackName = ""
    } = {}
) {
    return {
        conditionName: String(conditionName || "").trim() || String(fallbackName || "").trim() || "譚｡莉ｶ",
        conditionValue: conditionValue !== "FALSE",
        actuatorId: String(actuatorId || "")
    };
}

export function buildFlowStructuredConditionCommittedDataState(
    input = {},
    {
        conditionKind = "signal",
        conditionText = "条件",
        ensureConditionDraftSignal,
        normalizeConditionEntry
    } = {}
) {
    const committed = buildFlowStructuredConditionCommitState(input);
    return buildFlowStructuredConditionAppliedState(
        {
            kind: conditionKind,
            conditionText,
            firstExpression: conditionText,
            conditionName: committed.conditionName,
            conditionValue: committed.conditionValue,
            actuatorId: committed.actuatorId
        },
        ensureConditionDraftSignal,
        normalizeConditionEntry
    );
}

export function buildFlowConditionSavedState(
    input = {},
    {
        normalizeConditionEntry,
        normalizeConditionEntries,
        ensureConditionDraftSignal,
        ensureConditionNodeData
    }
) {
    const synced = buildFlowConditionSyncState(
        input,
        normalizeConditionEntry,
        normalizeConditionEntries
    );
    const nextData = {
        conditionText: synced.conditionText,
        conditionMode: synced.conditionMode,
        operator: synced.operator,
        trueLabel: synced.trueLabel,
        falseLabel: synced.falseLabel,
        timeoutLabel: synced.timeoutLabel,
        timeoutSec: synced.timeoutSec
    };
    if (synced.conditionKind) nextData.conditionKind = synced.conditionKind;
    if (synced.conditions) nextData.conditions = synced.conditions;
    if (synced.structuredOptions) {
        const applied = buildFlowStructuredConditionAppliedState(
            {
                kind: synced.conditionKind,
                conditionText: synced.conditionText,
                firstExpression: synced.conditions?.[0]?.expression ?? synced.conditionText,
                conditionName: synced.structuredOptions.conditionName,
                conditionValue: synced.structuredOptions.conditionValue,
                actuatorId: synced.structuredOptions.actuatorId
            },
            ensureConditionDraftSignal,
            normalizeConditionEntry
        );
        nextData.conditionKind = applied.conditionKind;
        if (!applied.skipped) {
            nextData.conditionName = applied.conditionName;
            nextData.conditionValue = applied.conditionValue;
            nextData.conditionActuatorId = applied.conditionActuatorId;
            nextData.conditionSignalId = applied.conditionSignalId;
            nextData.conditions = applied.conditions;
        }
    }
    return ensureConditionNodeData(nextData);
}

export function buildFlowSignalOutputSyncState(
    {
        label = "",
        signalScope = "external",
        signalName = "",
        action = ""
    } = {}
) {
    const scope = signalScope === "internal_flag" ? "internal_flag" : "external";
    const nextSignalName = String(signalName || "").trim() || "Signal";
    return {
        label: String(label || "信号操作"),
        signalScope: scope,
        signalName: nextSignalName,
        action: String(action || (scope === "internal_flag" ? "SET" : "ON")),
        shouldCommitInternalFlag: scope === "internal_flag"
    };
}

export function buildFlowSignalOutputEditorState(
    {
        data = {},
        signalScope = "external",
        internalFlags = []
    } = {}
) {
    const scope = signalScope === "internal_flag" ? "internal_flag" : "external";
    const action = String(data.action || (scope === "internal_flag" ? "SET" : "ON"));
    const flags = Array.isArray(internalFlags) ? internalFlags : [];
    return {
        label: String(data.label || ""),
        signalScope: scope,
        signalName: String(data.signalName || ""),
        showInternalFlagSelect: scope === "internal_flag",
        actionOptions: scope === "internal_flag"
            ? [
                { value: "SET", selected: action === "SET" },
                { value: "RESET", selected: action === "RESET" }
            ]
            : [
                { value: "ON", selected: action === "ON" },
                { value: "OFF", selected: action === "OFF" },
                { value: "BLINK", selected: action === "BLINK" }
            ],
        internalFlagOptions: flags.map((item) => ({
            value: String(item?.name || ""),
            selected: String(item?.name || "") === String(data.signalName || "")
        }))
    };
}

export function buildFlowSignalOutputEditorMarkup(
    editorState = {},
    escapeHtml = (value) => String(value ?? ""),
    headerHtml = ""
) {
    const actionOptions = (Array.isArray(editorState.actionOptions) ? editorState.actionOptions : []).map((option) =>
        `<option value="${option.value}"${option.selected ? " selected" : ""}>${option.value}</option>`
    ).join("");
    const internalFlagOptions = (Array.isArray(editorState.internalFlagOptions) ? editorState.internalFlagOptions : []).map((option) =>
        `<option value="${escapeHtml(option.value)}"${option.selected ? " selected" : ""}>${escapeHtml(option.value)}</option>`
    ).join("");
    return `
            ${headerHtml}
            <div class="editor-grid">
                <label>表示名
                    <input type="text" id="flowSignalLabel" value="${escapeHtml(editorState.label)}">
                </label>
                <label>種別
                    <select id="flowSignalScope">
                        <option value="external"${editorState.signalScope === "external" ? " selected" : ""}>外部信号</option>
                        <option value="internal_flag"${editorState.signalScope === "internal_flag" ? " selected" : ""}>内部フラグ</option>
                    </select>
                </label>
                <label>信号名
                    <input type="text" id="flowSignalName" value="${escapeHtml(editorState.signalName)}">
                </label>
                ${editorState.showInternalFlagSelect ? `
                    <label>既存フラグ
                        <select id="flowSignalInternalFlagSelect">
                            <option value="">選択してください</option>
                            ${internalFlagOptions}
                        </select>
                    </label>
                ` : ""}
                <label>操作
                    <select id="flowSignalAction">
                        ${actionOptions}
                    </select>
                </label>
            </div>
        `;
}

export function buildFlowSignalOutputScopeChangeState(
    nextScope = "external",
    internalFlags = []
) {
    const scope = nextScope === "internal_flag" ? "internal_flag" : "external";
    const firstInternalFlag = (Array.isArray(internalFlags) ? internalFlags : [])[0];
    const signalName = scope === "internal_flag" && firstInternalFlag?.name
        ? String(firstInternalFlag.name)
        : "";
    return {
        signalScope: scope,
        action: scope === "internal_flag" ? "SET" : "ON",
        signalName,
        shouldCommitInternalFlag: scope === "internal_flag"
    };
}

export function buildFlowSignalOutputScopeAppliedState(
    nextScope = "external",
    internalFlags = []
) {
    const nextState = buildFlowSignalOutputScopeChangeState(nextScope, internalFlags);
    return {
        signalScope: nextState.signalScope,
        action: nextState.action,
        signalName: nextState.signalName,
        shouldCommitInternalFlag: nextState.shouldCommitInternalFlag
    };
}

export function buildFlowSignalOutputNameChangeState(
    signalName = "",
    signalScope = "external",
    { shouldCommitOnChange = false } = {}
) {
    const nextSignalName = String(signalName || "").trim() || "Signal";
    const scope = signalScope === "internal_flag" ? "internal_flag" : "external";
    return {
        signalName: nextSignalName,
        shouldCommitInternalFlag: scope === "internal_flag" && shouldCommitOnChange
    };
}

export function buildFlowSignalOutputNameAppliedState(
    signalName = "",
    signalScope = "external",
    options = {}
) {
    return buildFlowSignalOutputNameChangeState(signalName, signalScope, options);
}

export function buildFlowSignalOutputLabelState(label = "") {
    return {
        label: String(label || "信号操作")
    };
}

export function buildFlowSignalOutputLabelAppliedState(label = "") {
    return buildFlowSignalOutputLabelState(label);
}

export function buildFlowSignalOutputInternalFlagSelectState(signalName = "") {
    const nextSignalName = String(signalName || "").trim();
    return {
        signalName: nextSignalName,
        shouldCommitInternalFlag: Boolean(nextSignalName)
    };
}

export function buildFlowSignalOutputInternalFlagAppliedState(signalName = "") {
    return buildFlowSignalOutputInternalFlagSelectState(signalName);
}

export function buildFlowSignalOutputActionChangeState(action = "ON") {
    const nextAction = String(action || "ON");
    return {
        action: nextAction
    };
}

export function buildFlowSignalOutputActionAppliedState(action = "ON") {
    return buildFlowSignalOutputActionChangeState(action);
}

export function buildFlowSignalOutputSavedState(
    input = {},
    ensureSignalOutputData = (data) => data
) {
    const nextState = buildFlowSignalOutputSyncState(input);
    const normalized = ensureSignalOutputData({
        label: nextState.label,
        signalScope: nextState.signalScope,
        signalName: nextState.signalName,
        action: nextState.action
    });
    return {
        label: normalized.label,
        signalScope: normalized.signalScope,
        signalName: normalized.signalName,
        action: normalized.action,
        shouldCommitInternalFlag: nextState.shouldCommitInternalFlag
    };
}

export function buildFlowSignalOutputAppliedDataState(
    input = {},
    ensureSignalOutputData = (data) => data
) {
    return buildFlowSignalOutputSavedState(input, ensureSignalOutputData);
}

export function bindFlowSignalOutputEditorEvents(
    panel,
    {
        data,
        getInternalFlagList,
        normalizeSignalScope,
        commitInternalFlagName,
        renderFlowNodeEditor,
        refreshFlowNodeHtml
    } = {}
) {
    if (!panel || !data) return;
    const readInternalFlags = typeof getInternalFlagList === "function" ? getInternalFlagList : () => [];
    const normalizeScope = typeof normalizeSignalScope === "function"
        ? normalizeSignalScope
        : (value) => (value === "internal_flag" ? "internal_flag" : "external");
    const commitFlag = typeof commitInternalFlagName === "function" ? commitInternalFlagName : () => {};
    const rerenderEditor = typeof renderFlowNodeEditor === "function" ? renderFlowNodeEditor : () => {};
    const refreshNode = typeof refreshFlowNodeHtml === "function" ? refreshFlowNodeHtml : () => {};

    panel.querySelector("#flowSignalLabel").addEventListener("input", (e) => {
        data.label = buildFlowSignalOutputLabelAppliedState(
            e.target.value
        ).label;
        refreshNode();
    });
    panel.querySelector("#flowSignalScope").addEventListener("change", (e) => {
        const nextState = buildFlowSignalOutputScopeAppliedState(
            normalizeScope(e.target.value),
            readInternalFlags()
        );
        data.signalScope = nextState.signalScope;
        data.action = nextState.action;
        if (nextState.signalName) data.signalName = nextState.signalName;
        if (nextState.shouldCommitInternalFlag) commitFlag(data.signalName);
        rerenderEditor();
        refreshNode();
    });
    panel.querySelector("#flowSignalName").addEventListener("input", (e) => {
        data.signalName = buildFlowSignalOutputNameChangeState(
            e.target.value,
            normalizeScope(data.signalScope)
        ).signalName;
        refreshNode();
    });
    panel.querySelector("#flowSignalName").addEventListener("change", (e) => {
        const nextState = buildFlowSignalOutputNameAppliedState(
            e.target.value,
            normalizeScope(data.signalScope),
            { shouldCommitOnChange: true }
        );
        data.signalName = nextState.signalName;
        if (nextState.shouldCommitInternalFlag) commitFlag(data.signalName);
        rerenderEditor();
        refreshNode();
    });
    panel.querySelector("#flowSignalInternalFlagSelect")?.addEventListener("change", (e) => {
        const nextState = buildFlowSignalOutputInternalFlagAppliedState(
            e.target.value
        );
        if (!nextState.signalName) return;
        data.signalName = nextState.signalName;
        if (nextState.shouldCommitInternalFlag) commitFlag(data.signalName);
        rerenderEditor();
        refreshNode();
    });
    panel.querySelector("#flowSignalAction").addEventListener("change", (e) => {
        data.action = buildFlowSignalOutputActionAppliedState(
            e.target.value || "ON"
        ).action;
        refreshNode();
    });
}

export function mountFlowSignalOutputEditor(
    panel,
    {
        data,
        getInternalFlagList,
        normalizeSignalScope = (value) => value,
        commitInternalFlagName,
        renderFlowNodeEditor,
        refreshFlowNodeHtml,
        escapeHtml = (value) => String(value ?? ""),
        headerHtml = ""
    } = {}
) {
    const signalScope = normalizeSignalScope(data?.signalScope);
    const editorState = buildFlowSignalOutputEditorState({
        data,
        signalScope,
        internalFlags: typeof getInternalFlagList === "function" ? getInternalFlagList() : []
    });
    panel.innerHTML = buildFlowSignalOutputEditorMarkup(editorState, escapeHtml, headerHtml);
    bindFlowSignalOutputEditorEvents(panel, {
        data,
        getInternalFlagList,
        normalizeSignalScope,
        commitInternalFlagName,
        renderFlowNodeEditor,
        refreshFlowNodeHtml
    });
}

export function buildFlowWaitEditorState(data = {}) {
    return {
        label: String(data.label || ""),
        durationSec: String(Number.isFinite(Number(data.durationSec)) ? Number(data.durationSec) : 1)
    };
}

export function buildFlowWaitEditorMarkup(
    editorState = {},
    escapeHtml = (value) => String(value ?? ""),
    headerHtml = ""
) {
    return `
            ${headerHtml}
            <div class="editor-grid">
                <label>表示名
                    <input type="text" id="flowWaitLabel" value="${escapeHtml(editorState.label)}">
                </label>
                <label>待機秒数
                    <input type="number" id="flowWaitDurationSec" min="0" step="0.1" value="${escapeHtml(editorState.durationSec)}">
                </label>
            </div>
        `;
}

export function buildFlowWaitLabelState(label = "") {
    return {
        label: String(label || "待機").trim() || "待機"
    };
}

export function buildFlowWaitDurationState(durationSec = 1) {
    const value = Number(durationSec);
    return {
        durationSec: Number.isFinite(value) && value >= 0 ? value : 0
    };
}

export function buildFlowWaitSyncState(
    {
        label = "",
        durationSec = 1
    } = {}
) {
    return {
        label: buildFlowWaitLabelState(label).label,
        durationSec: buildFlowWaitDurationState(durationSec).durationSec
    };
}

export function bindFlowWaitEditorEvents(
    panel,
    {
        data,
        refreshFlowNodeHtml
    } = {}
) {
    panel.querySelector("#flowWaitLabel")?.addEventListener("input", (e) => {
        data.label = buildFlowWaitLabelState(e.target.value).label;
        refreshFlowNodeHtml?.();
    });
    panel.querySelector("#flowWaitDurationSec")?.addEventListener("input", (e) => {
        data.durationSec = buildFlowWaitDurationState(e.target.value).durationSec;
        refreshFlowNodeHtml?.();
    });
}

export function mountFlowWaitEditor(
    panel,
    {
        data,
        escapeHtml = (value) => String(value ?? ""),
        headerHtml = "",
        refreshFlowNodeHtml
    } = {}
) {
    const editorState = buildFlowWaitEditorState(data);
    panel.innerHTML = buildFlowWaitEditorMarkup(editorState, escapeHtml, headerHtml);
    bindFlowWaitEditorEvents(panel, {
        data,
        refreshFlowNodeHtml
    });
}

export function buildFlowSubprocessEditorState(
    {
        subprocess = null,
        subprocesses = [],
        durationSec = 1
    } = {}
) {
    const current = subprocess || {};
    const list = Array.isArray(subprocesses) ? subprocesses : [];
    const nextDuration = Number.isFinite(Number(current.durationSec)) ? Number(current.durationSec) : Number(durationSec || 1);
    return {
        subprocessId: String(current.id || ""),
        subprocessName: String(current.name || ""),
        durationSec: String(nextDuration),
        moduleName: String(current.moduleName || ""),
        subprocessOptions: list.map((item) => ({
            value: String(item?.id || ""),
            label: String(item?.name || "サブプロセス"),
            selected: String(item?.id || "") === String(current.id || "")
        }))
    };
}

export function buildFlowSubprocessEditorMarkup(
    editorState = {},
    escapeHtml = (value) => String(value ?? ""),
    headerHtml = ""
) {
    const subprocessOptions = (Array.isArray(editorState.subprocessOptions) ? editorState.subprocessOptions : []).map((option) =>
        `<option value="${escapeHtml(option.value)}"${option.selected ? " selected" : ""}>${escapeHtml(option.label)}</option>`
    ).join("");
    return `
            ${headerHtml}
            <div class="editor-grid">
                <label>割り当て
                    <select id="flowSubprocessSelect">${subprocessOptions}</select>
                </label>
                <label>表示名
                    <input type="text" id="flowSubprocessName" value="${escapeHtml(editorState.subprocessName)}">
                </label>
                <label>所要秒数
                    <input type="number" id="flowSubprocessDurationSec" min="0" step="0.1" value="${escapeHtml(editorState.durationSec)}">
                </label>
                <button type="button" id="flowOpenSubprocessButton">内部フローを開く</button>
            </div>
        `;
}

export function buildFlowSubprocessSelectionState(subprocess = null) {
    const current = subprocess || {};
    return {
        subprocessId: String(current.id || ""),
        label: String(current.name || ""),
        subprocessName: String(current.name || ""),
        durationSec: current.durationSec
    };
}

export function buildFlowSubprocessNameState(
    subprocessName = "",
    { isDuplicate = false } = {}
) {
    const nextName = String(subprocessName || "").trim() || "サブプロセス";
    return {
        ok: !isDuplicate,
        subprocessName: nextName,
        label: nextName,
        customValidity: isDuplicate ? "同名のサブプロセスは作成できません。" : "",
        shouldReportValidity: Boolean(isDuplicate)
    };
}

export function buildFlowSubprocessDurationState(durationSec = 1) {
    const parsed = Number(durationSec || 1);
    return {
        durationSec: Number.isFinite(parsed) && parsed >= 0 ? parsed : 0
    };
}

export function buildFlowSubprocessSyncState(
    {
        subprocessName = "",
        durationSec = 1,
        isDuplicate = false
    } = {}
) {
    const nameState = buildFlowSubprocessNameState(subprocessName, { isDuplicate });
    const durationState = buildFlowSubprocessDurationState(durationSec);
    return {
        ok: nameState.ok,
        label: nameState.label,
        subprocessName: nameState.subprocessName,
        durationSec: durationState.durationSec,
        customValidity: nameState.customValidity,
        shouldReportValidity: nameState.shouldReportValidity
    };
}

export function bindFlowSubprocessEditorEvents(
    panel,
    {
        data,
        subprocess,
        findSubprocessById,
        isSubprocessNameDuplicate,
        refreshSubprocessReferenceNodes,
        renderFlowTabs,
        renderFlowNodeEditor,
        refreshFlowNodeHtml,
        ensureDrawflowModule,
        setActiveFlowModule
    } = {}
) {
    panel.querySelector("#flowSubprocessSelect")?.addEventListener("change", (e) => {
        const nextSubprocess = typeof findSubprocessById === "function"
            ? findSubprocessById(e.target.value)
            : null;
        if (!nextSubprocess) return;
        const nextState = buildFlowSubprocessSelectionState(nextSubprocess);
        data.subprocessId = nextState.subprocessId;
        data.label = nextState.label;
        data.subprocessName = nextState.subprocessName;
        data.durationSec = nextState.durationSec;
        renderFlowNodeEditor?.();
        refreshFlowNodeHtml?.();
    });
    panel.querySelector("#flowSubprocessName")?.addEventListener("input", (e) => {
        const rawValue = e.target.value || "";
        const trimmedValue = String(rawValue).trim() || "繧ｵ繝悶・繝ｭ繧ｻ繧ｹ";
        const nextState = buildFlowSubprocessNameState(rawValue, {
            isDuplicate: typeof isSubprocessNameDuplicate === "function"
                ? isSubprocessNameDuplicate(trimmedValue, subprocess?.id)
                : false
        });
        if (!nextState.ok) {
            e.target.setCustomValidity(nextState.customValidity);
            if (nextState.shouldReportValidity) e.target.reportValidity();
            return;
        }
        e.target.setCustomValidity(nextState.customValidity);
        subprocess.name = nextState.subprocessName;
        data.label = nextState.label;
        data.subprocessName = nextState.subprocessName;
        refreshSubprocessReferenceNodes?.(subprocess);
        renderFlowTabs?.();
    });
    panel.querySelector("#flowSubprocessDurationSec")?.addEventListener("input", (e) => {
        const nextState = buildFlowSubprocessDurationState(e.target.value || 1);
        subprocess.durationSec = nextState.durationSec;
        data.durationSec = nextState.durationSec;
        refreshSubprocessReferenceNodes?.(subprocess);
    });
    panel.querySelector("#flowOpenSubprocessButton")?.addEventListener("click", () => {
        ensureDrawflowModule?.(subprocess.moduleName);
        setActiveFlowModule?.(subprocess.moduleName);
    });
}

export function mountFlowSubprocessEditor(
    panel,
    {
        data,
        subprocess,
        subprocesses = [],
        escapeHtml = (value) => String(value ?? ""),
        headerHtml = "",
        findSubprocessById,
        isSubprocessNameDuplicate,
        refreshSubprocessReferenceNodes,
        renderFlowTabs,
        renderFlowNodeEditor,
        refreshFlowNodeHtml,
        ensureDrawflowModule,
        setActiveFlowModule
    } = {}
) {
    const editorState = buildFlowSubprocessEditorState({
        subprocess,
        subprocesses,
        durationSec: data?.durationSec
    });
    panel.innerHTML = buildFlowSubprocessEditorMarkup(editorState, escapeHtml, headerHtml);
    bindFlowSubprocessEditorEvents(panel, {
        data,
        subprocess,
        findSubprocessById,
        isSubprocessNameDuplicate,
        refreshSubprocessReferenceNodes,
        renderFlowTabs,
        renderFlowNodeEditor,
        refreshFlowNodeHtml,
        ensureDrawflowModule,
        setActiveFlowModule
    });
}

export function buildFlowErrorEditorState(data = {}) {
    return {
        errorCode: String(data.errorCode || ""),
        errorMessage: String(data.errorMessage || "")
    };
}

export function buildFlowErrorEditorMarkup(
    editorState = {},
    escapeHtml = (value) => String(value ?? ""),
    headerHtml = ""
) {
    return `
            ${headerHtml}
            <div class="editor-grid">
                <label>エラーコード
                    <input type="text" id="flowErrorCode" value="${escapeHtml(editorState.errorCode)}">
                </label>
                <label>エラーメッセージ
                    <input type="text" id="flowErrorMessage" value="${escapeHtml(editorState.errorMessage)}">
                </label>
            </div>
        `;
}

export function buildFlowErrorCodeState(errorCode = "") {
    return {
        errorCode: String(errorCode || "E001")
    };
}

export function buildFlowErrorMessageState(errorMessage = "") {
    return {
        errorMessage: String(errorMessage || "エラー")
    };
}

export function buildFlowErrorSyncState(
    {
        errorCode = "",
        errorMessage = ""
    } = {}
) {
    return {
        errorCode: buildFlowErrorCodeState(errorCode).errorCode,
        errorMessage: buildFlowErrorMessageState(errorMessage).errorMessage
    };
}

export function bindFlowErrorEditorEvents(
    panel,
    {
        data,
        refreshFlowNodeHtml
    } = {}
) {
    panel.querySelector("#flowErrorCode")?.addEventListener("input", (e) => {
        data.errorCode = buildFlowErrorCodeState(e.target.value).errorCode;
        refreshFlowNodeHtml?.();
    });
    panel.querySelector("#flowErrorMessage")?.addEventListener("input", (e) => {
        data.errorMessage = buildFlowErrorMessageState(e.target.value).errorMessage;
        refreshFlowNodeHtml?.();
    });
}

export function mountFlowErrorEditor(
    panel,
    {
        data,
        escapeHtml = (value) => String(value ?? ""),
        headerHtml = "",
        refreshFlowNodeHtml
    } = {}
) {
    const editorState = buildFlowErrorEditorState(data);
    panel.innerHTML = buildFlowErrorEditorMarkup(editorState, escapeHtml, headerHtml);
    bindFlowErrorEditorEvents(panel, {
        data,
        refreshFlowNodeHtml
    });
}

export function buildFlowButtonEditorState(
    data = {},
    mode = "momentary"
) {
    const normalizedMode = mode === "alternate" ? "alternate" : "momentary";
    return {
        label: String(data.label || ""),
        signalName: String(data.signalName || ""),
        mode: normalizedMode,
        trueLabel: String(data.trueLabel || "ON"),
        falseLabel: String(data.falseLabel || "OFF"),
        modeOptions: [
            { value: "momentary", label: "モメンタリ（押している間ON）", selected: normalizedMode === "momentary" },
            { value: "alternate", label: "オルタネート（押すたび反転・保持）", selected: normalizedMode === "alternate" }
        ]
    };
}

export function buildFlowButtonEditorMarkup(
    editorState = {},
    escapeHtml = (value) => String(value ?? ""),
    headerHtml = ""
) {
    const modeOptions = (Array.isArray(editorState.modeOptions) ? editorState.modeOptions : []).map((option) =>
        `<option value="${option.value}"${option.selected ? " selected" : ""}>${escapeHtml(option.label)}</option>`
    ).join("");
    return `
            ${headerHtml}
            <div class="editor-grid">
                <label>表示名
                    <input type="text" id="flowButtonLabel" value="${escapeHtml(editorState.label)}">
                </label>
                <label>入力信号名
                    <input type="text" id="flowButtonSignal" value="${escapeHtml(editorState.signalName)}">
                </label>
                <label>動作モード
                    <select id="flowButtonMode">
                        ${modeOptions}
                    </select>
                </label>
                <label>ONラベル
                    <input type="text" id="flowButtonTrueLabel" value="${escapeHtml(editorState.trueLabel)}">
                </label>
                <label>OFFラベル
                    <input type="text" id="flowButtonFalseLabel" value="${escapeHtml(editorState.falseLabel)}">
                </label>
            </div>
        `;
}

export function buildFlowButtonModeChangeState(mode = "momentary") {
    return {
        mode: mode === "alternate" ? "alternate" : "momentary"
    };
}

export function buildFlowButtonLabelState(label = "") {
    return {
        label: String(label || "押しボタン")
    };
}

export function buildFlowButtonSignalState(signalName = "") {
    return {
        signalName: String(signalName || "X_PB1")
    };
}

export function buildFlowButtonBranchLabelState(label = "", fallback = "ON") {
    return {
        label: String(label || fallback)
    };
}

export function buildFlowButtonSyncState(
    {
        label = "",
        signalName = "",
        mode = "momentary",
        trueLabel = "",
        falseLabel = ""
    } = {}
) {
    return {
        label: buildFlowButtonLabelState(label).label,
        signalName: buildFlowButtonSignalState(signalName).signalName,
        mode: buildFlowButtonModeChangeState(mode).mode,
        trueLabel: buildFlowButtonBranchLabelState(trueLabel, "ON").label,
        falseLabel: buildFlowButtonBranchLabelState(falseLabel, "OFF").label
    };
}

export function bindFlowButtonEditorEvents(
    panel,
    {
        data,
        normalizeButtonMode = (value) => value,
        refreshFlowNodeHtml
    } = {}
) {
    panel.querySelector("#flowButtonLabel")?.addEventListener("input", (e) => {
        data.label = buildFlowButtonLabelState(e.target.value).label;
        refreshFlowNodeHtml?.();
    });
    panel.querySelector("#flowButtonSignal")?.addEventListener("input", (e) => {
        data.signalName = buildFlowButtonSignalState(e.target.value).signalName;
        refreshFlowNodeHtml?.();
    });
    panel.querySelector("#flowButtonMode")?.addEventListener("change", (e) => {
        data.mode = buildFlowButtonModeChangeState(
            normalizeButtonMode(e.target.value)
        ).mode;
        refreshFlowNodeHtml?.();
    });
    panel.querySelector("#flowButtonTrueLabel")?.addEventListener("input", (e) => {
        data.trueLabel = buildFlowButtonBranchLabelState(e.target.value, "ON").label;
        refreshFlowNodeHtml?.();
    });
    panel.querySelector("#flowButtonFalseLabel")?.addEventListener("input", (e) => {
        data.falseLabel = buildFlowButtonBranchLabelState(e.target.value, "OFF").label;
        refreshFlowNodeHtml?.();
    });
}

export function mountFlowButtonEditor(
    panel,
    {
        data,
        mode = "momentary",
        escapeHtml = (value) => String(value ?? ""),
        headerHtml = "",
        normalizeButtonMode = (value) => value,
        refreshFlowNodeHtml
    } = {}
) {
    const editorState = buildFlowButtonEditorState(data, mode);
    panel.innerHTML = buildFlowButtonEditorMarkup(editorState, escapeHtml, headerHtml);
    bindFlowButtonEditorEvents(panel, {
        data,
        normalizeButtonMode,
        refreshFlowNodeHtml
    });
}

export function buildFlowEndEditorState(data = {}) {
    const result = data.result === "abort" || data.result === "error" ? data.result : "normal";
    return {
        label: String(data.label || ""),
        result,
        resultOptions: [
            { value: "normal", label: "正常終了", selected: result === "normal" },
            { value: "abort", label: "中断", selected: result === "abort" },
            { value: "error", label: "異常終了", selected: result === "error" }
        ]
    };
}

export function buildFlowEndEditorMarkup(
    editorState = {},
    escapeHtml = (value) => String(value ?? ""),
    headerHtml = ""
) {
    const resultOptions = (Array.isArray(editorState.resultOptions) ? editorState.resultOptions : []).map((option) =>
        `<option value="${option.value}"${option.selected ? " selected" : ""}>${escapeHtml(option.label)}</option>`
    ).join("");
    return `
            ${headerHtml}
            <div class="editor-grid">
                <label>表示名
                    <input type="text" id="flowEndLabel" value="${escapeHtml(editorState.label)}">
                </label>
                <label>終了種別
                    <select id="flowEndResult">
                        ${resultOptions}
                    </select>
                </label>
            </div>
        `;
}

export function buildFlowEndResultChangeState(result = "normal") {
    return {
        result: result === "abort" || result === "error" ? result : "normal"
    };
}

export function buildFlowEndLabelState(label = "") {
    return {
        label: String(label || "終了")
    };
}

export function buildFlowEndSyncState(
    {
        label = "",
        result = "normal"
    } = {}
) {
    return {
        label: buildFlowEndLabelState(label).label,
        result: buildFlowEndResultChangeState(result).result
    };
}

export function bindFlowEndEditorEvents(
    panel,
    {
        data,
        refreshFlowNodeHtml
    } = {}
) {
    panel.querySelector("#flowEndLabel")?.addEventListener("input", (e) => {
        data.label = buildFlowEndLabelState(e.target.value).label;
        refreshFlowNodeHtml?.();
    });
    panel.querySelector("#flowEndResult")?.addEventListener("change", (e) => {
        data.result = buildFlowEndResultChangeState(e.target.value || "normal").result;
        refreshFlowNodeHtml?.();
    });
}

export function mountFlowEndEditor(
    panel,
    {
        data,
        escapeHtml = (value) => String(value ?? ""),
        headerHtml = "",
        refreshFlowNodeHtml
    } = {}
) {
    const editorState = buildFlowEndEditorState(data);
    panel.innerHTML = buildFlowEndEditorMarkup(editorState, escapeHtml, headerHtml);
    bindFlowEndEditorEvents(panel, {
        data,
        refreshFlowNodeHtml
    });
}

export function mountFlowConditionEditor(
    panel,
    {
        data,
        conditionMode,
        conditionKind,
        conditionRows = [],
        operator,
        internalFlags = [],
        actuators = [],
        headerHtml = "",
        escapeHtml = (value) => String(value ?? ""),
        getConditionMode,
        getConditionKind,
        ensureConditionDraftSignal,
        normalizeConditionEntries,
        normalizeConditionEntry,
        renderFlowNodeEditor,
        refreshFlowNodeHtml
    } = {}
) {
    const { editorState, renderState, markupOptions } = buildFlowConditionEditorPanelState({
        data,
        conditionMode,
        conditionKind,
        conditionRows,
        operator,
        internalFlags
    });
    panel.innerHTML = buildFlowConditionEditorMarkup(
        editorState,
        renderState,
        {
            headerHtml,
            internalFlags: markupOptions.internalFlags,
            actuators,
            selectedActuatorId: markupOptions.selectedActuatorId,
            escapeHtml
        }
    );
    bindFlowConditionEditorEvents(panel, {
        data,
        getConditionMode,
        getConditionKind,
        ensureConditionDraftSignal,
        normalizeConditionEntries,
        normalizeConditionEntry,
        renderFlowNodeEditor,
        refreshFlowNodeHtml
    });
}

export function buildControlSpecActuatorListState(actuators = []) {
    const rows = Array.isArray(actuators) ? actuators : [];
    return {
        rows,
        count: rows.length,
        metaText: `登録済みアクチュエータ: ${rows.length}`,
        isEmpty: rows.length === 0,
        emptyMessage: "登録されているアクチュエータはありません。「＋ アクチュエータ追加」ボタンから追加してください。"
    };
}

function buildControlSpecActuatorValveSummaryText(
    actuator = {},
    normalizeActuatorType = (value) => String(value || "")
) {
    const actuatorType = typeof normalizeActuatorType === "function"
        ? normalizeActuatorType(actuator?.actuatorType)
        : String(actuator?.actuatorType || "");
    if (actuatorType !== "pneumaticCylinder") return "";
    const pneumaticValveMode = normalizePneumaticValveMode(actuator?.pneumaticValveMode, actuatorType);
    const valveModel = normalizePneumaticActuatorValveModel(
        actuator?.valveModel,
        actuatorType,
        actuator?.actions,
        pneumaticValveMode,
        actuator?.solenoidConfig
    );
    const usesVerticalLabels = Array.isArray(actuator?.actions)
        && actuator.actions.some((action) => ["up", "down"].includes(String(action?.id || "").trim()));
    const directionPair = resolveMotionSolenoidDirectionPair(usesVerticalLabels ? "up" : "extend");
    if (pneumaticValveMode === "singleSolenoid") {
        const singleOutput = String(valveModel?.singleOutput || "").trim();
        const activeLabel = String(valveModel?.singleSolenoidActiveDirection || "").trim() === "negative"
            ? directionPair.negativeLabel
            : directionPair.positiveLabel;
        return singleOutput ? `${singleOutput} / 通電=${activeLabel}` : `出力未設定 / 通電=${activeLabel}`;
    }
    const positiveOutput = String(valveModel?.positiveOutput || "").trim() || "未設定";
    const negativeOutput = String(valveModel?.negativeOutput || "").trim() || "未設定";
    return `${directionPair.positiveLabel}:${positiveOutput} / ${directionPair.negativeLabel}:${negativeOutput}`;
}

export function buildControlSpecActuatorRowModel(
    actuator = {},
    getActuatorTypeLabel = () => "",
    getMotorControlTypeLabel = () => "",
    getPneumaticValveModeLabel = () => "",
    normalizeControlMethod = (value) => String(value || ""),
    normalizeActuatorType = (value) => String(value || "")
) {
    const actuatorType = normalizeActuatorType(actuator?.actuatorType);
    const typeLabel = typeof getActuatorTypeLabel === "function"
        ? getActuatorTypeLabel(actuatorType)
        : String(actuatorType || "");
    const motorLabel = actuatorType === "motor" && typeof getMotorControlTypeLabel === "function"
        ? ` / ${getMotorControlTypeLabel(actuator?.motorControlType, actuatorType)}`
        : "";
    const valveLabel = actuatorType === "pneumaticCylinder" && typeof getPneumaticValveModeLabel === "function"
        ? ` / ${getPneumaticValveModeLabel(actuator?.pneumaticValveMode)}`
        : "";
    const controlMethod = typeof normalizeControlMethod === "function"
        ? normalizeControlMethod(actuator?.controlMethod, actuatorType)
        : String(actuator?.controlMethod || "");
    const axisRefText = actuator?.axisRef ? ` / ${String(actuator.axisRef)}` : "";
    const valveSummaryText = buildControlSpecActuatorValveSummaryText(actuator, normalizeActuatorType);
    return {
        idText: String(actuator?.id || ""),
        nameText: String(actuator?.name || ""),
        typeText: `${typeLabel}${motorLabel}${valveLabel}`,
        controlMethodText: `${controlMethod}${axisRefText}`,
        valveSummaryText,
        actionCountText: String(Array.isArray(actuator?.actions) ? actuator.actions.length : 0)
    };
}

export function buildControlSpecSignalListState(signals = []) {
    const rows = Array.isArray(signals) ? signals : [];
    return {
        rows,
        count: rows.length,
        metaText: `登録済み信号: ${rows.length}`,
        isEmpty: rows.length === 0,
        emptyMessage: "登録されている信号はありません。「＋ 信号追加」ボタンから信号を追加してください。"
    };
}

export function buildControlSpecSignalRowModel(
    signal = {},
    findCategory = () => null
) {
    const direction = String(signal?.direction || "output");
    const badgeClass = direction === "input" ? "input" : (direction === "internal" ? "internal" : "output");
    const directionLabel = direction === "input"
        ? "input (入力)"
        : (direction === "internal" ? "internal (内部)" : "output (出力)");
    const category = typeof findCategory === "function" ? findCategory(signal?.category) : null;
    return {
        idText: String(signal?.id || ""),
        nameText: String(signal?.name || ""),
        categoryText: String(category?.name || signal?.category || ""),
        badgeClass,
        directionLabel,
        addressText: String(signal?.address || ""),
        iecNameText: String(signal?.iecName || ""),
        dataTypeText: String(signal?.dataType || "BOOL"),
        activeStateText: String(signal?.activeState || "TRUE"),
        actuatorIdText: String(signal?.actuatorId || ""),
        descriptionText: String(signal?.description || "")
    };
}

export function buildControlSpecSignalCategoryListState(categories = []) {
    const rows = Array.isArray(categories) ? categories : [];
    return {
        rows,
        count: rows.length,
        isEmpty: rows.length === 0,
        emptyMessage: "登録されている信号カテゴリはありません。必要に応じてカテゴリを追加してください。"
    };
}

export function buildControlSpecSignalCategoryRowModel(category = {}) {
    return {
        idText: String(category?.id || ""),
        nameText: String(category?.name || ""),
        directionText: String(category?.direction || ""),
        addressPrefixText: String(category?.addressPrefix || ""),
        startByteText: String(category?.startByte ?? 0)
    };
}

export function buildControlSpecFlowSignalCandidateOptionsState(
    candidates = [],
    escapeHtml = (value) => String(value ?? "")
) {
    const list = Array.isArray(candidates) ? candidates : [];
    return {
        options: [
            `<option value="">条件分岐や信号操作から選択</option>`,
            ...list.map((candidate, index) => {
                const suffix = candidate?.existingSignalId ? " (登録済み)" : "";
                return `<option value="${index}">${escapeHtml(candidate?.label || "")} → ${escapeHtml(candidate?.signalId || "")}${suffix}</option>`;
            })
        ].join("")
    };
}

export function buildControlSpecFlowSignalCandidateFormState(
    candidate = {},
    hasCategory = () => false
) {
    const direction = String(candidate?.direction || "input");
    const categoryId = direction === "output" ? "actuator" : "sensor";
    return {
        signalId: String(candidate?.signalId || ""),
        signalName: String(candidate?.name || candidate?.signalId || ""),
        categoryId: typeof hasCategory === "function" && hasCategory(categoryId) ? categoryId : "",
        direction,
        description: String(candidate?.description || ""),
        actuatorId: String(candidate?.actuatorId || "")
    };
}

export function buildControlSpecFlowSignalCandidateApplyState(
    candidate = {},
    hasCategory = () => false
) {
    if (candidate?.existingSignalId) {
        return {
            mode: "existing",
            existingSignalId: String(candidate.existingSignalId || "")
        };
    }
    return {
        mode: "new",
        formState: buildControlSpecFlowSignalCandidateFormState(candidate, hasCategory)
    };
}

export function normalizeSignalDataType(value) {
    const normalized = String(value || "BOOL").trim().toUpperCase();
    return SIGNAL_DATA_TYPES.has(normalized) ? normalized : "BOOL";
}

export function toSignalDraftId(text, fallback = "Signal") {
    const normalized = String(text || "")
        .trim()
        .replace(/[^A-Za-z0-9_]+/g, "_")
        .replace(/^_+|_+$/g, "");
    if (!normalized) return fallback;
    return /^[A-Za-z_]/.test(normalized) ? normalized : `S_${normalized}`;
}

export function makeUniqueSignalDraftId(baseId, signals = []) {
    const used = new Set((Array.isArray(signals) ? signals : []).map((sig) => String(sig?.id || "").toLowerCase()));
    let candidate = baseId;
    let index = 2;
    while (used.has(candidate.toLowerCase())) {
        candidate = `${baseId}_${index}`;
        index += 1;
    }
    return candidate;
}

export function findExistingSignalByName(name, direction, signals = []) {
    const normalizedName = String(name || "").trim();
    if (!normalizedName) return null;
    return (Array.isArray(signals) ? signals : []).find((sig) =>
        String(sig?.name || "").trim() === normalizedName &&
        (!direction || String(sig?.direction || "") === direction)
    ) || null;
}

export function buildNextSignalAddress(category = null, signals = []) {
    if (!category) return "";
    const prefix = category.addressPrefix || "%MX";
    const startByte = Math.max(0, parseInt(category.startByte, 10) || 0);
    const used = new Set();
    const escapedPrefix = prefix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const isBitAddress = prefix.endsWith("X");
    const pattern = isBitAddress
        ? new RegExp(`^${escapedPrefix}(\\d+)\\.(\\d+)$`, "i")
        : new RegExp(`^${escapedPrefix}(\\d+)$`, "i");

    (Array.isArray(signals) ? signals : []).forEach((signal) => {
        const match = String(signal?.address || "").trim().match(pattern);
        if (!match) return;
        const byteNo = parseInt(match[1], 10);
        if (byteNo < startByte) return;
        if (isBitAddress) {
            const bitNo = parseInt(match[2], 10);
            if (bitNo >= 0 && bitNo <= 7) used.add(`${byteNo}.${bitNo}`);
            return;
        }
        used.add(String(byteNo));
    });

    for (let byteNo = startByte; byteNo < startByte + 1000; byteNo += 1) {
        if (!isBitAddress && !used.has(String(byteNo))) return `${prefix}${byteNo}`;
        if (!isBitAddress) continue;
        for (let bitNo = 0; bitNo < 8; bitNo += 1) {
            if (!used.has(`${byteNo}.${bitNo}`)) return `${prefix}${byteNo}.${bitNo}`;
        }
    }
    return "";
}

export function buildControlSpecFlowConditionCandidates(
    moduleName = "Home",
    normalizedConditionNode = {},
    existingSignals = [],
    startIndex = 0
) {
    const conditionText = String(normalizedConditionNode?.conditionText || "条件").trim() || "条件";
    const entries = Array.isArray(normalizedConditionNode?.conditions) ? normalizedConditionNode.conditions : [];
    const existingList = Array.isArray(existingSignals) ? existingSignals : [];
    const candidates = [];

    entries.forEach((entry, index) => {
        const expression = String(entry?.expression || "").trim();
        if (!expression) return;
        const signalId = toSignalDraftId(expression, `Condition_${startIndex + candidates.length + 1}`);
        const existing = existingList.find((sig) => sig?.id === signalId);
        candidates.push({
            dedupeKey: `condition:${expression}`,
            sourceType: "condition",
            label: `条件分岐 (${moduleName}): ${conditionText} / ${entry?.label || `条件${index + 1}`}`,
            signalId,
            name: entry?.label || conditionText || expression,
            direction: "input",
            description: `条件分岐「${conditionText}」の式: ${expression}`,
            actuatorId: existing?.actuatorId || "",
            existingSignalId: existing?.id || ""
        });
    });

    return candidates;
}

export function buildControlSpecFlowSignalOutputCandidate(
    moduleName = "Home",
    normalizedSignalOutputNode = {},
    existingSignals = [],
    fallbackIndex = 0
) {
    if (normalizedSignalOutputNode?.signalScope !== "external") return null;
    const signalName = String(normalizedSignalOutputNode?.signalName || "").trim();
    if (!signalName) return null;
    const signalId = toSignalDraftId(signalName, `Output_${fallbackIndex + 1}`);
    const existing = (Array.isArray(existingSignals) ? existingSignals : []).find((sig) => sig?.id === signalId);
    return {
        dedupeKey: `signal_output:${signalName}`,
        sourceType: "signal_output",
        label: `信号操作 (${moduleName}): ${normalizedSignalOutputNode?.label || signalName}`,
        signalId,
        name: normalizedSignalOutputNode?.label || signalName,
        direction: "output",
        description: `信号操作ノードの信号名: ${signalName} (${normalizedSignalOutputNode?.action || "ON"})`,
        actuatorId: existing?.actuatorId || "",
        existingSignalId: existing?.id || ""
    };
}

export function appendUniqueControlSpecFlowCandidates(
    candidates = [],
    seenKeys = [],
    nextCandidates = []
) {
    const list = Array.isArray(candidates) ? [...candidates] : [];
    const seen = new Set(Array.isArray(seenKeys) ? seenKeys : []);
    (Array.isArray(nextCandidates) ? nextCandidates : []).forEach((candidate) => {
        const key = String(candidate?.dedupeKey || "").trim();
        if (!key || seen.has(key)) return;
        seen.add(key);
        list.push({
            sourceType: candidate?.sourceType || "",
            label: candidate?.label || "",
            signalId: candidate?.signalId || "",
            name: candidate?.name || "",
            direction: candidate?.direction || "",
            description: candidate?.description || "",
            actuatorId: candidate?.actuatorId || "",
            existingSignalId: candidate?.existingSignalId || ""
        });
    });
    return {
        candidates: list,
        seenKeys: Array.from(seen)
    };
}

export function buildControlSpecSignalFormInput(values = {}) {
    return {
        id: String(values?.id || ""),
        name: String(values?.name || ""),
        iecName: String(values?.iecName || ""),
        category: String(values?.category || ""),
        direction: String(values?.direction || ""),
        address: String(values?.address || ""),
        dataType: String(values?.dataType || ""),
        activeState: String(values?.activeState || ""),
        actuatorId: String(values?.actuatorId || ""),
        description: String(values?.description || "")
    };
}

export function buildControlSpecSignalDraft(values = {}) {
    return {
        id: String(values?.id || "").trim(),
        name: String(values?.name || "").trim(),
        iecName: String(values?.iecName || "").trim(),
        category: String(values?.category || "").trim(),
        direction: String(values?.direction || "input") || "input",
        address: String(values?.address || "").trim(),
        dataType: normalizeSignalDataType(values?.dataType || "BOOL"),
        activeState: String(values?.activeState || "TRUE") || "TRUE",
        actuatorId: String(values?.actuatorId || "").trim(),
        description: String(values?.description || "").trim()
    };
}

export function validateControlSpecSignalDraft(draft = {}, editingSignalId = null, signals = []) {
    if (!draft?.id) {
        return { ok: false, errorMessage: "信号IDは必須入力項目です。" };
    }
    if (!/^[a-zA-Z0-9_]+$/.test(draft.id)) {
        return { ok: false, errorMessage: "信号IDは半角英数字およびアンダースコア（_）のみ使用可能です。" };
    }
    if (!draft?.name) {
        return { ok: false, errorMessage: "名称は必須入力項目です。" };
    }
    if (draft?.iecName && !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(draft.iecName)) {
        return { ok: false, errorMessage: "IEC変数名は英字またはアンダースコアで始まる半角英数字/アンダースコアのみ使用できます。" };
    }
    if ((draft?.direction === "input" || draft?.direction === "output") && !draft?.address) {
        return { ok: false, errorMessage: "input / output 信号のアドレスは必須入力項目です。" };
    }
    const list = Array.isArray(signals) ? signals : [];
    if (editingSignalId == null) {
        const duplicate = list.some((signal) => String(signal?.id || "").toLowerCase() === String(draft.id || "").toLowerCase());
        if (duplicate) {
            return { ok: false, errorMessage: `信号ID '${draft.id}' は既に登録されています。` };
        }
        return { ok: true, mode: "create" };
    }
    const index = list.findIndex((signal) => signal?.id === editingSignalId);
    if (index === -1) {
        return { ok: false, errorMessage: "編集対象の信号データが見つかりません。" };
    }
    return { ok: true, mode: "update", index };
}

export function buildSavedControlSpecSignals(signals = [], draft = {}, validation = {}) {
    const list = Array.isArray(signals) ? [...signals] : [];
    if (validation?.mode === "create") {
        list.push({ ...draft });
        return list;
    }
    if (validation?.mode === "update" && validation.index >= 0 && validation.index < list.length) {
        list[validation.index] = {
            ...list[validation.index],
            name: draft?.name || "",
            iecName: draft?.iecName || "",
            category: draft?.category || "",
            direction: draft?.direction || "input",
            address: draft?.address || "",
            dataType: draft?.dataType || "BOOL",
            activeState: draft?.activeState || "TRUE",
            actuatorId: draft?.actuatorId || "",
            description: draft?.description || ""
        };
    }
    return list;
}

export function buildDeleteControlSpecSignalState(signalId = "", refCheck = {}, signals = []) {
    const id = String(signalId || "");
    const confirmMessage = refCheck?.referenced
        ? `【警告】信号 '${id}' は、アクチュエータ '${refCheck.actuatorId}' のアクション '${refCheck.actionId}' 内の「${refCheck.type}」で現在参照されています。\n\n削除すると、設計検証エラー（SIGNAL_MISSING）が発生します。本当に削除しますか？`
        : `信号 '${id}' を削除しますか？`;
    return {
        confirmMessage,
        nextSignals: (Array.isArray(signals) ? signals : []).filter((signal) => signal?.id !== id)
    };
}

export function findControlSpecSignalReferenceInActuators(signalId = "", actuators = []) {
    const id = String(signalId || "");
    for (const actuator of (Array.isArray(actuators) ? actuators : [])) {
        const actions = Array.isArray(actuator?.actions) ? actuator.actions : [];
        for (const action of actions) {
            if (action?.command) {
                const sets = Array.isArray(action.command.setOutputs) ? action.command.setOutputs : [];
                const resets = Array.isArray(action.command.resetOutputs) ? action.command.resetOutputs : [];
                if (sets.includes(id) || resets.includes(id)) {
                    return {
                        referenced: true,
                        actuatorId: actuator?.id || "",
                        actionId: action?.id || "",
                        type: "コマンド出力"
                    };
                }
            }
            const doneConditions = Array.isArray(action?.doneConditions) ? action.doneConditions : [];
            for (const condition of doneConditions) {
                if (condition?.signalId === id) {
                    return {
                        referenced: true,
                        actuatorId: actuator?.id || "",
                        actionId: action?.id || "",
                        type: "完了条件"
                    };
                }
            }
            const faultConditions = Array.isArray(action?.faultConditions) ? action.faultConditions : [];
            for (const condition of faultConditions) {
                if (condition?.signalId === id) {
                    return {
                        referenced: true,
                        actuatorId: actuator?.id || "",
                        actionId: action?.id || "",
                        type: "異常条件"
                    };
                }
            }
        }
    }
    return { referenced: false };
}

export function buildLoadedControlSpecSignalFormState(signal = {}, signalId = "") {
    return {
        editingSignalId: String(signalId || signal?.id || ""),
        titleText: `信号を編集: ${String(signalId || signal?.id || "")}`,
        inputId: String(signal?.id || ""),
        inputIdDisabled: true,
        inputIdBackground: "#eee",
        inputName: String(signal?.name || ""),
        inputIecName: String(signal?.iecName || ""),
        category: String(signal?.category || ""),
        direction: String(signal?.direction || "input") || "input",
        address: String(signal?.address || ""),
        dataType: normalizeSignalDataType(signal?.dataType),
        activeState: String(signal?.activeState || "TRUE") || "TRUE",
        actuatorId: String(signal?.actuatorId || ""),
        description: String(signal?.description || ""),
        hideError: true
    };
}

export function buildResetControlSpecSignalFormState(defaultCategoryId = "") {
    return {
        editingSignalId: null,
        titleText: "信号を追加",
        inputId: "",
        inputIdDisabled: false,
        inputIdBackground: "#fff",
        inputName: "",
        inputIecName: "",
        category: String(defaultCategoryId || ""),
        direction: "input",
        address: "",
        dataType: "BOOL",
        activeState: "TRUE",
        actuatorId: "",
        description: "",
        hideError: true
    };
}

export function buildControlSpecSignalCategoryFormInput(values = {}) {
    return {
        id: String(values?.id || ""),
        name: String(values?.name || ""),
        direction: String(values?.direction || ""),
        addressPrefix: String(values?.addressPrefix || ""),
        startByte: String(values?.startByte || "")
    };
}

export function buildSignalCategoryOptionsState(categories = [], selectedId = "", escapeHtml = (value) => String(value ?? "")) {
    const list = Array.isArray(categories) ? categories : [];
    return {
        options: list.map((category) => {
            const selected = category.id === selectedId ? " selected" : "";
            return `<option value="${escapeHtml(category.id)}"${selected}>${escapeHtml(category.name)} (${escapeHtml(category.id)})</option>`;
        }).join("")
    };
}

export function buildLoadedSignalCategoryFormState(category = {}) {
    return {
        editingSignalCategoryId: String(category?.id || ""),
        titleText: `カテゴリを編集: ${String(category?.id || "")}`,
        inputId: String(category?.id || ""),
        inputIdDisabled: true,
        inputIdBackground: "#eee",
        inputName: String(category?.name || ""),
        direction: String(category?.direction || "internal") || "internal",
        addressPrefix: String(category?.addressPrefix || "%MX") || "%MX",
        startByte: String(category?.startByte ?? 0)
    };
}

export function buildResetSignalCategoryFormState() {
    return {
        editingSignalCategoryId: null,
        titleText: "カテゴリを追加",
        inputId: "",
        inputIdDisabled: false,
        inputIdBackground: "#fff",
        inputName: "",
        direction: "internal",
        addressPrefix: "%MX",
        startByte: "0"
    };
}

export function buildControlSpecSignalCategoryDraft(values = {}) {
    return {
        id: String(values?.id || "").trim(),
        name: String(values?.name || "").trim(),
        direction: String(values?.direction || "internal") || "internal",
        addressPrefix: String(values?.addressPrefix || "%MX") || "%MX",
        startByte: Math.max(0, parseInt(values?.startByte, 10) || 0)
    };
}

export function validateControlSpecSignalCategoryDraft(draft = {}, editingCategoryId = null, categories = []) {
    if (!draft?.id) {
        return { ok: false, errorMessage: "カテゴリIDは必須入力項目です。" };
    }
    if (!/^[a-zA-Z0-9_]+$/.test(draft.id)) {
        return { ok: false, errorMessage: "カテゴリIDは半角英数字およびアンダースコア（_）のみ使用可能です。" };
    }
    if (!draft?.name) {
        return { ok: false, errorMessage: "名称は必須入力項目です。" };
    }
    const list = Array.isArray(categories) ? categories : [];
    if (editingCategoryId == null) {
        if (list.some((entry) => String(entry?.id || "").toLowerCase() === draft.id.toLowerCase())) {
            return { ok: false, errorMessage: `カテゴリID '${draft.id}' は既に登録されています。` };
        }
        return { ok: true, mode: "create" };
    }
    const index = list.findIndex((entry) => entry?.id === editingCategoryId);
    if (index === -1) {
        return { ok: false, errorMessage: "編集対象のカテゴリデータが見つかりません。" };
    }
    return { ok: true, mode: "update", index };
}

export function buildSavedControlSpecSignalCategories(categories = [], draft = {}, validation = {}) {
    const list = Array.isArray(categories) ? [...categories] : [];
    if (validation?.mode === "create") {
        list.push({ ...draft });
        return list;
    }
    if (validation?.mode === "update" && validation.index >= 0 && validation.index < list.length) {
        list[validation.index] = {
            ...list[validation.index],
            name: draft?.name || "",
            direction: draft?.direction || "internal",
            addressPrefix: draft?.addressPrefix || "%MX",
            startByte: Math.max(0, parseInt(draft?.startByte, 10) || 0)
        };
    }
    return list;
}

export function buildDeleteControlSpecSignalCategoryState(categoryId = "", categories = [], signals = []) {
    const id = String(categoryId || "");
    const list = Array.isArray(categories) ? categories : [];
    if (list.length <= 1) {
        return {
            ok: false,
            alertMessage: "信号カテゴリは最低1件必要です。"
        };
    }
    const nextSignals = (Array.isArray(signals) ? signals : []).map((signal) =>
        signal?.category === id ? { ...signal, category: "" } : signal
    );
    const used = nextSignals.some((signal, index) => (Array.isArray(signals) ? signals : [])[index]?.category === id);
    return {
        ok: true,
        confirmMessage: used
            ? `カテゴリ '${id}' は信号で使用されています。削除すると該当信号のカテゴリが未設定になります。本当に削除しますか？`
            : `カテゴリ '${id}' を削除しますか？`,
        nextCategories: list.filter((entry) => entry?.id !== id),
        nextSignals
    };
}

function buildControlSpecActuatorValveModelInput(
    values = {},
    actuatorType = "",
    pneumaticValveMode = "doubleSolenoid",
    actions = []
) {
    const sourceValveModel = values?.valveModel && typeof values.valveModel === "object"
        ? values.valveModel
        : {};
    return normalizePneumaticActuatorValveModel(
        {
            positiveOutput: String(values?.valvePositiveOutput || values?.positiveOutput || sourceValveModel?.positiveOutput || "").trim(),
            negativeOutput: String(values?.valveNegativeOutput || values?.negativeOutput || sourceValveModel?.negativeOutput || "").trim(),
            singleOutput: String(values?.valveSingleOutput || values?.singleOutput || sourceValveModel?.singleOutput || "").trim(),
            singleSolenoidActiveDirection: String(
                values?.valveSingleSolenoidActiveDirection
                || values?.singleSolenoidActiveDirection
                || sourceValveModel?.singleSolenoidActiveDirection
                || ""
            ).trim(),
            stateTable: {
                extend: {
                    positive: String(values?.valveExtendPositive || sourceValveModel?.stateTable?.extend?.positive || "").trim(),
                    negative: String(values?.valveExtendNegative || sourceValveModel?.stateTable?.extend?.negative || "").trim(),
                    single: String(values?.valveExtendSingle || sourceValveModel?.stateTable?.extend?.single || "").trim()
                },
                retract: {
                    positive: String(values?.valveRetractPositive || sourceValveModel?.stateTable?.retract?.positive || "").trim(),
                    negative: String(values?.valveRetractNegative || sourceValveModel?.stateTable?.retract?.negative || "").trim(),
                    single: String(values?.valveRetractSingle || sourceValveModel?.stateTable?.retract?.single || "").trim()
                },
                stop: {
                    positive: String(values?.valveStopPositive || sourceValveModel?.stateTable?.stop?.positive || "").trim(),
                    negative: String(values?.valveStopNegative || sourceValveModel?.stateTable?.stop?.negative || "").trim(),
                    single: String(values?.valveStopSingle || sourceValveModel?.stateTable?.stop?.single || "").trim()
                },
                emergency: {
                    positive: String(values?.valveEmergencyPositive || sourceValveModel?.stateTable?.emergency?.positive || "").trim(),
                    negative: String(values?.valveEmergencyNegative || sourceValveModel?.stateTable?.emergency?.negative || "").trim(),
                    single: String(values?.valveEmergencySingle || sourceValveModel?.stateTable?.emergency?.single || "").trim()
                }
            }
        },
        actuatorType,
        actions,
        pneumaticValveMode,
        values?.solenoidConfig || values?.valveModel || null
    );
}

export function buildControlSpecActuatorFormInput(
    values = {},
    fallbackId = "",
    actuators = [],
    editingActuatorId = null
) {
    const typedId = String(values?.typedId || "").trim();
    const actuatorType = String(values?.actuatorType || "");
    const pneumaticValveMode = normalizePneumaticValveMode(values?.pneumaticValveMode, actuatorType);
    const actions = JSON.parse(JSON.stringify(Array.isArray(values?.actions) ? values.actions : []));
    const valveModel = buildControlSpecActuatorValveModelInput(
        values,
        actuatorType,
        pneumaticValveMode,
        actions
    );
    const formInput = {
        typedId,
        id: String(fallbackId || typedId || "").trim(),
        name: String(values?.name || "").trim(),
        actuatorType,
        motorControlType: String(values?.motorControlType || ""),
        pneumaticValveMode,
        valveModel,
        controlMethod: String(values?.controlMethod || ""),
        axisRef: String(values?.axisRef || "").trim(),
        actions
    };
    if (normalizeAxisActuatorType(actuatorType) === "pneumaticCylinder") {
        syncPneumaticActuatorActionCommands(formInput);
    }
    return formInput;
}

export function buildControlSpecActuatorDraft(values = {}, fallbackId = "") {
    return buildControlSpecActuatorFormInput(values, fallbackId);
}

function validatePneumaticActuatorValveModelDraft(draft = {}) {
    if (normalizeAxisActuatorType(draft?.actuatorType) !== "pneumaticCylinder") {
        return null;
    }
    const pneumaticValveMode = normalizePneumaticValveMode(draft?.pneumaticValveMode, draft?.actuatorType);
    const sourceValveModel = draft?.valveModel && typeof draft.valveModel === "object"
        ? draft.valveModel
        : {};
    const valveModel = normalizePneumaticActuatorValveModel(
        sourceValveModel,
        draft?.actuatorType,
        [],
        pneumaticValveMode,
        sourceValveModel
    );
    if (pneumaticValveMode === "singleSolenoid") {
        if (!String(valveModel?.singleOutput || "").trim()) {
            return "片ソレノイドでは片ソレノイド出力が必須です。";
        }
        return null;
    }
    const positiveOutput = String(valveModel?.positiveOutput || "").trim();
    const negativeOutput = String(valveModel?.negativeOutput || "").trim();
    if (!positiveOutput) {
        return "両ソレノイドでは前進側 / 上昇側 出力が必須です。";
    }
    if (!negativeOutput) {
        return "両ソレノイドでは後退側 / 下降側 出力が必須です。";
    }
    if (positiveOutput.toUpperCase() === negativeOutput.toUpperCase()) {
        return "両ソレノイドでは前進側 / 上昇側 と後退側 / 下降側 に同じ出力名は使えません。";
    }
    return null;
}

export function validateControlSpecActuatorDraft(draft = {}, editingActuatorId = null, actuators = []) {
    if (!/^[a-zA-Z0-9_]+$/.test(String(draft?.id || ""))) {
        return { ok: false, errorMessage: "アクチュエータIDは半角英数字およびアンダースコア（_）のみ使用可能です。" };
    }
    if (!draft?.name) {
        return { ok: false, errorMessage: "名称は必須項目です。" };
    }
    if ((draft?.controlMethod === "plcopenMotion" || draft?.controlMethod === "plcopenFluidPower") && !draft?.axisRef) {
        return { ok: false, errorMessage: "PLCopen系の制御方式では軸参照名 (Axis) が必要です。" };
    }
    const valveModelError = validatePneumaticActuatorValveModelDraft(draft);
    if (valveModelError) {
        return { ok: false, errorMessage: valveModelError };
    }
    const list = Array.isArray(actuators) ? actuators : [];
    if (editingActuatorId == null) {
        const duplicate = list.some((actuator) => String(actuator?.id || "").toLowerCase() === String(draft?.id || "").toLowerCase());
        if (duplicate) {
            return { ok: false, errorMessage: `アクチュエータID '${draft.id}' は既に登録されています。` };
        }
        return { ok: true, mode: "create" };
    }
    const index = list.findIndex((actuator) => actuator?.id === editingActuatorId);
    if (index === -1) {
        return { ok: false, errorMessage: "編集対象のアクチュエータが見つかりません。" };
    }
    return { ok: true, mode: "update", index };
}

export function buildSavedControlSpecActuators(actuators = [], draft = {}, validation = {}) {
    const list = Array.isArray(actuators) ? [...actuators] : [];
    if (validation?.mode === "create") {
        list.push({
            id: draft?.id || "",
            name: draft?.name || "",
            actuatorType: draft?.actuatorType || "",
            motorControlType: draft?.motorControlType || "",
            pneumaticValveMode: draft?.pneumaticValveMode || "",
            valveModel: draft?.valveModel || null,
            solenoidConfig: projectActuatorSolenoidConfigFromValveModel(
                draft?.valveModel,
                draft?.pneumaticValveMode
            ),
            controlMethod: draft?.controlMethod || "",
            axisRef: draft?.axisRef || "",
            actions: Array.isArray(draft?.actions) ? draft.actions : []
        });
        return list;
    }
    if (validation?.mode === "update" && validation.index >= 0 && validation.index < list.length) {
        list[validation.index] = {
            ...list[validation.index],
            name: draft?.name || "",
            actuatorType: draft?.actuatorType || "",
            motorControlType: draft?.motorControlType || "",
            pneumaticValveMode: draft?.pneumaticValveMode || "",
            valveModel: draft?.valveModel || null,
            solenoidConfig: projectActuatorSolenoidConfigFromValveModel(
                draft?.valveModel,
                draft?.pneumaticValveMode
            ),
            controlMethod: draft?.controlMethod || "",
            axisRef: draft?.axisRef || "",
            actions: Array.isArray(draft?.actions) ? draft.actions : []
        };
    }
    return list;
}

export function buildDeleteControlSpecActuatorState(actuatorId = "", refCheck = {}, actuators = []) {
    const id = String(actuatorId || "");
    const confirmMessage = refCheck?.referenced
        ? `【警告】アクチュエータ '${id}' は、フローモジュール '${refCheck.moduleName}' 内のノード '${refCheck.nodeLabel}' (ID: ${refCheck.nodeId}) で現在参照されています。\n\n削除すると、設計検証エラー（ACTUATOR_MISSING）が発生します。本当に削除しますか？`
        : `アクチュエータ '${id}' を削除しますか？`;
    return {
        confirmMessage,
        nextActuators: (Array.isArray(actuators) ? actuators : []).filter((actuator) => actuator?.id !== id)
    };
}

export function findControlSpecActuatorReferenceInFlow(actuatorId = "", actionId = null, modules = []) {
    const actuatorKey = String(actuatorId || "");
    const actionKey = actionId == null ? null : String(actionId);
    for (const moduleEntry of (Array.isArray(modules) ? modules : [])) {
        const moduleName = String(moduleEntry?.moduleName || "Home");
        const nodes = Array.isArray(moduleEntry?.nodes) ? moduleEntry.nodes : [];
        for (const node of nodes) {
            const nodeActuatorId = node?.actuatorId;
            const nodeActionId = node?.actionId;
            if (nodeActuatorId !== actuatorKey) continue;
            if (actionKey !== null && nodeActionId !== actionKey) continue;
            return {
                referenced: true,
                moduleName,
                nodeId: node?.nodeId,
                nodeLabel: node?.nodeLabel || `Node ${node?.nodeId || ""}`
            };
        }
    }
    return { referenced: false };
}

export function buildLoadedControlSpecActuatorFormState(actuator = {}, actuatorId = "") {
    const normalizedActuatorType = String(actuator?.actuatorType || "pneumaticCylinder") || "pneumaticCylinder";
    const pneumaticValveMode = normalizePneumaticValveMode(actuator?.pneumaticValveMode, normalizedActuatorType);
    const valveModel = normalizePneumaticActuatorValveModel(
        actuator?.valveModel,
        normalizedActuatorType,
        actuator?.actions,
        pneumaticValveMode,
        actuator?.solenoidConfig
    );
    return {
        editingActuatorId: String(actuatorId || actuator?.id || ""),
        actuatorActions: JSON.parse(JSON.stringify(Array.isArray(actuator?.actions) ? actuator.actions : [])),
        selectedActionIndex: null,
        titleText: `アクチュエータを編集: ${String(actuatorId || actuator?.id || "")}`,
        inputId: String(actuator?.id || ""),
        inputIdDisabled: true,
        inputIdBackground: "#eee",
        inputName: String(actuator?.name || ""),
        actuatorType: normalizedActuatorType,
        motorControlType: String(actuator?.motorControlType || ""),
        pneumaticValveMode,
        valveModel,
        controlMethod: String(actuator?.controlMethod || ""),
        axisRef: String(actuator?.axisRef || ""),
        hideError: true,
        hideDetailCard: true
    };
}

export function buildResetControlSpecActuatorFormState(defaultActions = []) {
    const valveModel = normalizePneumaticActuatorValveModel(
        {
            stateTable: createDefaultPneumaticValveStateTable("doubleSolenoid", "positive")
        },
        "pneumaticCylinder",
        [],
        "doubleSolenoid"
    );
    return {
        editingActuatorId: null,
        actuatorActions: JSON.parse(JSON.stringify(Array.isArray(defaultActions) ? defaultActions : [])),
        selectedActionIndex: null,
        titleText: "アクチュエータを追加",
        inputId: "",
        inputIdDisabled: false,
        inputIdBackground: "#fff",
        inputName: "",
        actuatorType: "pneumaticCylinder",
        motorControlType: "speedControl",
        pneumaticValveMode: "doubleSolenoid",
        valveModel,
        controlMethod: "discreteIo",
        axisRef: "",
        hideError: true,
        hideDetailCard: true
    };
}

export function buildResetControlSpecActuatorActionState(nextActions = []) {
    return {
        actuatorActions: JSON.parse(JSON.stringify(Array.isArray(nextActions) ? nextActions : [])),
        selectedActionIndex: null,
        hideDetailCard: true
    };
}

export function buildDeleteControlSpecActionState(
    index = -1,
    action = null,
    selectedActionIndex = null,
    editingActuatorId = "",
    refCheck = {},
    actions = []
) {
    const currentAction = action || {};
    const actionId = String(currentAction?.id || "");
    const confirmMessage = editingActuatorId
        ? (refCheck?.referenced
            ? `【警告】アクション '${actionId}' は、フローモジュール '${refCheck.moduleName}' 内のノード '${refCheck.nodeLabel}' (ID: ${refCheck.nodeId}) で現在参照されています。\n\n削除すると設計検証エラーが発生します。本当に削除しますか？`
            : `アクション '${actionId}' を削除しますか？`)
        : `アクション '${actionId}' を削除しますか？`;
    const nextActions = (Array.isArray(actions) ? actions : []).filter((_, actionIndex) => actionIndex !== index);
    let nextSelectedActionIndex = selectedActionIndex;
    if (selectedActionIndex === index) {
        nextSelectedActionIndex = null;
    } else if (selectedActionIndex > index) {
        nextSelectedActionIndex -= 1;
    }
    return {
        confirmMessage,
        nextActions,
        nextSelectedActionIndex,
        hideDetailCard: selectedActionIndex === index
    };
}

export function buildAddedControlSpecActionState(actions = [], nextAction = {}) {
    const nextActions = [...(Array.isArray(actions) ? actions : []), nextAction || {}];
    return {
        actuatorActions: nextActions,
        selectedActionIndex: nextActions.length - 1
    };
}

export function createEmptyControlSpecActuatorAction(name = "前進", deps = {}) {
    const {
        makeUniqueActionId = (baseId) => baseId
    } = deps;
    const actionName = String(name || "アクション").trim() || "アクション";
    return {
        id: makeUniqueActionId(buildControlSpecSlug(actionName, "action")),
        name: actionName,
        completionMode: "ignore",
        timeoutSec: 0,
        command: { setOutputs: [], resetOutputs: [] },
        doneConditions: [],
        faultConditions: []
    };
}

export function getNextControlSpecActionPresetName(actions = [], presetCandidates = []) {
    const used = new Set(
        (Array.isArray(actions) ? actions : []).map((action) => String(action?.name || "").trim())
    );
    return (Array.isArray(presetCandidates) ? presetCandidates : []).find((name) => !used.has(name)) || "カスタム";
}

export function buildControlSpecActionDetailFormInput(values = {}) {
    return {
        name: String(values?.name || ""),
        completionMode: normalizeControlSpecActionCompletionMode(values?.completionMode, values),
        timeoutSec: String(values?.timeoutSec || ""),
        plcopen: values?.plcopen || {},
        executeSignal: String(values?.executeSignal || ""),
        setOutputSig: String(values?.setOutputSig || ""),
        resetOutputSig: String(values?.resetOutputSig || ""),
        parameters: values?.parameters || {},
        status: values?.status || {},
        doneSig: String(values?.doneSig || ""),
        doneVal: Boolean(values?.doneVal),
        faultSig: String(values?.faultSig || ""),
        faultVal: Boolean(values?.faultVal)
    };
}

export function buildControlSpecActionDetailDraft(values = {}) {
    const name = String(values?.name || "").trim();
    const completionMode = normalizeControlSpecActionCompletionMode(values?.completionMode, values);
    const timeoutSec = Math.max(0, parseInt(values?.timeoutSec, 10) || 0);
    return {
        name,
        completionMode,
        timeoutSec: completionMode === "ignore" ? 0 : timeoutSec,
        plcopen: values?.plcopen || {},
        command: {
            setOutputs: values?.setOutputSig ? [String(values.setOutputSig)] : [],
            resetOutputs: values?.resetOutputSig ? [String(values.resetOutputSig)] : [],
            executeSignal: String(values?.executeSignal || "").trim()
        },
        parameters: values?.parameters || {},
        status: values?.status || {},
        doneConditions: completionMode === "sensor" && values?.doneSig ? [{
            signalId: String(values.doneSig),
            operator: "==",
            value: Boolean(values.doneVal)
        }] : [],
        faultConditions: values?.faultSig ? [{
            signalId: String(values.faultSig),
            operator: "==",
            value: Boolean(values.faultVal)
        }] : []
    };
}

export function validateControlSpecActionDetailDraft(draft = {}) {
    if (!draft?.name) {
        return { ok: false, errorMessage: "アクション名称は必須項目です。" };
    }
    if (draft?.completionMode === "sensor" && (!Array.isArray(draft?.doneConditions) || draft.doneConditions.length === 0)) {
        return { ok: false, errorMessage: "完了方式がセンサ監視のときは完了条件信号が必要です。" };
    }
    if (draft?.completionMode === "timer" && !(Number(draft?.timeoutSec) > 0)) {
        return { ok: false, errorMessage: "完了方式がタイマ完了のときはタイムアウトを 1 秒以上にしてください。" };
    }
    return { ok: true };
}

export function buildSavedControlSpecActionDetailState(actions = [], index = -1, draft = {}) {
    const list = Array.isArray(actions) ? [...actions] : [];
    if (index < 0 || index >= list.length) {
        return {
            actions: list,
            selectedActionIndex: null,
            hideDetailCard: true
        };
    }
    list[index] = {
        ...list[index],
        name: draft?.name || "",
        completionMode: normalizeControlSpecActionCompletionMode(draft?.completionMode, draft),
        timeoutSec: draft?.timeoutSec ?? 0,
        plcopen: draft?.plcopen || {},
        command: draft?.command || { setOutputs: [], resetOutputs: [], executeSignal: "" },
        parameters: draft?.parameters || {},
        status: draft?.status || {},
        doneConditions: Array.isArray(draft?.doneConditions) ? draft.doneConditions : [],
        faultConditions: Array.isArray(draft?.faultConditions) ? draft.faultConditions : []
    };
    return {
        actions: list,
        selectedActionIndex: null,
        hideDetailCard: true
    };
}

export function buildLoadedControlSpecActionDetailState(action = {}, index = -1) {
    const doneCondition = Array.isArray(action?.doneConditions) ? action.doneConditions[0] : null;
    const faultCondition = Array.isArray(action?.faultConditions) ? action.faultConditions[0] : null;
    const completionMode = normalizeControlSpecActionCompletionMode("", {
        doneConditions: action?.doneConditions,
        timeoutSec: action?.timeoutSec
    });
    return {
        selectedActionIndex: index,
        titleText: `アクション編集: ${String(action?.id || "")}`,
        inputId: String(action?.id || ""),
        inputIdDisabled: true,
        inputIdBackground: "#eee",
        inputName: String(action?.name || ""),
        completionMode,
        timeoutSec: action?.timeoutSec != null ? action.timeoutSec : 0,
        plcopenFamily: String(action?.plcopen?.family || ""),
        plcopenFbName: String(action?.plcopen?.fbName || ""),
        executeSignal: String(action?.command?.executeSignal || ""),
        doneStatusSignal: String(action?.status?.doneSignal || ""),
        busySignal: String(action?.status?.busySignal || ""),
        errorSignal: String(action?.status?.errorSignal || ""),
        errorIdSignal: String(action?.status?.errorIdSignal || ""),
        positionParam: String(action?.parameters?.position || ""),
        velocityParam: String(action?.parameters?.velocity || ""),
        setOutput: (Array.isArray(action?.command?.setOutputs) && action.command.setOutputs[0]) || "",
        resetOutput: (Array.isArray(action?.command?.resetOutputs) && action.command.resetOutputs[0]) || "",
        doneSignal: doneCondition ? doneCondition.signalId : "",
        doneValue: doneCondition && doneCondition.value === false ? "FALSE" : "TRUE",
        faultSignal: faultCondition ? faultCondition.signalId : "",
        faultValue: faultCondition && faultCondition.value === false ? "FALSE" : "TRUE",
        showDetailCard: true
    };
}

export function buildControlSpecActionSignalOptionsState(
    signals = [],
    escapeHtml = (value) => String(value ?? "")
) {
    const list = Array.isArray(signals) ? signals : [];
    const renderSignalOption = (signal) => (
        `<option value="${escapeHtml(signal?.id || "")}">${escapeHtml(signal?.id || "")} (${escapeHtml(signal?.name || "")})</option>`
    );
    const outputs = list.filter((signal) => signal?.direction === "output").map(renderSignalOption).join("");
    const inputs = list.filter((signal) => signal?.direction === "input").map(renderSignalOption).join("");
    return {
        setOutputOptions: `<option value="">出力信号を選択 (任意)</option>${outputs}`,
        resetOutputOptions: `<option value="">リセット出力信号を選択 (任意)</option>${outputs}`,
        doneSignalOptions: `<option value="">完了条件信号を選択 (任意)</option>${inputs}`,
        faultSignalOptions: `<option value="">異常条件信号を選択 (任意)</option>${inputs}`
    };
}

export function buildControlSpecActionListState(actions = []) {
    const rows = Array.isArray(actions) ? actions : [];
    return {
        rows,
        isEmpty: rows.length === 0,
        emptyMessage: "アクションが定義されていません。上のボタンから追加してください。"
    };
}

export function buildControlSpecActionListRowModel(action = {}, index = -1, selectedActionIndex = null) {
    const doneCondition = Array.isArray(action?.doneConditions) ? action.doneConditions[0] : null;
    const faultCondition = Array.isArray(action?.faultConditions) ? action.faultConditions[0] : null;
    const completionMode = normalizeControlSpecActionCompletionMode("", action);
    const setOutputText = (Array.isArray(action?.command?.setOutputs) && action.command.setOutputs[0]) || "未設定";
    const resetOutputText = (Array.isArray(action?.command?.resetOutputs) && action.command.resetOutputs[0]) || "未設定";
    const plcopenText = action?.plcopen?.fbName ? `${action.plcopen.family || "PLCopen"} / ${action.plcopen.fbName}` : "";
    return {
        index,
        isActive: selectedActionIndex === index,
        idText: String(action?.id || ""),
        nameText: String(action?.name || ""),
        setOutputText,
        resetOutputText,
        completionModeText: getControlSpecActionCompletionModeLabel(completionMode),
        doneConditionText: completionMode === "sensor"
            ? (doneCondition ? `${doneCondition.signalId} == ${doneCondition.value}` : "未設定")
            : completionMode === "timer"
                ? "タイマで完了"
                : "待ちなし",
        faultConditionText: faultCondition ? `${faultCondition.signalId} == ${faultCondition.value}` : "未設定",
        timeoutText: action?.timeoutSec ? `${action.timeoutSec}秒` : "無効",
        plcopenText
    };
}

export function buildControlSpecActionRowModel(
    step = {},
    formatCommands = () => "",
    formatConditions = () => ""
) {
    return {
        stepText: String(step?.step ?? ""),
        labelText: String(step?.label || step?.name || ""),
        actuatorText: step?.actuator ? `${step.actuator.name || step.actuator.id} (${step.actuator.id || ""})` : String(step?.axisName || ""),
        actionText: step?.action ? `${step.action.name || step.action.id} (${step.action.id || ""})` : String(step?.motionName || ""),
        commandsText: typeof formatCommands === "function" ? formatCommands(step?.commands) : "",
        doneConditionsText: typeof formatConditions === "function" ? formatConditions(step?.doneConditions) : "",
        timeoutText: step?.timeoutSec ? `${step.timeoutSec}s` : ""
    };
}

export function buildControlSpecConditionRowModel(
    step = {},
    formatExpression = () => "",
    formatBranches = () => ""
) {
    return {
        stepText: String(step?.step ?? ""),
        nameText: String(step?.conditionName || step?.conditionText || step?.name || ""),
        kindText: String(step?.conditionKind || "expression"),
        expressionText: typeof formatExpression === "function" ? formatExpression(step) : "",
        branchesText: typeof formatBranches === "function" ? formatBranches(step) : "",
        timeoutText: step?.timeoutSec ? `${step.timeoutSec}s` : ""
    };
}

export function buildControlSpecErrorStepRowModel(step = {}) {
    return {
        typeText: "error step",
        stepNodeText: `${String(step?.step ?? "")} / ${String(step?.nodeId || "")}`,
        codeText: String(step?.errorCode || step?.errorIdent || ""),
        messageText: String(step?.errorMessage || step?.name || ""),
        targetText: String(step?.nodePath || "")
    };
}

export function buildControlSpecTimeoutFindingRowModel(finding = {}) {
    return {
        typeText: "timeout finding",
        stepNodeText: String(finding?.nodeId || ""),
        codeText: String(finding?.code || ""),
        messageText: String(finding?.message || ""),
        targetText: formatControlSpecFindingTarget(finding)
    };
}

function normalizeComparableOutputList(outputs = []) {
    return (Array.isArray(outputs) ? outputs : [])
        .map((output) => String(output || "").trim())
        .filter(Boolean)
        .sort();
}

function areComparableOutputListsEqual(left = [], right = []) {
    const leftList = normalizeComparableOutputList(left);
    const rightList = normalizeComparableOutputList(right);
    return leftList.length === rightList.length
        && leftList.every((value, index) => value === rightList[index]);
}

function areValveStateRowsEquivalent(left = {}, right = {}, pneumaticValveMode = "doubleSolenoid") {
    const valveMode = normalizePneumaticValveMode(pneumaticValveMode, "pneumaticCylinder");
    if (valveMode === "singleSolenoid") {
        return normalizeValveOutputState(left?.single, "hold") === normalizeValveOutputState(right?.single, "hold");
    }
    return normalizeValveOutputState(left?.positive, "hold") === normalizeValveOutputState(right?.positive, "hold")
        && normalizeValveOutputState(left?.negative, "hold") === normalizeValveOutputState(right?.negative, "hold");
}

function buildPneumaticActuatorWarningFindings(actuator = {}) {
    if (!actuator || normalizeAxisActuatorType(actuator?.actuatorType) !== "pneumaticCylinder") {
        return [];
    }
    const pneumaticValveMode = normalizePneumaticValveMode(actuator?.pneumaticValveMode, actuator?.actuatorType);
    const valveModel = normalizePneumaticActuatorValveModel(
        actuator?.valveModel,
        actuator?.actuatorType,
        actuator?.actions,
        pneumaticValveMode,
        actuator?.solenoidConfig
    );
    const findings = [];
    const extendState = valveModel?.stateTable?.extend || null;
    const retractState = valveModel?.stateTable?.retract || null;
    if (extendState && retractState && areValveStateRowsEquivalent(extendState, retractState, pneumaticValveMode)) {
        findings.push({
            severity: "warning",
            code: "VALVE_STATE_DUPLICATE_DIRECTION",
            message: "extend/retract valve states are identical.",
            actuatorId: String(actuator?.id || "")
        });
    }
    const emergencyState = valveModel?.stateTable?.emergency || null;
    const hasEmergencyOn = emergencyState && (pneumaticValveMode === "singleSolenoid"
        ? normalizeValveOutputState(emergencyState?.single, "hold") === "on"
        : normalizeValveOutputState(emergencyState?.positive, "hold") === "on"
            || normalizeValveOutputState(emergencyState?.negative, "hold") === "on");
    if (hasEmergencyOn) {
        findings.push({
            severity: "warning",
            code: "VALVE_EMERGENCY_OUTPUT_ON",
            message: "emergency valve state keeps output on.",
            actuatorId: String(actuator?.id || "")
        });
    }
    (Array.isArray(actuator?.actions) ? actuator.actions : []).forEach((action) => {
        const actionId = String(action?.id || "").trim();
        const stateRow = buildCommandStateForActionId(actionId, pneumaticValveMode, valveModel);
        if (!stateRow) return;
        const expectedCommand = buildActionCommandFromValveState(stateRow, pneumaticValveMode, valveModel);
        const actualCommand = normalizeActionCommand(action?.command);
        if (
            !areComparableOutputListsEqual(actualCommand.setOutputs, expectedCommand.setOutputs)
            || !areComparableOutputListsEqual(actualCommand.resetOutputs, expectedCommand.resetOutputs)
        ) {
            findings.push({
                severity: "warning",
                code: "VALVE_ACTION_COMMAND_MISMATCH",
                message: "action command does not match valve model.",
                actuatorId: String(actuator?.id || ""),
                actionId
            });
        }
    });
    return findings;
}

function buildLocalControlSpecFindings(project = {}) {
    const actuators = Array.isArray(project?.actuators) ? project.actuators : [];
    return actuators.flatMap((actuator) => buildPneumaticActuatorWarningFindings(actuator));
}

/**
 * 制御仕様ビュー向け Pure Helper ユーティリティ
 */

/**
 * 制御仕様UIで表示するための ViewModel オブジェクトを構築します。
 * @param {Object} project - プロジェクトデータ
 * @param {Object} generationIr - バックエンドから生成された中間表現 (IR)
 * @returns {Object} UI表示に適した整形済み ViewModel
 */
export function buildControlSpecViewModel(project, generationIr) {
    const signals = Array.isArray(project?.signals) ? project.signals : [];
    const actuators = Array.isArray(project?.actuators) ? project.actuators : [];
    const valveModels = actuators.filter((actuator) => normalizeAxisActuatorType(actuator?.actuatorType) === "pneumaticCylinder");
    const steps = Array.isArray(generationIr?.steps) ? generationIr.steps : [];
    const ioTable = Array.isArray(generationIr?.ioTable) ? generationIr.ioTable : [];
    const findings = [
        ...(Array.isArray(generationIr?.controlSpecFindings) ? generationIr.controlSpecFindings : []),
        ...buildLocalControlSpecFindings(project)
    ];

    const hasErrors = findings.some(f => f.severity === "error");
    const actionSteps = steps.filter(step => step?.type === "motion" || step?.type === "actuatorAction");
    const conditionSteps = steps.filter(step => step?.type === "condition");
    const errorSteps = steps.filter(step => step?.type === "error");
    const timeoutFindings = findings.filter(finding => String(finding?.code || "").includes("TIMEOUT"));

    return {
        signals,
        actuators,
        steps,
        actionSteps,
        conditionSteps,
        errorSteps,
        timeoutFindings,
        ioTable,
        valveModels,
        findings,
        hasErrors,
        signalCount: signals.length,
        actuatorCount: actuators.length,
        valveModelCount: valveModels.length,
        actionStepCount: actionSteps.length,
        conditionStepCount: conditionSteps.length,
        errorStepCount: errorSteps.length,
        timeoutFindingCount: timeoutFindings.length,
        findingCount: findings.length
    };
}

/**
 * I/O表 (ioTable) のデータを CSV 文字列に変換します。
 * @param {Array} ioTable - I/O表の配列
 * @returns {string} CSVフォーマットの文字列
 */
export function serializeIoTableCsv(ioTable) {
    const headers = ["Direction", "Address", "Signal ID", "Signal Name", "Usages", "Referenced Steps"];
    const rows = [headers];

    const list = Array.isArray(ioTable) ? ioTable : [];
    for (const entry of list) {
        const direction = entry.direction || "";
        const address = entry.address || "";
        const signalId = entry.signalId || "";
        const signalName = entry.signalName || "";
        const usagesStr = Array.isArray(entry.usages) ? entry.usages.join("; ") : "";
        
        const refSteps = Array.isArray(entry.referencedBy) 
            ? entry.referencedBy.map(r => `Step ${r.step} (${r.label || ""}: ${r.usage || ""})`).join("; ")
            : "";

        const csvRow = [direction, address, signalId, signalName, usagesStr, refSteps].map(val => {
            const strVal = String(val);
            // セミコロン（;）は usages や referencedBy の配列要素の結合に使用しているため、
            // CSV出力として区切り記号との混同を防ぎ、可読性を高めるために引用符（""）で囲みます。
            if (strVal.includes(",") || strVal.includes('"') || strVal.includes("\n") || strVal.includes(";")) {
                return `"${strVal.replace(/"/g, '""')}"`;
            }
            return strVal;
        });
        rows.push(csvRow);
    }

    return rows.map(r => r.join(",")).join("\n");
}

/**
 *重大な設計エラー findings (severity: "error") が存在し、ST生成をブロックすべきか判定します。
 * @param {Object} generationIr - 中間表現 (IR)
 * @returns {boolean} ブロックすべき場合は true
 */
export function hasBlockingControlSpecFindings(generationIr) {
    return getBlockingControlSpecFindings(generationIr).length > 0;
}

/**
 * ST生成前に確認すべき重大な制御仕様 findings を返します。
 * @param {Object} generationIr - 中間表現 (IR)
 * @returns {Array} severity: "error" の finding 一覧
 */
export function getBlockingControlSpecFindings(generationIr) {
    const findings = Array.isArray(generationIr?.controlSpecFindings) ? generationIr.controlSpecFindings : [];
    return findings.filter(f => f?.severity === "error");
}

export function mergeControlSpecFindings(localFindings = [], apiFindings = []) {
    const localList = Array.isArray(localFindings) ? localFindings : [];
    const apiList = Array.isArray(apiFindings) ? apiFindings : [];
    const merged = new Map();
    const pushFinding = (finding, source) => {
        if (!finding || typeof finding !== "object") return;
        const key = [
            source === "local" ? "" : String(finding.path || ""),
            String(finding.severity || ""),
            String(finding.code || ""),
            String(finding.message || ""),
            String(finding.nodeId || ""),
            String(finding.actuatorId || ""),
            String(finding.actionId || ""),
            String(finding.signalId || "")
        ].join("|");
        if (merged.has(key)) {
            const current = merged.get(key);
            if (current.source !== source) current.source = "local+api";
            return;
        }
        merged.set(key, { ...finding, source });
    };
    localList.forEach((finding) => pushFinding(finding, "local"));
    apiList.forEach((finding) => pushFinding(finding, "api"));
    return Array.from(merged.values());
}

export function formatControlSpecFindingSource(source = "") {
    if (source === "local+api") return "IR / API";
    if (source === "api") return "API";
    return "IR";
}

export function formatControlSpecFindingTarget(finding = {}) {
    const parts = [];
    if (finding.nodeId) parts.push(`node ${finding.nodeId}`);
    if (finding.actuatorId) parts.push(`actuator ${finding.actuatorId}`);
    if (finding.actionId) parts.push(`action ${finding.actionId}`);
    if (finding.signalId) parts.push(`signal ${finding.signalId}`);
    if (finding.path) parts.push(`path ${finding.path}`);
    return parts.join(" / ");
}

export function buildControlSpecFindingTargetActions(finding = {}) {
    const actions = [];
    if (finding.nodeId) {
        actions.push({
            target: "node",
            label: "node",
            dataset: { nodeId: String(finding.nodeId) }
        });
    }
    if (finding.actuatorId) {
        actions.push({
            target: "actuator",
            label: "actuator",
            dataset: {
                actuatorId: String(finding.actuatorId),
                actionId: String(finding.actionId || "")
            }
        });
    }
    if (finding.signalId) {
        actions.push({
            target: "signal",
            label: "signal",
            dataset: { signalId: String(finding.signalId) }
        });
    }
    return actions;
}

export function buildControlSpecFindingsMeta(findings = [], validationState = {}) {
    const list = Array.isArray(findings) ? findings : [];
    const errorCount = list.filter((finding) => finding?.severity === "error").length;
    const warningCount = list.filter((finding) => finding?.severity === "warning").length;
    const status = validationState?.busy
        ? " / API検証中"
        : (validationState?.errorMessage
            ? ` / API検証失敗: ${validationState.errorMessage}`
            : (validationState?.lastValidatedAt ? " / API検証済み" : ""));
    return {
        errorCount,
        warningCount,
        text: `指摘 ${list.length}件 / error ${errorCount}件 / warning ${warningCount}件${status}`
    };
}

export function buildEmptyControlSpecFindingsMessage(validationState = {}) {
    return validationState?.errorMessage
        ? `制御仕様の検証結果はありません。API検証の取得に失敗しました: ${validationState.errorMessage}`
        : "制御仕様の検証結果はありません。";
}

/**
 * 制御仕様 finding の表示用メッセージを日本語化します。
 * IR 内の message は生成・回帰テストの互換性のため変更しません。
 * @param {Object} finding - 制御仕様 finding
 * @returns {string}
 */
export function localizeControlSpecFindingMessage(finding) {
    const code = finding?.code || "";
    const actuator = finding?.actuatorId ? `アクチュエータ "${finding.actuatorId}"` : "対象アクチュエータ";
    const action = finding?.actionId ? `アクション "${finding.actionId}"` : "対象アクション";
    const signal = finding?.signalId ? `信号 "${finding.signalId}"` : "対象信号";

    if (code === "SIGNAL_ID_MISSING") {
        return `${actuator} / ${action} で参照している signal ID が空です。信号辞書から信号を選択してください。`;
    }
    if (code === "SIGNAL_DIRECTION_MISMATCH") {
        return `${signal} の方向が、この用途に合っていません。出力指令には output、完了・異常条件には input または internal を指定してください。`;
    }
    if (code === "ACTUATOR_DUPLICATE") {
        return `${actuator} の ID が重複しています。project.actuators[] 内で一意になるように変更してください。`;
    }
    if (code === "SIGNAL_DUPLICATE") {
        return `${signal} の ID が重複しています。project.signals[] 内で一意になるように変更してください。`;
    }
    if (code === "VALVE_STATE_DUPLICATE_DIRECTION") {
        return `${actuator} の前進/後退側の動作表が同一です。進退が同じ通電結果になっていないか確認してください。`;
    }
    if (code === "VALVE_EMERGENCY_OUTPUT_ON") {
        return `${actuator} の非常停止行で ON が残っています。非常停止時にソレノイドを保持する意図か確認してください。`;
    }
    if (code === "VALVE_ACTION_COMMAND_MISMATCH") {
        return `${actuator} / ${action} の出力指令が弁モデルの動作表と一致していません。アクション出力と弁モデルを見直してください。`;
    }
    return finding?.message || "";
}

/**
 * ST生成前警告に表示する短い文面を作ります。
 * @param {Array} findings - 制御仕様 findings
 * @param {number} limit - 表示する最大件数
 * @returns {string}
 */
export function formatControlSpecBlockingFindings(findings, limit = 5) {
    const list = Array.isArray(findings) ? findings : [];
    return list.slice(0, limit).map(finding => {
        const code = finding?.code || "UNKNOWN";
        const message = localizeControlSpecFindingMessage(finding);
        const target = [
            finding?.nodeId ? `node:${finding.nodeId}` : "",
            finding?.actuatorId ? `actuator:${finding.actuatorId}` : "",
            finding?.actionId ? `action:${finding.actionId}` : "",
            finding?.signalId ? `signal:${finding.signalId}` : ""
        ].filter(Boolean).join(" / ");
        return `- ${code}: ${message}${target ? ` (${target})` : ""}`;
    }).join("\n");
}
